export const createStructuredDocumentFromImage = async (
  signedUrl, // Changed from base64Image to signedUrl
  fileName,
  docLabel,
  docDescription,
) => {
  try {
    // Determine the image MIME type based on the file extension using native JS
    const imageExtension = fileName
      .substring(fileName.lastIndexOf("."))
      .toLowerCase();
    let imageType = "image/jpeg"; // Default MIME type

    if (imageExtension === ".png") {
      imageType = "image/png";
    } else if (imageExtension === ".gif") {
      imageType = "image/gif";
    } else if (imageExtension === ".webp") {
      imageType = "image/webp";
    }

    // Set up the request payload with the enhanced prompt
    const requestBody = {
      model: "gpt-4o-mini",
      messages: [
        {
          role: "user",
          content: [
            {
              type: "text",
              text: `
You are provided with an image of a single page from a document. You are given the following overview information about the document:
- **Document Label**: ${docLabel}
- **Summary Description**: ${docDescription}

Your task is to extract the text from the image and rebuild it into a consistent HTML structure that represents the content visually appealingly and logically. The document type is identified, and it should guide the structuring.

**Instructions**:
1. **Extract the Text**: Carefully read the image and extract all textual content.
2. **Overall Structure**:
   - Wrap the entire content between \`<Page>\` tags.
   - Immediately after the opening \`<Page>\` tag, include an \`<Information>\` tag with attributes specifying the page orientation, e.g., \`<Information orientation="Portrait" />\`. Determine whether the page is in landscape or portrait orientation based on the image.
3. **Semantic HTML**:
   - Use \`<header>\` for any header content (e.g., company logos, document titles, page numbers at the top).
   - Use \`<footer>\` for footer content (e.g., page numbers at the bottom, disclaimers).
   - Use \`<section>\` for distinct logical sections within the main body.
   - Use \`<article>\` if there are standalone pieces of content.
4. **Headings and Subheadings**:
   - Identify and use \`<h1>\` for the main title.
   - Use \`<h2>\`, \`<h3>\`, etc., for subheadings, maintaining the hierarchical structure.
   - Carefully analyze the context of the size of the text to ensure that the heading hierarchy is correctly captured 
   - IMPORTANT NOTE: The begining of a new page is not necessarily always a header/heading it can be a continuation of text from the previous page. Please take special care to analyze the begining of a page in context of the ending of the previous page to determine if the new page is a continuation of the previous page or not.
   - IMPORTANT NOTE: You can determine whether a new page must start with a heading by analyzing the context of the text in relation to the ending of the previous page. Obvious clues will be a continuation of a list, table or paragraph. It is important to note the context of the text when making this determination
5. **Paragraphs and Text**:
   - Use \`<p>\` for regular paragraphs.
   - Preserve any bullet points or numbering using \`<ul>\`, \`<ol>\`, and \`<li>\`.
6. **Tables**:
   - If you identify a table, reconstruct it **accurately** using \`<table>\`, \`<thead>\`, \`<tbody>\`, \`<tr>\`, \`<th>\`, and \`<td>\`.
   - Pay special attention to the structure and content of tables to ensure they are as precise as possible.
   - If unsure about the table structure, wrap the content in \`<div class="possible-table">\` with a comment for manual review.
7. **Images within the Document**:
   - If there are images, include a placeholder at the appropriate location using the \`<image>\` tag with attributes.
   - For each image, provide detailed attributes that include:
     - \`description\`: A thorough description of the image content.
     - \`size\`: Approximate size of the image, including dimensions if possible (e.g., 'approximately 250 pixels wide by 300 pixels high').
     - \`location\`: A detailed description of where the image is located within the document, including references to nearby text (e.g., 'The image is located at the bottom left corner after the sentence ending with "the truck's performance is unmatched". There is text above and to the right of the image.').
   - Format the image placeholder as follows:

     \`<image description="[Description]" size="[Size]" location="[Location]" />\`

8. **Consistency**:
   - Ensure the HTML output is standardized and consistent across documents.
   - Do not include any styling; focus solely on correcting and standardizing the structural elements.
9. **Content Accuracy**:
   - Be as accurate as possible in transcribing the text.
   - Retain the original wording, capitalization, and punctuation.

Example html structure for standard documents:
"
<Page>
  <Information orientation="Portrait" />
  <header>Company Header with Logo</header>
  <h1>Annual Report 2024</h1>
  <section>
    <h2>Executive Summary</h2>
    <p>The annual report provides an overview of the company's performance...</p>
  </section>
  <section>
    <h2>Financial Highlights</h2>
    <p>Revenue increased by 15% compared to the previous year...</p>
    <ul>
      <li>Revenue: $5,000,000</li>
      <li>Net Profit: $1,200,000</li>
      <li>Expenses: $3,800,000</li>
    </ul>
  </section>
  <footer>Page 1 of 10</footer>
</Page>
"
  8. **Header and Footer identification**:
    - If you dedect headers and footers with text inside please take special care that you place the header and footer in the correct positions
    - Header at the top of the page, footer at the bottom of the page

**Special Instructions Based on Document Type:**
Special Document type 1:  **ID Documents, Passports, Licenses**:
   - Certain document types might indicate that it is not a standard document structure
   - Examples of these types are ID Documents, Passports, and Licenses
   - These documents will likely be a scanned ID or passport or License and will not follow a standard document structure
   - Recognize that these are standardized documents with key personal information.
   - Instead of a full HTML structure, extract and present key pieces of data in a table format.
   - Example Table Structure:
     "html
     <Page>
       <Information orientation="Portrait" />
       <table>
         <tr>
           <th>ID Number</th>
           <td>8609175187087</td>
         </tr>
         <tr>
           <th>Name</th>
           <td>Marnus Janse van Rensburg</td>
         </tr>
         <!-- Add more rows as necessary -->
       </table>
     </Page>
     "
Special Document type 2: **Invoices**:
   - Ensure the HTML structure is appropriate for invoices.
   - Accurately replicate tables, especially those listing items, quantities, and prices.
   - Pay special attention to identifying and correctly formatting important numbers (e.g., totals).
   - Example Table Structure for Invoices:
     "
     <Page>
       <Information orientation="Portrait" />
       <header>Company Logo</header>
       <h1>Invoice #12345</h1>
       <section>
         <h2>Billing Information</h2>
         <p>Customer Name: John Doe</p>
         <p>Address: 123 Main St, Anytown, USA</p>
       </section>
       <table>
         <thead>
           <tr>
             <th>Item</th>
             <th>Quantity</th>
             <th>Price</th>
           </tr>
         </thead>
         <tbody>
           <tr>
             <td>Widget A</td>
             <td>2</td>
             <td>$10.00</td>
           </tr>
           <tr>
             <td>Widget B</td>
             <td>1</td>
             <td>$20.00</td>
           </tr>
         </tbody>
       </table>
       <footer>Total: $40.00</footer>
     </Page>
     "
Special Document type 3: **Forms**:
   - Forms can either be completed or not. Try and determine if a form was completed
   - Try and structure the Form document as accurate to the original form as possible.
   - If a form is not completed try and reproduce the form exactly as is. This can mean that you have to leave open lines or spaces to allow place for completion.
   - Completed forms can either be completed in typed text (computer) or completed in handwritting.
   - You must try and recreate the completed forms as closely as possible. If the form is completed in handwritting, try and analyze the handwritting and reproduce the text as accurate as possible


IMPORTANT INSTRUCTION: **No Additional Text**:
    - Provide **only** the HTML response, with no explanations or additional remarks.

**Image**:
(See attached image)
              `,
            },
            {
              type: "image_url",
              image_url: {
                url: signedUrl, // Use the signed URL directly
              },
            },
          ],
        },
      ],
      max_tokens: 2000,
      temperature: 0.3,
    };

    // Return the request payload without making the API call
    return requestBody;
  } catch (error) {
    console.error("Error generating request body:", error);
    throw new Error(
      "Unable to generate request body for creating a structured HTML document from the image.",
    );
  }
};

export const createStructuredDocument = async (
  formattedText,
  extractedDataPoints,
  fileName,
  docLabel,
  docDescription,
  docRequired,
) => {
  try {
    // Set up the request payload with the enhanced prompt
    const requestBody = {
      model: "gpt-4o-mini",
      messages: [
        {
          role: "user",
          content: `
You are provided with formatted text extracted from a PDF document. You are given the following overview information about the document:
- **Document Label**: ${docLabel}. 
- **Summary Description**: ${docDescription}. 
- **The document type is: "${extractedDataPoints.documentType}"

Your task is to rebuild this text into a consistent HTML structure that represents the content visually and logically. The document type is identified, and it should guide the structuring.

**Instructions**:
1. Wrap the entire document content between \`<Document>\` tags.
2. Identify Page Boundaries and **Wrap Content with \`<Page>\` Tags**:
   - Analyze the content to determine where each page begins and ends.
   - For each identified page, encapsulate its content within \`<Page>\` tags.
   - Consider contextual cues such as headers, footers, page numbers, and significant section breaks that typically indicate page transitions.
   - If explicit page numbers or headers/footers are present, use them to accurately delineate pages.
   - In cases where page boundaries are not explicitly clear, infer them based on content flow and logical sectioning.
   - Example use of \`<Page>\` tags:
   "
   <Document>
      <Page>
          <!-- Page-specific content -->
      </Page>
      <Page>
          <!-- Page-specific content -->
      </Page>
    </Document>
    "
3. Use Semantic HTML Elements:
    - Utilize semantic HTML elements where appropriate (<header>, <section> etc.) within each <Page>.
4. Divide the Document into Logical Sections where it is content appropriate:
    - Within each \`<Page>\`, wrap distinct logical sections in <section> tags to provide flexibility for visual rendering.
5. Extract headings, paragraphs, and other logical elements, considering the document type.
   - Use \`<h1>\` for the main document title.
   - Use \`<h2>\`, \`<h3>\`, etc., for subheadings, ensuring the structure follows a logical hierarchy.
6. Identify potential tables in the content and rebuild them using html:
   - Only use HTML table tags (\`<table>\`, \`<thead>\`, \`<tbody>\`, etc.) if you are confident that (1) the text represents a valid table structure and (2) you can confidently rebuild the table structure.
   - If unsure, include the table content inside a \`<div class="possible-table">\` wrapper with a comment for manual review.
   - If not a valid table, list the content logically using \`<ul>\` or \`<ol>\`.
   - If you are confident that it is a table but you are not confident that you can accuratly rebuild the table then try and format the raw text in a logical and easily readable way (this can be structured in lists or csv type format)
7. IMPORTANT INSTRUCTION: You are provided with datapoints that have been extracted from the text. You must now Incorporate metadata from the extracted data points:
   - Tag each relevant data point with \`<span>\` and use the attribute \`data-key="name_of_data_point"\`.
   - You are provided with a list of datapoints that you must tag with \`<span>\` tags
   **Extracted Data Points**:
${JSON.stringify(extractedDataPoints)}

8. **Ensure Consistency and Accuracy**:
   - Maintain standardized and consistent HTML output across documents.
   - Do not include any styling; focus solely on structural elements.
   - Be as accurate as possible in transcribing the text, retaining original wording, capitalization, and punctuation.
9. **No Additional Text**:
    - Provide **only** the HTML response, with no additional text or explanations.

**Formatted Document Text**:
"""
${formattedText}
"""

**Output Requirements**:
- Provide the entire HTML structure wrapped in \`<Document>\` tags.
- Use consistent HTML tags as instructed.
- Ensure the output is well-structured, with standard headings, paragraphs, lists, and tables where applicable.

Provide **only** the HTML response, with no additional text or explanations.
        `,
        },
      ],
      max_tokens: 2000,
      temperature: 0.3,
    };

    // Directly return the request payload instead of making the API call
    return requestBody;
  } catch (error) {
    console.error("Error generating request body:", error);
    throw new Error(
      "Unable to generate request body for creating a structured HTML document.",
    );
  }
};

// Function to create the structured HTML prompt for Word documents
export const createStructuredWordDocument = async (
  formattedText, // Semantic HTML extracted from the Word document
  extractedDataPoints, // Data points extracted from the document
  fileName, // Name of the file
  docLabel, // Label of the document
  docDescription, // Description of the document
  docRequired, // Any additional required information
) => {
  try {
    // Set up the request payload with the enhanced prompt for Word-specific issues
    const requestBody = {
      model: "gpt-4o-mini", // Specify the AI model
      messages: [
        {
          role: "user",
          content: `
You are provided with a semantic HTML document that has been generated from a Word document using a text extraction tool. The HTML structure is assumed to be correct and semantically meaningful. Your task is to review and reconstruct this HTML to align with the specific HTML structure required by our application.

**Document Information:**
- **Document Label**: ${docLabel}
- **File Name**: ${fileName}
- **Description**: ${docDescription}

**Extracted Data Points:**
${JSON.stringify(extractedDataPoints)}

**Existing HTML Document:**
"""
${formattedText}
"""

**Instructions:**

1. **Evaluate and Reconstruct HTML Structure**:
   - Analyze the provided semantic HTML to ensure it adheres to our application's HTML structure guidelines.
   - Reconstruct the HTML to maintain logical and visual consistency with other document types (PDF, OCR).
   
2. **Maintain Semantic Meaning**:
   - Ensure that the meaning and intent of the original content are preserved.
   - Do not alter or fabricate any important data points such as Account Numbers, Names, Dates, etc.

3. **Consistent Tagging and Attributes**:
   - Use consistent HTML tags and attributes as per our app's standards.
   - For dynamic data points, ensure they are tagged appropriately using <span> tags with 'data-key' attributes.
   - Example:
     '''html
     <span data-key="account-number">123456789</span>
     '''

4. **Organize Content Logically**:
   - Structure the document using semantic HTML elements such as '<header>', '<section>', '<article>', '<footer>', etc.
   - Ensure proper nesting and hierarchy of elements for accessibility and maintainability.

5. **Handle Tables, Lists, and Images Appropriately**:
   - **Tables**:
     - Use '<table>', '<thead>', '<tbody>', '<tr>', '<th>', and '<td>' tags.
     - If unsure about the table structure, wrap content in '<div class="possible-table">' with a comment for manual review.
   - **Lists**:
     - Format bullet points and numbered lists using '<ul>', '<ol>', and '<li>' tags.
   - **Images**:
     - Include '<img>' tags with appropriate 'src', 'alt', and other relevant attributes.
     - Example:
       '''html
       <img src="image-url.jpg" alt="Description of image" />
       '''

6. **Error Handling and Reporting**:
   - **Highlight Uncertainties**:
     - Use HTML comments like '<!-- [NOTE]: Unable to determine the structure for this section -->' to mark areas requiring manual review.
   - **Avoid Adding Explanatory Text**:
     - Provide **only** the reconstructed HTML without any additional commentary or explanations.

7. **Output Requirements**:
   - Return the **entire reconstructed HTML document**, maintaining all original content and formatting as per the app's standards.
   - Ensure that the output is well-structured, clean, and free from any OCR or extraction-related artifacts.

**Output:**
- Provide **only** the reconstructed HTML response, with no additional text or explanations.
          `,
        },
      ],
      max_tokens: 2000, // Adjust based on expected output length
      temperature: 0.3, // Lower temperature for more deterministic output
    };

    // Directly return the request payload instead of making the API call
    return requestBody;
  } catch (error) {
    console.error("Error generating request body for Word document:", error);
    throw new Error(
      "Unable to generate request body for creating a structured HTML document from Word-extracted text.",
    );
  }
};

export const createStructuredCSVDocument = async (
  formattedText,
  extractedDataPoints,
  fileName,
  docLabel,
  docDescription,
  docRequired,
) => {
  try {
    // Set up the request payload with the enhanced prompt for CSV data
    const requestBody = {
      model: "gpt-4",
      messages: [
        {
          role: "user",
          content: `
You are provided with CSV data extracted and corrected from a document. Your task is to transform this CSV data into a well-structured HTML document that accurately represents the data in a visually logical format.

**Document Information:**
- **Document Label**: ${docLabel}
- **File Name**: ${fileName}
- **Description**: ${docDescription}

**Instructions**:

1. **Transform CSV Data into HTML Table**:
   - Read the CSV content provided.
   - Create an HTML document that includes a table representing the CSV data.
   - Use appropriate HTML table tags: \`<table>\`, \`<thead>\`, \`<tbody>\`, \`<tr>\`, \`<th>\`, and \`<td>\`.

2. **Table Structure**:
   - The first row of the CSV data contains the headers. Use these to create the table headers within \`<thead>\`.
   - Subsequent rows contain the data entries. Place these within \`<tbody>\`.

3. **Data Integrity**:
   - Ensure that all data from the CSV is accurately represented in the HTML table.
   - Do not alter, omit, or fabricate any data.
   - Preserve the order of the rows as they appear in the CSV.

4. **Incorporate Extracted Data Points**:
   - You are provided with a list of data points that need to be highlighted in the HTML table.
   - For each data point, wrap the corresponding cell content in a \`<span>\` tag with the attribute \`data-key="name_of_data_point"\`.
   - **Extracted Data Points**:
\`\`\`
${JSON.stringify(extractedDataPoints)}
\`\`\`

5. **HTML Document Structure**:
   - Wrap the entire content within \`<Document>\` tags.
   - Include a \`<header>\` section containing the document label, file name, and description.
   - You may use other semantic HTML elements like \`<section>\` if appropriate.

6. **Formatting and Style**:
   - Focus on structural HTML. Do not include any inline styles or CSS.
   - Ensure the HTML is well-formed, with properly nested and closed tags.

7. **Special Characters and Escaping**:
   - Ensure that any special characters in the data are properly escaped in the HTML to prevent rendering issues.

8. **No Additional Content**:
   - Do not include any commentary, explanations, or additional text outside of the HTML content.

**CSV Data**:
"""
${formattedText}
"""

**Output Requirements**:
- Provide the complete HTML document starting with \`<Document>\` and including the table representing the CSV data.
- Ensure the output is well-structured and suitable for rendering in an HTML environment.
- Ensire you produce the output containing all the data that was provided in the original csv

Provide **only** the HTML response, with no additional text or explanations.
          `,
        },
      ],
      max_tokens: 4000,
      temperature: 0.3,
    };

    // Return the request payload
    return requestBody;
  } catch (error) {
    console.error("Error generating request body for CSV document:", error);
    throw new Error(
      "Unable to generate request body for creating a structured HTML document from CSV data.",
    );
  }
};

// Create the HTML document from OCR extracted text
export const createStructuredOCRDocument = async (
  formattedText,
  extractedDataPoints,
  fileName,
  docLabel,
  docDescription,
  docRequired,
) => {
  try {
    // Set up the request payload with the enhanced prompt for OCR-specific issues
    const requestBody = {
      model: "gpt-4o-mini",
      messages: [
        {
          role: "user",
          content: `
    You are provided with an HTML document that has been generated through Optical Character Recognition (OCR). The HTML structure is assumed to be correct. Your task is to identify and correct common OCR-related text extraction issues within the text content. The potential issues include:
    - Missing text or incomplete phrases
    - Merged or separated words incorrectly
    - Irregular spacing, line breaks, or illegible characters
    
    You are given the following overview information about the document:
    - **Document Label**: ${docLabel}
    - **Summary Description**: ${docDescription}
    
    **Instructions**:
    1. **Identify OCR Errors**:
       - Carefully review the text within the HTML to identify any missing text, incomplete phrases, merged or separated words, and irregular spacing or line breaks.
    
    2. **Contextual Correction**:
       - For each identified error, determine if it can be safely corrected based on the surrounding context and the document type. Take special care not to change or fabricate any important datapoints (example: Account Number, Name etc )
    
    3. **Address Potential Missing Text**:
       - If you identify a place where text is missing:
         1. **Derivable Text**: If the missing text can be derived from the surrounding context or document type, add the text and enclose it in * characters, e.g., *derived text*.
         2. **Generatable Text**: If you can safely generate the missing text without impacting the material meaning of the document, generate it and enclose it in * characters, e.g., *generated text*.
         3. **Unrecoverable Text**: If the text cannot be derived or generated, leave a placeholder, e.g., \`<span class="missing-text">Missing Text</span>\`.
    
    4. **Maintain HTML Structure**:
       - Do **not** alter the existing HTML tags or structure. Focus solely on correcting the textual content.
    
    5. **Preserve Document Integrity**:
       - Ensure that corrections do not alter the original meaning or intent of the document.
    
    6. **Highlight Uncertainties**:
       - Use HTML comments like \`<!-- [NOTE]: Unable to determine the structure for this section -->\` to mark areas where assumptions were made or uncertainties exist.
    
    7. **Fix Potential page Title/Heading Errors**
      -  Each '<Page>' has been extracted individually and then concatenated algorithmically.
      - A common error is the injection of incorrect headings or titles at the start of each '<Page>'.
      - Your task is to identify incorrect headings or titles at the start of a '<Page>' and remove or correct them
      - **Detailed Instructions:**
          7.1. **Identify Incorrect Headings/Titles:**
             - Analyze the concluding text of the previous '<Page>' and the initial text of the current '<Page>'.
             - Determine if the heading/title at the start of the current '<Page>' is appropriate based on the contextual flow.
          7.2. **Correct or Remove Erroneous Headings/Titles:**
              - Remove headings/titles that are erroneously injected.
              - Modify headings/titles that inaccurately represent the ensuing content.
           7.3. **Preserve Correct Headings:**
              - Retain headings/titles that correctly introduce a new section or topic.
          7.4. **Use HTML Comments for Uncertainties:**
              - Annotate areas with uncertainties using HTML comments for manual review.
          7.5. **Maintain HTML Structure and Semantics:**
              - Do not alter existing HTML tags or structure.
              - Ensure semantic consistency with appropriate heading levels.
          7.6. **Ensure Document Integrity:**
              - Maintain the original meaning and intent of the document without introducing new information.
    
    8. **Output Requirements**:
       - Provide the **entire corrected HTML structure**, maintaining all original HTML tags and structure.
       - Ensure that only the text content is modified to fix OCR-related issues.
       - Do **not** include any additional text, explanations, or remarks outside of the HTML content.
    
    **Existing HTML Document**:
    ${formattedText}
    
    **Extracted Data Points**:
    ${JSON.stringify(extractedDataPoints)}
    
    **Output**:
    - Provide the corrected HTML with OCR issues addressed as per the instructions above.
    
    Provide **only** the HTML response, with no additional text or explanations.
          `,
        },
      ],
      max_tokens: 2000,
      temperature: 0.4,
    };

    // Directly return the request payload instead of making the API call
    return requestBody;
  } catch (error) {
    console.error("Error generating request body for OCR document:", error);
    throw new Error(
      "Unable to generate request body for creating a structured HTML document from OCR-extracted text.",
    );
  }
};

// AICalls.js - File for handling AI-based text extraction
export const generateAuditRiskProfile = async (
  resolvedDataPoints,
  auditData,
  systemName,
  documentSupportData,
) => {
  // Convert JSON data into a plain text format to optimize token usage
  const resolvedDataPointsText = JSON.stringify(resolvedDataPoints, null, 2);
  const auditDataText = JSON.stringify(auditData, null, 2);
  const documentSupportDataText = JSON.stringify(documentSupportData, null, 2);

  // Draft the prompt for generating the audit risk profile
  const requestBodyAuditRiskProfile = {
    model: "gpt-4o-mini",
    messages: [
      {
        role: "user",
        content: `
You are acting as a lead auditor evaluating the final audit reports and resolved data points after reviewing documents submitted for a ${systemName} registration process. You given the following overview information about the documents for context:
**Documents Overview**
"""
${documentSupportDataText}
"""
Your audit team has evaluated each document, extracted key data points, and prepared an audit report for each document. Now, your role is to assess this data and prepare an overall audit risk profile. The resolved data points can provide data on the resolved data points and unresolved data points pending on the thoroughness of the report provided. If only resolved data points are provided then proceed with audit based on those, however if bot resolved and unresolved datapoints are provided consider them all in your final findings.

**Audit Reports for Each Document**:
"""
${auditDataText}
"""

**Resolved Data Points**:
"""
${resolvedDataPointsText}
"""

Please complete the following tasks:

1. **Audit Status**:
   - Assign an **auditStatus** to the registration process based on the resolved data points and audit reports.
     - **Approved**: No significant risks identified.
     - **Proceed with Caution**: Medium-level risks require monitoring or follow-up.
     - **Advise Not to Proceed**: High risks identified that indicate severe issues.
   - Ignore any data points with empty values ("") as they were not present in the analyzed documents.

2. **Audit Overview**:
   - Provide a concise **auditOverview** summarizing key findings, strengths, weaknesses, and any compliance issues.

3. **Risk Score**:
   - Assign a **riskScore** of "Low," "Medium," or "High" based on the identified risk factors.

4. **Risk Elements**:
   - List specific **riskElements** that indicate potential issues (e.g., conflicting data points, missing critical information). Use null if no risks are identified.

Return a JSON response in the following format:

**START_JSON**
{
  "auditRiskProfile": {
    "auditStatus": "", // Audit status for the registration process
    "auditOverview": "", // Summary of audit findings
    "riskScore": "", // Overall risk score: "Low", "Medium", or "High"
    "riskElements": [] // List of risk elements, or "None" if no risks
  }
}
**END_JSON**

Return **only** the JSON response enclosed by START_JSON and END_JSON markers, without any additional text or explanation.
        `,
      },
    ],
    max_tokens: 1500,
    temperature: 0.2,
  };

  // Return the generated prompt
  return requestBodyAuditRiskProfile;
};

// Fallback function to generate a simpler request body for AI conflict resolution
export const datapointConflictResolverSimple = async (
  dataPoints,
  systemName,
  documentSupportData,
) => {
  // Convert JSON data into a plain text format to optimize token usage
  const dataPointsText = JSON.stringify(dataPoints, null, 2);
  const documentSupportDataText = JSON.stringify(documentSupportData, null, 2);

  // Draft the simplified prompt for the lead auditor
  const requestBodyLeadAuditSimple = {
    model: "gpt-4o-mini",
    messages: [
      {
        role: "user",
        content: `
You are acting as a lead auditor evaluating extracted data points from ${systemName} documents. The data provided contains values extracted from different documents for key data points. You given the following overview information about the documents for context:
**Documents Overview**
"""
${documentSupportDataText}
"""
Your primary responsibility is to review all the values for each data point and resolve a final audited value for each data point if that data point can be resolved. 
- In some cases, the different documents provided conflicting values for the same data point (or field). 
- In other cases, they provided similar or non-conflicting data. 
- In other cases, only a single document provided a value for the data point, meaning there is only a single value for that data point.  

It is your task as lead auditor to consider the nature of each data point and the possible conflicting values and return a final value for each data point that can safely be resolved.

If a data point cannot be resolved, provide an empty string ("") as the value for that data point.

**Extracted Data Points**:
"""
${dataPointsText}
"""

Please complete the following tasks:

1. **Conflict Resolution**:
   - For each data point with multiple conflicting values, compare the contexts and determine whether the differences are due to formatting or a true conceptual difference.
   - If conflicts are only superficial (e.g., different formatting such as "445 836 548" vs. "445836548"), resolve the conflict and provide the final agreed value.
   - If they are conceptually different, leave the value empty ("").
   - In cases where we do not have multiple values extracted for a data point (only a single value), evaluate if the single value provided looks conceptually appropriate for the data point. If it is appropriate, assign it; otherwise, leave it empty.

Return only a JSON response in the following format:

**START_JSON**
{
  "dataPoints": {
    "fieldName1": "resolved_value_or_empty_string",
    "fieldName2": "resolved_value_or_empty_string",
    ...
  }
}
**END_JSON**

Return **only** the JSON response, no explanations or other text.
        `,
      },
    ],
    max_tokens: 1000,
    temperature: 0.2,
  };

  // Return the generated prompt
  return requestBodyLeadAuditSimple;
};
/**
 * Creates a request payload for resolving conflicting data points using function-calling with o3-mini.
 * Analyzes consolidated data points, normalizes values, and returns a structured JSON array.
 * @param {object} dataPoints - Consolidated data points from Step 6, e.g., { consolidatedDataPoints: { fieldName: { value, documents } } }.
 * @param {string} systemName - Name of the system (e.g., "SupplierData").
 * @param {object} documentSupportData - Metadata about documents (e.g., labels, descriptions).
 * @returns {object} Request payload for the AI model with function-calling.
 * @throws {Error} If input is invalid or payload generation fails.
 */
export const reasoningConflictResolverPrompt = async (
  dataPoints,
  systemName,
  documentSupportData,
) => {
  try {
    // Validate input
    if (
      !dataPoints ||
      typeof dataPoints !== "object" ||
      !dataPoints.consolidatedDataPoints
    ) {
      throw new Error("Invalid or missing dataPoints");
    }
    if (!systemName || typeof systemName !== "string") {
      throw new Error("Invalid or missing systemName");
    }
    if (!documentSupportData || typeof documentSupportData !== "object") {
      throw new Error("Invalid or missing documentSupportData");
    }

    // Stringify inputs for the prompt
    const dataPointsText = JSON.stringify(
      dataPoints.consolidatedDataPoints,
      null,
      2,
    );
    const documentSupportDataText = JSON.stringify(
      documentSupportData,
      null,
      2,
    );

    // Prompt content
    const userMessage = `
You are a lead auditor evaluating extracted data points from documents submitted for a ${systemName} registration process. Your task is to resolve conflicts in the provided consolidated data points, determining final values or marking unresolved conflicts for user review. You are given document metadata to infer document authority and context.

Input Data:
- Consolidated Data Points:
{
${dataPointsText}
}
- Document Metadata:
{
${documentSupportDataText}
}

It is your primary responsibility to review all values for each data point and resolve a final audited value if it can be safely resolved, using conceptual understanding of the document types inferred from metadata (e.g., labels like 'Official Identity Document' or 'Resume').

Possible Scenarios for Data Points:
- Some data points have multiple conflicting values from different documents.
- Some data points have similar or identical values, differing only in formatting or abbreviation.
- Some data points have a single value, which should be validated for conceptual correctness.

Task:
1. Analyze Each Data Point:
   - For each fieldName in the consolidated data points, examine its 'value' and 'documents' fields:
     - If 'value' is not 'conflict', it is resolved; include it as 'resolved' with the value.
     - If 'value' is 'conflict', analyze the 'documents' array to resolve or mark as unresolved.
   - For single-value fields, validate appropriateness (e.g., '123' is invalid for a name).
2. Normalize Values:
   - For string values, normalize by:
     - Trimming leading/trailing whitespace.
     - Converting to lowercase for comparison.
     - Removing non-alphanumeric characters (except spaces, hyphens, and periods).
   - For arrays or objects, normalize by:
     - Sorting array elements (e.g., ['Python', 'Java'] vs. ['Java', 'Python']).
     - Standardizing object field order (e.g., { degree: 'BSc', institution: 'UCT' } vs. { institution: 'UCT', degree: 'BSc' }).
     - Expanding common abbreviations (e.g., 'BSc' to 'Bachelor of Science' for comparison).
   - For dates, convert to a standard format (e.g., 'YYYY-MM-DD') for comparison.
3. Resolve Conflicts:
   - If all normalized values are identical, resolve to the most authoritative source's original value (preserve case and formatting), prioritizing based on metadata (e.g., official identity documents for 'name' or 'idNumber').
   - If values differ after normalization, mark as 'unresolved' and list all original values with their document sources.
   - Conceptual Equivalence:
     - Resolve values that are conceptually the same despite textual differences (e.g., abbreviations, synonyms, formatting variations).
     - Use metadata to infer document authority (e.g., a document labeled 'Official Identity Document' is more reliable for 'name' than one labeled 'Resume').
   - Examples:
     - 'name': 'Laurence Kekana' (from a document labeled 'Official Identity Document') vs. 'L Kekana' (from a document labeled 'Resume') -> Resolve to 'Laurence Kekana', as the full name from an official source is more authoritative.
     - 'name': 'Marnus J v Rensburg' vs. 'Marnus Janse van Rensburg' -> Resolve to 'Marnus Janse van Rensburg', as 'Janse' is a likely expansion of 'J' after normalization.
     - 'idNumber': '86 09 17 518 7087' vs. '8609175187087' -> Resolve to '8609175187087', as both are identical after removing spaces.
     - 'address': '123 Main St, Apt 4B' vs. '123 Main Street, Apartment 4B' -> Resolve to '123 Main Street, Apartment 4B', as 'Street' and 'Apartment' are standard expansions.
     - 'education': [{ degree: 'BSc', institution: 'University of Cape Town' }] vs. [{ institution: 'University of Cape Town', degree: 'Bachelor of Science' }] -> Resolve to [{ degree: 'Bachelor of Science', institution: 'University of Cape Town' }], normalizing field order and expanding 'BSc'.
     - 'skills': ['Python', 'Java'] vs. ['Java', 'Python'] -> Resolve to ['Java', 'Python'] (alphabetical order for consistency).
     - 'dateOfBirth': '01/01/1990' vs. '1 January 1990' -> Resolve to '1990-01-01', using a standard format.
     - 'gender': 'M' vs. 'Male' -> Resolve to 'Male', as 'M' is a common abbreviation.
4. Output Structure:
   - Return a JSON array of objects, each with:
     - 'fieldName': The data point's key (e.g., 'name').
     - 'status': 'resolved' or 'unresolved'.
     - 'value': The resolved value (string, array, or object) if status is 'resolved', otherwise null.
     - 'conflictingValues': Array of { documentName, value } objects if status is 'unresolved', otherwise empty array.
   - Use the provided function 'output_resolved_data_points' to return the result.

Example Input:
{
  "name": {
    "value": "conflict",
    "documents": [
      { "value": "marnus j v rensburg", "sources": ["source1"] },
      { "value": "Marnus Janse van Rensburg", "sources": ["source2"] }
    ]
  },
  "idNumber": {
    "value": "8609175187087",
    "documents": ["source1", "source2"]
  },
  "address": {
    "value": "conflict",
    "documents": [
      { "value": "123 Main St, Apt 4B", "sources": ["source1"] },
      { "value": "123 Main Street, Apartment 4B", "sources": ["source2"] }
    ]
  }
}

Example Output:
{
  "dataPoints": [
    {
      "fieldName": "name",
      "status": "resolved",
      "value": "Marnus Janse van Rensburg",
      "conflictingValues": []
    },
    {
      "fieldName": "idNumber",
      "status": "resolved",
      "value": "8609175187087",
      "conflictingValues": []
    },
    {
      "fieldName": "address",
      "status": "resolved",
      "value": "123 Main Street, Apartment 4B",
      "conflictingValues": []
    }
  ]
}

Instructions:
- Normalize strings, arrays, and objects before comparison to resolve formatting or structural differences.
- Preserve original value formatting for resolved values, choosing the most authoritative source based on metadata.
- Resolve conceptually equivalent values using the examples as guidance.
- If a field cannot be resolved, include all conflicting values with their document sources.
- Return ONLY valid JSON via the function call. No extra text or commentary.
`;

    // Define the function schema for function-calling
    const functionSchema = {
      name: "output_resolved_data_points",
      description:
        "Returns a JSON array of resolved or unresolved data points.",
      parameters: {
        type: "object",
        properties: {
          dataPoints: {
            type: "array",
            items: {
              type: "object",
              properties: {
                fieldName: {
                  type: "string",
                  description: "The data point's key (e.g., 'name')",
                },
                status: {
                  type: "string",
                  enum: ["resolved", "unresolved"],
                  description: "Resolution status",
                },
                value: {
                  type: ["string", "array", "object", "null"],
                  description: "Resolved value, null if unresolved",
                },
                conflictingValues: {
                  type: "array",
                  items: {
                    type: "object",
                    properties: {
                      documentName: { type: "string" },
                      value: { type: ["string", "array", "object"] },
                    },
                    required: ["documentName", "value"],
                  },
                  description: "Conflicting values if unresolved",
                },
              },
              required: ["fieldName", "status", "value", "conflictingValues"],
            },
          },
        },
        required: ["dataPoints"],
      },
    };

    // Construct the request payload
    return {
      model: "o3-mini",
      messages: [
        {
          role: "user",
          content: userMessage,
        },
      ],
      functions: [functionSchema],
      function_call: { name: "output_resolved_data_points" },
      max_completion_tokens: 16500,
    };
  } catch (error) {
    console.error("Error generating conflict resolver prompt:", error.message);
    throw new Error(`Failed to generate prompt: ${error.message}`);
  }
};
export const datapointConflictResolver = async (
  dataPoints,
  systemName,
  documentSupportData,
) => {
  /* ---------- stringify helpers ---------- */
  const dataPointsText = JSON.stringify(dataPoints, null, 2);
  const documentSupportDataText = JSON.stringify(documentSupportData, null, 2);

  /* ---------- anchor keys so the model can’t invent names ---------- */
  const expectedKeys = Object.keys(
    dataPoints?.consolidatedDataPoints ?? {},
  ).join(", ");

  /* ---------- OpenAI request ---------- */
  const requestBodyLeadAudit = {
    model: "o3-mini-2025-01-31",
    messages: [
      {
        role: "user",
        content: `
You are the **lead auditor** for documents in *${systemName}*. Your task is to resolve conflicting data points across documents and return a single, consolidated JSON object with resolved values.

**Document overview**
"""
${documentSupportDataText}
"""

**Extracted data points (raw)**
"""
${dataPointsText}
"""

### Your job
For each key in **expectedKeys**, resolve conflicts and return a single value in a flat object. Follow these rules:
1. If values are identical or conceptually equivalent (e.g., same meaning despite minor formatting), mark as **resolved** and return one canonical value.
2. If values differ significantly, prioritize based on document context:
   - Prefer values from documents labeled as primary sources (e.g., official records like ID or certificates) over secondary sources (e.g., self-reported data like CVs).
   - Use document labels and descriptions from **document overview** to determine reliability.
3. If a field has only one value, validate it against the document context; if invalid, mark as **unresolved** with null.
4. If conflicts cannot be resolved, mark as **unresolved** and return null, noting conflicting values in the function’s conflictingValues field.
5. Use the keys from **expectedKeys** as field names, but if possible, infer more descriptive names from the data or document context (e.g., replace numeric keys with semantic names like "idNumber" or "name" based on content).

**expectedKeys:**  
${expectedKeys}

### Output format
Return the output by calling the \`returnResolvedDataPoints\` function. For text responses, wrap the JSON in \`START_JSON\` and \`END_JSON\` markers.

**Example Output (based on expectedKeys):**
START_JSON
{
  "dataPoints": {
    ${Object.keys(dataPoints?.consolidatedDataPoints ?? {})
      .map(
        (key) =>
          `"${key}": ${
            typeof dataPoints.consolidatedDataPoints[key].value === "string"
              ? `"${dataPoints.consolidatedDataPoints[key].value}"`
              : JSON.stringify(dataPoints.consolidatedDataPoints[key].value)
          }`,
      )
      .join(",\n    ")}
  }
}
END_JSON
        `,
      },
    ],
    functions: [
      {
        name: "returnResolvedDataPoints",
        description: "Return the final resolved data points as a flat object.",
        parameters: {
          type: "object",
          properties: {
            dataPoints: {
              type: "object",
              description:
                "A flat object mapping field names to resolved values.",
              additionalProperties: {
                type: ["string", "array", "object", "null"],
                description: "Resolved value for the field.",
              },
            },
            conflictingValues: {
              type: "array",
              description:
                "List of unresolved fields and their conflicting values.",
              items: {
                type: "object",
                properties: {
                  fieldName: { type: "string" },
                  values: {
                    type: "array",
                    items: {
                      type: "object",
                      properties: {
                        documentName: { type: "string" },
                        value: { type: ["string", "array", "object", "null"] },
                      },
                      required: ["documentName", "value"],
                    },
                  },
                },
                required: ["fieldName", "values"],
              },
            },
          },
          required: ["dataPoints"],
        },
      },
    ],
    function_call: { name: "returnResolvedDataPoints" },
    max_completion_tokens: 10_000,
  };

  return requestBodyLeadAudit;
};

export const extractDatapointsAI = async (
  extractedText,
  fileName,
  systemName,
  dataPointDefinitions,
  docLabel,
  docDescription,
  docRequired,
) => {
  try {
    // Construct the dynamic response structure
    const dataPointKeys = Object.keys(dataPointDefinitions);
    const additionalDataPoints = [
      "documentType",
      "docOverview",
      "riskScore",
      "riskElements",
    ];
    const allDataPoints = [...dataPointKeys, ...additionalDataPoints];

    // Set up the request payload with the dynamic prompt
    const requestBody = {
      model: "gpt-4o-mini",
      messages: [
        {
          role: "user",
          content: `
You are provided with text extracted from a document. The document was submitted as part of a ${systemName} registration/creation process on your company's management system. You are given the following overview information about the document:
- **Document Label**: ${docLabel}. 
- **Summary Description**: ${docDescription}. 
- **Is it a Compulsory Requirement?**: ${docRequired ? "Yes" : "No"}

Your task is to determine if the document matches the intended type, (${docLabel}), and extract key details from the document based on specific fields. Follow these steps carefully:

1. **Document Verification**:
   - First, determine if the document matches the intended ${docLabel} type by checking for specific cues such as headers, titles, keywords, and context within the document.
   - Use explicit mentions and inferred information to decide whether the document can be used as intended.
   - For documents marked as compulsory, be particularly diligent in determining if they match the intended type and fulfill all requirements.

2. **Data Extraction**:
   - Extract only those data points that are contextually accurate and directly relevant to the document type. Discard any information that cannot be confidently linked to the intended ${fileName}.
   - Ensure the extracted data points meet their intended purpose.

3. **Contextual Validation**:
   - Always consider the **context** of the document when extracting data. For example:
     - If an address is mentioned in a "Proof of Bank Account" document, determine if it is the **company's address** or **the bank's address** before extraction.
   - Only extract and assign values that are relevant and contextually accurate.

**Document Text**:
"""
${extractedText}
"""

**Response Requirements**:
- Provide the response in JSON format.
- The JSON should contain key-value pairs for all the relevant data points, without any unnecessary nesting or containers.
- If a value cannot be confidently identified or the document is not the intended type, leave it as an **empty string** (""). Avoid "None" or speculative values.

**Required JSON Structure**:
{
  "documentType": "", // Document type as identified based on the context
  "is${fileName}": "",  // This is a boolean value that verifies whether the document matches the intended ${docLabel} type
  "docOverview": "", // A concise explanation of why the document type was assigned True or False and what this document is
  "riskScore": "", // The risk score based on the quality of the document: Low, Medium, High (In the context of ${systemName} management)
  "riskElements": "", // Any risk elements identified that support the assigned risk score. If none, respond with an empty string.
  ${dataPointKeys
    .map((key) => `"${key}": "" // ${dataPointDefinitions[key]}`)
    .join(",\n    ")}
}

IMPORTANT NOTE: Provide **only** the JSON response, with no additional text or explanations.
          `,
        },
      ],
      max_tokens: 1500,
      temperature: 0.2,
    };

    // Directly return the request payload instead of making the API call
    return requestBody;
  } catch (error) {
    console.error("Error generating request body:", error);
    throw new Error(
      "Unable to generate request body for the provided document.",
    );
  }
};

// Function to extract key details from the Proof of Bank Account document using the OpenAICalls Cloud Function
export const extractProofOfBankAccount = async (extractedText) => {
  try {
    // Set up the request payload
    const requestBody = {
      model: "gpt-4o-mini",
      messages: [
        {
          role: "user",
          content: `You are provided with text extracted from a document. Identify if it is a proof of bank account document, and extract key details based on the following fields. Provide only a JSON response, filling in as many fields as possible without guessing.

Document Text:
"""
${extractedText}
"""

Response must be a JSON object named supplierExtract. Below is the required structure:

{
  "supplierExtract": {
    "documentType": "", 
    "isProofOfBankAccount": "", 
    "type": "", // Supplier if company or PTY, Contractor if individual
    "registrationNumber": "", // Only if the document is for a company
    "IDNumber": "", // Only if the document is for an individual
    "name": "", // Company name or individual's name
    "productService": "", 
    "contactPerson": "", 
    "contactNumber": "", 
    "address": "", 
    "email": "", 
    "bank": "", 
    "bankAccountNumber": "", 
    "accountType": "", 
    "branchCode": "", 
    "defaultPaymentRef": ""
  }
}

Provide the response as JSON only, no other text should be returned.`,
        },
      ],
      max_tokens: 1500,
      temperature: 0.2,
    };

    // Directly return the request payload instead of making the API call
    return requestBody;
  } catch (error) {
    console.error("Error generating request body:", error);
    throw new Error(
      "Unable to generate request body for the provided document.",
    );
  }
};

export const reformatOCRDocument = async (
  extractedText,
  fileName,
  docLabel,
  docDescription,
) => {
  try {
    // Set up the request payload
    const requestBody = {
      model: "gpt-4o-mini",
      messages: [
        {
          role: "user",
          content: `You are provided with a structured HTML-like document extracted from a scanned PDF using an OCR tool. The document is organized with <Document> tags containing multiple <Page> tags, each representing a single page of the original document.

**Document Information:**
- **Document Label**: "${docLabel}"
- **File Name**: "${fileName}"
- **Description**: "${docDescription}"

**Structure Overview:**
- The entire content is wrapped within <Document> tags.
- Each page is enclosed within <Page> tags in the order they appear in the original document.
- Inside each <Page>, the content may include headers, footers, sections, paragraphs, tables, and images.

**Task:**

You must review the structured HTML-like document to identify possible errors or mistakes caused by the OCR process.

1. **Text Refinement**:
   - The document was scanned, and text was extracted using OCR technology, which may have introduced errors such as missing parts, irregular spacing, merged words, and inconsistent line breaks.
   - **Reformat the text within each <Page>** to improve readability without altering the meaning or adding any fabricated content.
   - Ensure correct spacing between words, consistent line breaks, and rectify any formatting issues.

2. **HTML Structure Evaluation**:
   - **Review the HTML structure** for any obvious flaws or errors.
   - **Correct any structural issues** to ensure valid and consistent HTML formatting.
   - Ensure all HTML tags are properly closed and nested.
   - Maintain the logical and visual hierarchy of the document elements..

3. **Consider Document Type and Description**:
   - Based on the **Document Label** ("${docLabel}") and **Description** ("${docDescription}"), tailor the HTML structure to fit the specific needs of this document type.
   - For example, if the document is an invoice, ensure that tables are properly formatted for item listings and that values are correctly presented; if it's a report, maintain clear section headings.

4. **Consistency and Standards**:
   - Ensure the entire document maintains a standardized and consistent HTML structure.
   - Do not add any additional styling; focus solely on correcting and standardizing the structural elements.

5. **Error Handling and Reporting**:
   - **Highlight or mark** areas where you had to make assumptions or encountered uncertainties.
   - Use comments like "<!-- [NOTE]: Unable to determine the structure for this section -->" to indicate such areas for post-processing reviews.

6. **Refine Instructions for Specific Elements Only When Needed**:
   - **Tables**:
     - Considering the context of the text, ensure tables are accurately constructed and logically using <table>, <thead>, <tbody>, <tr>, <th>, and <td>.
     - If unsure about the table structure, wrap the content in <div class="possible-table"> with a comment for manual review.
   - **Images**:
     - Include placeholders for images using the <image> tag with appropriate attributes.
     - Provide detailed attributes such as "description", "size", and "location".
   - **Lists**:
     - Properly format bullet points and numbered lists using "<ul>", "<ol>", and "<li>" tags.

**Instructions:**
- Process each <Page> in sequence, maintaining the original order.
- Return only the reformatted structured HTML document wrapped within <Document> tags, without any additional commentary or explanation.

**Example Output:**
"
<Document>
  <Page>
    <header>Company Logo</header>
    <h1>Invoice #12345</h1>
    <section>
      <h2>Billing Information</h2>
      <p>Customer Name: John Doe</p>
      <p>Address: 123 Main St, Anytown, USA</p>
    </section>
    <table>
      <thead>
        <tr>
          <th>Item</th>
          <th>Quantity</th>
          <th>Price</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Widget A</td>
          <td>2</td>
          <td>$10.00</td>
        </tr>
        <tr>
          <td>Widget B</td>
          <td>1</td>
          <td>$20.00</td>
        </tr>
      </tbody>
    </table>
    <footer>Page 1 of 3</footer>
  </Page>
  <!-- More pages -->
</Document>"

**Structured Document:**
"""
${extractedText}
"""
`,
        },
      ],
      max_tokens: 2000,
      temperature: 0.1,
    };

    // Directly return the request payload instead of making the API call
    return requestBody;
  } catch (error) {
    console.error("Error generating request body for OCR document:", error);
    throw new Error(
      "Unable to generate request body for reformatting the provided OCR document.",
    );
  }
};

export const reformatCSVDocument = async (
  csvContent,
  fileName,
  docLabel,
  docDescription,
) => {
  try {
    // Set up the request payload
    const requestBody = {
      model: "gpt-4",
      messages: [
        {
          role: "user",
          content: `You are provided with CSV data extracted from a document. The data may contain errors due to the extraction process, such as misaligned fields, missing values, incorrect delimiters, or inconsistent formatting.

**Document Information:**
- **Document Label**: "${docLabel}"
- **File Name**: "${fileName}"
- **Description**: "${docDescription}"

**Task:**

You must review the CSV data to identify and correct any errors or mistakes that occurred during the extraction process.

1. **Data Validation and Correction**:
   - **Review the CSV content** for inconsistencies or errors.
   - **Correct issues** such as misaligned columns, missing headers, incorrect delimiters, and data that doesn't match the expected format.
   - Ensure all rows have the same number of fields as the headers.
   - Fill in missing values with appropriate placeholders if necessary (e.g., an empty string).

2. **Data Integrity**:
   - Ensure the data accurately represents the original information.
   - Do not fabricate or alter the original data beyond correcting obvious errors.
   - Preserve the meaning and context of all entries.

3. **CSV Formatting**:
   - Use commas as delimiters.
   - Enclose fields containing commas, double quotes, or newlines in double quotes.
   - Escape double quotes within fields by doubling them (e.g., " becomes "").
   - Ensure consistent line breaks and no extra blank lines.

4. **Headers Consistency**:
   - Verify that the headers are correct and represent the columns accurately.
   - If headers are missing or corrupted, reconstruct them based on the data context.

5. **Output Requirements**:
   - **Return only** the corrected and properly formatted CSV content.
   - Do not include any additional commentary, explanations, or notes.

**Instructions:**
- Focus solely on correcting the CSV data.
- Maintain the original order of the data entries.
- Ensure that the output is ready for conversion into an HTML table or other formats.

**CSV Data:**
"""
${csvContent}
"""
`,
        },
      ],
      max_tokens: 2000,
      temperature: 0.1,
    };

    // Return the request payload
    return requestBody;
  } catch (error) {
    console.error("Error generating request body for CSV document:", error);
    throw new Error(
      "Unable to generate request body for reformatting the provided CSV document.",
    );
  }
};

export const reformatDocument = async (extractedText) => {
  try {
    // Set up the request payload
    const requestBody = {
      model: "gpt-4o-mini",
      messages: [
        {
          role: "user",
          content: `The following text is extracted from a document and may contain irregular spacing, merged words, and inconsistent line breaks. Please reformat the text to improve readability without changing its meaning or content. Return only the reformatted text, with no introduction or conclusion:
  
  Document Text:
  """
  ${extractedText}
  """
  
  Ensure that the spacing between words is correct, line breaks are consistent, and any formatting issues are corrected. Return only the document content, without any additional commentary or explanation.`,
        },
      ],
      max_tokens: 2000,
      temperature: 0.1,
    };

    // Directly return the request payload instead of making the API call
    return requestBody;
  } catch (error) {
    console.error("Error generating request body:", error);
    throw new Error(
      "Unable to generate request body for reformatting the provided document.",
    );
  }
};

// Function to generate a simple request to OpenAI for a welcome poem
export const AITestFunction = async () => {
  try {
    // Set up the request payload
    const requestBody = {
      model: "gpt-o1",
      messages: [
        {
          role: "user",
          content: `Write a short, cheerful welcome poem for new members joining our community.`,
        },
      ],
      max_tokens: 1500,
      temperature: 0.7,
    };

    // Directly return the request payload instead of making the API call
    return requestBody;
  } catch (error) {
    console.error("Error generating request body:", error);
    throw new Error(
      "Unable to generate request body for the simple test request.",
    );
  }
};

// src/components/heroManagementDashboard/unityLearning/unityLearningPlatform/uiComponents/ViewEditItemFunctions/aiPrompts.js
export const generateLearningContentPrompt = async (
  docId, // Outcome ID (e.g., "WD204-4-outcome-0")
  hierarchy, // From getRichLearningHierarchy
  company, // e.g., "Better_Best"
  collectionBrand, // e.g., "shared"
) => {
  try {
    console.log("!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!PROMP!!!!!!!!!!!!!");
    // Extract hierarchy levels
    const program = hierarchy.find((h) => h.level === "programs")?.data || {
      name: "Unknown Program",
      id: "N/A",
    };
    console.log("Program", program);
    const module = hierarchy.find((h) => h.level === "modules")?.data || {
      name: "Unknown Module",
      id: "N/A",
    };
    console.log("Module", module);
    const component = hierarchy.find((h) => h.level === "components")?.data || {
      name: "Unknown Component",
      id: "N/A",
    };
    console.log("Component", component);
    const unit = hierarchy.find((h) => h.level === "units")?.data || {
      name: "Unknown Unit",
      id: "N/A",
      outcomes: [],
    };
    console.log("Unit", unit);
    const outcome = unit.outcomes.find((o) => o.id === docId) || {
      name: "Unknown Outcome",
      sequence: 0,
    };
    console.log("Outcome", outcome);

    // Set up the request payload with the dynamic prompt
    const requestBody = {
      model: "gpt-4.5-preview",
      messages: [
        {
          role: "user",
          content: `
You are an expert educational content creator designing material for a state-of-the-art Learning Management System (LMS). Your task is to generate detailed, engaging learning content for a specific learning outcome within a structured curriculum.

**Context:**
- **Learning Outcome**: "${outcome.name}" (ID: ${docId})
- **Sequence**: Outcome ${outcome.sequence + 1} of ${
            unit.outcomes.length
          } in its unit
- **Hierarchy**:
  - Program: "${program.name}" (ID: ${program.id})
  - Module: "${module.name}" (ID: ${module.id})
  - Component: "${component.name}" (ID: ${component.id})
  - Unit: "${unit.name}" (ID: ${unit.id})
  - Outcomes in this Unit:
    ${unit.outcomes
      .map((o) => `- "${o.name}" (ID: ${o.id}, Sequence: ${o.sequence + 1})`)
      .join("\n    ")}

**Instructions**:
1. Create concise, educational content (200-300 words) for the outcome "${
            outcome.name
          }".
2. Include:
   - A brief explanation of the concept, tailored to the context of "${
     module.name
   }" and "${component.name}".
   - A practical example relevant to the hierarchy (e.g., applicable to "${
     program.name
   }").
   - A short exercise or question to reinforce learning.
3. Tone: Clear, engaging, and suitable for visual web-based learning.
4. Do not fabricate facts; base content on general knowledge or infer from the provided context.
5. Return only the raw text content, no additional formatting or comments.

**Example Output**:
Express is a minimal and flexible Node.js web application framework that simplifies building robust APIs and web apps. It provides a straightforward way to handle HTTP requests, routing, and middleware. In the context of "JavaScript & Frontend Development with React" and "Web applications with React," Express can serve as a backend API for a React frontend. For example, in "Software Engineer- Full stack web development," you might use Express to create an endpoint: \`app.get('/users', (req, res) => res.json(users));\` to fetch user data for display in React.

**Exercise**: Write a simple Express route that returns a "Hello, World!" message when visiting "/hello".
          `,
        },
      ],
      max_tokens: 1500, // Adjusted to match extractDatapointsAI
      temperature: 0.2, // Adjusted to match extractDatapointsAI
    };

    // Directly return the request payload
    return requestBody;
  } catch (error) {
    console.error("Error generating learning content prompt:", error);
    throw new Error("Unable to generate prompt for learning content creation.");
  }
};

/**
 * Generates Prompt #1 for gathering context and creating a structured outline for a single learning outcome,
 * returning the outline in a JSON structure so it can be easily parsed and used in subsequent prompts.
 *
 * @param {string} contextString - A string providing detailed context about the learning outcome,
 *                                where it fits in the program, and the sequenced outcomes around it.
 *
 * @returns {object} - A requestBody object containing the prompt payload for your AI model.
 *
 * REQUIRED JSON STRUCTURE (Please read carefully):
 * {
 *   "contextualSummary": "string",
 *   "outline": [
 *     {
 *       "title": "string",
 *       "subPoints": [
 *         {
 *           "heading": "string",
 *           "notes": "string"
 *         }
 *       ]
 *     }
 *   ]
 * }
 *
 * --- FIELD DESCRIPTIONS ---
 * 1. "contextualSummary": A short descriptive paragraph (string) that summarizes:
 *    - The main objective of the learning outcome in the context of the entire program/module/unit.
 *    - Any relevant transitions from previous outcomes or lead-ins to subsequent outcomes.
 *
 * 2. "outline": An array of sections (each section is an object) describing the planned structure for the material.
 *
 *    Each "outline" object has two fields:
 *      - "title": A short, high-level label for the section (e.g., "Introduction", "Key Concepts", "Practical Application").
 *      - "subPoints": An array of objects for subtopics under that section.
 *         Each object in "subPoints" includes:
 *             "heading": The name or short label of a subtopic (string).
 *             "notes": A brief phrase or bullet covering what will be included in that subtopic (string).
 *
 * EXAMPLE JSON (for demonstration; do not copy verbatim):
 * {
 *   "contextualSummary": "This outcome focuses on how JavaScript manages asynchronous tasks using the call stack, task queue, and event loop.
 *                        It builds on previously introduced async concepts and primes learners for upcoming Node.js and React work.",
 *   "outline": [
 *     {
 *       "title": "Introduction",
 *       "subPoints": [
 *         {
 *           "heading": "Why JS Internals Matter",
 *           "notes": "Briefly highlight how understanding JavaScript runtime helps with async code."
 *         },
 *         {
 *           "heading": "Link to Previous Outcomes",
 *           "notes": "Reference how it follows async/await and Promises."
 *         }
 *       ]
 *     },
 *     {
 *       "title": "Call Stack",
 *       "subPoints": [
 *         {
 *           "heading": "Definition",
 *           "notes": "Explain LIFO principle and function invocation stack."
 *         }
 *       ]
 *     }
 *   ]
 * }
 *
 * NOTE: This example is only to illustrate the format. The actual data must align with the
 *       specific learning outcome found in 'contextString'.
 */
export const createContextOutlinePrompt = async (contextString) => {
  try {
    // Build the message content for the "user" role
    const userMessage = `
You are an expert educational content architect. The program structure is:
- Program: multiple months of learning
- Module: roughly one month of full-time tuition
- Component: roughly one week of full-time tuition
- Unit: roughly one day of full-time tuition
- Learning Outcome: a single key concept or skill to be mastered in that day

Below is context about a specific learning outcome:

${contextString}

**YOUR TASK (Prompt #1: Context & Outline)**:
1. Provide a short descriptive paragraph named "contextualSummary" that explains the purpose of this learning outcome in the context of the program.
2. Produce an "outline" array, where each element is an object:
   - "title": The name of a major section.
   - "subPoints": An array of objects, each containing:
     - "heading": A subtopic name.
     - "notes": A short note on what will be covered.
3. Return only valid JSON matching the exact structure below (with no extra text or fields):
{
  "contextualSummary": "",
  "outline": [
    {
      "title": "",
      "subPoints": [
        {
          "heading": "",
          "notes": ""
        }
      ]
    }
  ]
}

**Important**:
- Do **not** include any disclaimers or explanations outside the JSON.
- Ensure the JSON is well-formed and parseable.

Please comply strictly by returning valid JSON only.
    `;

    // Construct the request payload
    const requestBody = {
      model: "gpt-4o-mini", // or whichever model you prefer
      messages: [
        {
          role: "user",
          content: userMessage,
        },
      ],
      max_tokens: 1500,
      temperature: 0.2,
    };

    return requestBody;
  } catch (error) {
    console.error("Error generating context and outline prompt (JSON):", error);
    throw new Error(
      "Unable to generate Prompt #1 for context and outline in JSON.",
    );
  }
};

// PROMPT 1 NEW PROMPT
// =============================================================================
// createContextOutlinePrompt.js   ·  v2  (o3‑mini + function‑call)
// =============================================================================
/**
 * Build Prompt #1 – “Context & Outline”
 *
 * We already hold the *entire* programme in memory, so we can hand the model a
 * rich, human‑readable snapshot (`programText`), zoom into the *current* unit
 * (via `unit` & `outcomeList`) and finally single‑out the one outcome
 * (`outcome`) it must write for.
 *
 * The model must return JSON **via function‑call** that matches:
 * {
 *   "contextualSummary": "string",
 *   "outline": [
 *     {
 *       "title": "string",
 *       "subPoints": [
 *         { "heading": "string", "notes": "string" }
 *       ]
 *     }
 *   ]
 * }
 *
 * @param {string}  programText   – pretty‑printed programme tree
 * @param {object}  unit          – { name, sequence }
 * @param {object}  outcome       – { name, globalSequence }
 * @param {string}  outcomeList   – bullet list of *all* outcomes in the unit
 * @returns {object} OpenAI requestBody for o3‑mini
 * | variable           | example value                                                                                                                                                                                                                                                                                                                                                                | what it represents                                                                                                                                                                                                  |
| ------------------ | ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------- | ------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------- |
| **`safeProgText`** | `text Module 1: Security Foundations     🡆 Component: Threat Landscape         · Unit 1: Introduction to Threats         · Unit 2: Threat Actors     🡆 Component: Governance & Policy         · Unit 3: Cyber‑security Legislation  Module 2: Network Defence     🡆 Component: Firewalls & IDS         · Unit 4: Packet Filtering         · Unit 5: Intrusion Detection ` | A *multiline string* that prints the entire programme tree. Each module header is followed by its components and each component shows its units. It’s created once per prompt so the model “sees” the full context. |
| **`safeUnitName`** | `"Packet Filtering"`                                                                                                                                                                                                                                                                                                                                                         | The title of the unit that contains the clicked outcome.                                                                                                                                                            |
| **`unitSeq`**      | `4`                                                                                                                                                                                                                                                                                                                                                                          | Sequence number of that unit *within its component* (integer).                                                                                                                                                      |
| **`safeOutcome`**  | `"Explain how stateless packet‑filtering decisions are made"`                                                                                                                                                                                                                                                                                                                | The *single* learning outcome the user clicked on – the outcome we’re about to generate content for.                                                                                                                |
| **`globalSeq`**    | `27`                                                                                                                                                                                                                                                                                                                                                                         | The **globalSequence** (unique inside the programme) for that outcome. Enables unambiguous ordering across the whole curriculum.                                                                                    |
| **`safeList`**     | `text • 25 – Define packet filtering • 26 – Describe default‑deny rules • 27 – Explain how stateless packet‑filtering decisions are made • 28 – Compare stateless vs stateful filtering `                                                                                                                                                                                    | A bullet list of *all* outcomes inside the current unit, sorted by `globalSequence`. The model sees how the clicked outcome fits amongst its peers.                                                                 |

 */ // =============================================================================
// Prompt 1 — Context + Outline
// =============================================================================
// =============================================================================
// Prompt 1 — Context + Outline
// =============================================================================
export const createContextOutlinePromptUpdate = (
  programText,
  programName,
  unit,
  outcome,
  outcomeList,
) => {
  /* ── 1. Guard-clauses & sanitise ─────────────────────────────────── */
  const safeProgText = String(programText || "").trim() || "No programme data";
  const safeUnitName = unit?.name?.trim() || "Unnamed Unit";
  const unitSeq = Number.isFinite(unit?.sequence) ? unit.sequence : "?";
  const safeOutcome = outcome?.name?.trim() || "Unnamed Outcome";
  const globalSeq = Number.isFinite(outcome?.globalSequence)
    ? outcome.globalSequence
    : "?";
  const safeList = (outcomeList || "• No outcomes").trim();

  /* ── 2. System / user messages ───────────────────────────────────── */
  const userMessage = `
You are an expert educational content architect and senior learning designer creating content for a structured learning programme.

The program structure is:
- Program: multiple months of learning
- Module: roughly one month of full-time tuition
- Component: roughly one week of full-time tuition
- Unit: roughly one day of full-time tuition
- Learning Outcome: a single key concept or skill to be mastered in that day

You are working on a program called "${programName}".

────────────────────────────────────────
PROGRAMME HIERARCHY (overview only)
────────────────────────────────────────
${safeProgText}

────────────────────────────────────────
FOCUS for this task
────────────────────────────────────────
• Unit ${unitSeq}: "${safeUnitName}"
The learning outcomes that are part of this unit (${unitSeq}) are (in teaching order):
    ${safeList}

• Outcome that you will develop (globalSequence ${globalSeq}): ${safeOutcome}

────────────────────────────────────────
YOUR TASK – Prompt #1 (Context + Outline)
────────────────────────────────────────
Create a learner-centred teaching plan for the selected outcome.

1. Write "contextualSummary" as a short paragraph that explains:
   - why this outcome matters within the wider programme,
   - what the learner should understand or be able to do by the end,
   - how it connects to nearby outcomes in this unit.
   Write this in clear, motivating language suitable for a first-time learner.

2. Produce an "outline" array with 1 to 4 major items.
   Each outline item must be a real teachable section at outline/section level.
   It should be narrow enough to support a coherent lesson segment, not a broad compendium of reminders, tools, case lists, and recap material all at once.

3. Build the outline as a sequence of complementary sections.
   Do not make every outline item a self-sufficient mini-course that reteaches the whole outcome.
   Spread the teaching load across the sequence.
   Typical section roles may include:
   - introducing the core concept,
   - building the framework or model,
   - applying it to cases or decisions,
   - turning it into action, reflection, or practice.
   Use whatever role pattern genuinely fits the outcome.

4. For each outline item:
   - "title": a concise, learner-friendly section title.
   - "subPoints": specific subtopics in teaching order.
   - "notes": brief teaching guidance describing how the section should teach, not just what topic it names.

5. The "notes" for each outline item should explicitly help later prompts avoid repetition.
   Where appropriate, notes should mention:
   - the primary role of this section in the sequence,
   - what this section newly teaches,
   - what earlier section(s) may be briefly recalled but not fully repeated,
   - what named framework, model, key term, or matrix should be introduced here and then reused consistently later,
   - what type of example, case, activity, or reflection best fits this section,
   - whether a diagram, matrix, decision flow, process visual, comparison figure, graph, or illustration would genuinely improve understanding.

6. Build the outline for teaching quality, not just topic coverage.
   A strong outcome outline should create progression, not repetition.
   If one section introduces a framework, later sections should apply or extend it rather than redefine it from scratch.
   If one section is mainly reflective or action-oriented, it should not also reteach the full conceptual foundation.

7. Prefer fewer, stronger, role-specific sections over sections that all do the same job.
   Avoid outline items that are mainly:
   - long inventories of examples,
   - checklist collections,
   - toolkit compendiums,
   - stacked tips or misconceptions with little explanation,
   - repeated summary sections that restate earlier items.

8. The outline should naturally create room for selective teaching moves such as:
   - Why This Matters
   - In Plain English
   - Key Term
   - Worked Example
   - Common Mistake
   - Pro Tip
   - Try It Yourself
   - Checkpoint
   - Real-World Connection
   - Remember This
   - Mini Recap
   - Reflection Prompt
   - Challenge Extension
   - Before You Move On
   Use these ideas selectively. Do not force every teaching move into every section.

9. Keep the outline tightly scoped to the selected outcome.
   Do not pad with generic introductions.
   Avoid overlap and repetition across outline items.
   If a possible section feels too broad or too reference-heavy, narrow it so the later content can teach it well.

10. Return only valid JSON matching the exact structure below, with no extra text or fields:
{
  "contextualSummary": "",
  "outline": [
    {
      "title": "",
      "subPoints": [
        {
          "heading": "",
          "notes": ""
        }
      ]
    }
  ]
}

Constraints
• Be granular and plan teachable chunks.
• No overlaps or repetition.
• Sequencing inside the outline must build logically.
• Plan for learner-friendly narrative teaching, not note-style dumping.
• Use tables only when the concept clearly requires comparison or structured data.
• Prefer instructional visuals over decorative ones.
• Return JSON via the output_outcome_outline function-call only.

Important
- Do not include any disclaimers or explanations outside the JSON.
- Ensure the JSON is well-formed and parseable.

Please comply strictly by returning valid JSON only.
`.trim();

  /* ── 3. Request body for o3 with function-spec ───────────────────── */
  return {
    model: "o3",
    messages: [{ role: "user", content: userMessage }],
    functions: [
      {
        name: "output_outcome_outline",
        description:
          "Return the contextual summary and structured outline for one learning outcome.",
        parameters: {
          type: "object",
          properties: {
            contextualSummary: { type: "string" },
            outline: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  title: { type: "string" },
                  subPoints: {
                    type: "array",
                    items: {
                      type: "object",
                      properties: {
                        heading: { type: "string" },
                        notes: { type: "string" },
                      },
                      required: ["heading", "notes"],
                    },
                  },
                },
                required: ["title", "subPoints"],
              },
            },
          },
          required: ["contextualSummary", "outline"],
        },
      },
    ],
    function_call: { name: "output_outcome_outline" },
    max_completion_tokens: 16500,
  };
};

// export const createContextOutlinePromptUpdate = (
//   programText,
//   programName,
//   unit,
//   outcome,
//   outcomeList,
// ) => {
//   /* ── 1. Guard‑clauses & sanitise ─────────────────────────────────── */
//   const safeProgText = String(programText || "").trim() || "No programme data";
//   const safeUnitName = unit?.name?.trim() || "Unnamed Unit";
//   const unitSeq = Number.isFinite(unit?.sequence) ? unit.sequence : "?";
//   const safeOutcome = outcome?.name?.trim() || "Unnamed Outcome";
//   const globalSeq = Number.isFinite(outcome?.globalSequence)
//     ? outcome.globalSequence
//     : "?";
//   const safeList = (outcomeList || "• No outcomes").trim();

//   /* ── 2. System / user messages ───────────────────────────────────── */
//   const userMessage = `
// You are an expert educational content architect creating content for a learning program. The program structure is:
// - Program: multiple months of learning
// - Module: roughly one month of full-time tuition
// - Component: roughly one week of full-time tuition
// - Unit: roughly one day of full-time tuition
// - Learning Outcome: a single key concept or skill to be mastered in that day

// You are working on a program called "${programName}".
// ────────────────────────────────────────
// PROGRAMME HIERARCHY   (overview only)
// ────────────────────────────────────────
// ${safeProgText}

// ────────────────────────────────────────
// FOCUS for this task:
// ────────────────────────────────────────
// • Unit ${unitSeq}: “${safeUnitName}”
// The learning outcomes that are part of this unit (${unitSeq}) are (in teaching order)):
//     ${safeList}

// • Outcome that you will develop (globalSequence ${globalSeq}): ${safeOutcome}

// ────────────────────────────────────────
// YOUR TASK  – Prompt #1  (Context ✚ Outline)
// ────────────────────────────────────────
// **YOUR TASK (Prompt #1: Context & Outline)**:
// 1. Provide a short descriptive paragraph named "contextualSummary" that explains the purpose of this learning outcome in the context of the program.
// 2. Produce an "outline" array of maximum 4 items, where each element is an object:
//    - "title": The name of a major section.
//    - "subPoints": An array of objects, each containing:
//      - "heading": A subtopic name.
//      - "notes": A description on what will be covered in the subtopic.
// 2.1. The outline should be structured in a way that it can be used to create a detailed learning module. Ensure that the outline is comprehensive and covers all aspects of the learning outcome.
// 2.2. You can have a maximum of 4  items in the "outline" array. You are also allowed to have a single outline item if the learning outcome is simple. So you can have 1 , 2, 3 or 4 items in the outline array depending on the outcome.
// 3. Return only valid JSON matching the exact structure below (with no extra text or fields):
// {
//   "contextualSummary": "",
//   "outline": [
//     {
//       "title": "",
//       "subPoints": [
//         {
//           "heading": "",
//           "notes": ""
//         }
//       ]
//     }
//   ]
// }
// Constraints
// • Be granular – plan teachable chunks.
// • No overlaps or repetition.
// • Sequencing inside the outline must build logically.
// • **Return JSON via the   output_outcome_outline   function‑call only.**

// **Important**:
// - Do **not** include any disclaimers or explanations outside the JSON.
// - Ensure the JSON is well-formed and parseable.

// Please comply strictly by returning valid JSON only.
// `.trim();

//   /* ── 3. Request body for o3‑mini with function‑spec ──────────────── */
//   return {
//     model: "o3",
//     messages: [{ role: "user", content: userMessage }],
//     functions: [
//       {
//         name: "output_outcome_outline",
//         description:
//           "Return the contextual summary and structured outline for one learning outcome.",
//         parameters: {
//           type: "object",
//           properties: {
//             contextualSummary: { type: "string" },
//             outline: {
//               type: "array",
//               items: {
//                 type: "object",
//                 properties: {
//                   title: { type: "string" },
//                   subPoints: {
//                     type: "array",
//                     items: {
//                       type: "object",
//                       properties: {
//                         heading: { type: "string" },
//                         notes: { type: "string" },
//                       },
//                       required: ["heading", "notes"],
//                     },
//                   },
//                 },
//                 required: ["title", "subPoints"],
//               },
//             },
//           },
//           required: ["contextualSummary", "outline"],
//         },
//       },
//     ],
//     function_call: { name: "output_outcome_outline" },
//     // generous token‑limit – the outline itself is small, but context is large
//     max_completion_tokens: 16500,
//   };
// };

/**
/**
 * Generates Prompt #2 for producing detailed academic learning content for a single outline item,
 * **without** using high-level structural HTML tags like <header>, <section>, or <article>.
 *
 * @param {string} contextOfProgram - A string providing the broad program context,
 *                                    including program, module, unit, and outcome sequence details.
 * @param {string} fullOutline - The complete structured outline for the learning outcome (passed as a string).
 * @param {string} outlineItemText - A text representing the specific outline item to develop.
 *                                   This text should include the title and subpoints in the following format:
 *                                   Title: "Section Title"
 *                                   SubPoints:
 *                                   - Subtopic 1: Description of subtopic.
 *                                   - Subtopic 2: Description of subtopic.
 *
 * @returns {object} - A requestBody object containing the prompt payload for the AI model.
 */

/**
 * Generates Prompt #2 for producing detailed academic learning content for a single outline item,
 * **without** using high-level structural HTML tags like <header>, <section>, or <article>.
 *
 * @param {string} contextOfProgram - A string providing the broad program context,
 *                                    including program, module, unit, and outcome sequence details.
 * @param {string} fullOutline - The complete structured outline for the learning outcome (passed as a string).
 * @param {string} outlineItemText - A text representing the specific outline item to develop.
 *                                   This text should include the title and subpoints in the following format:
 *                                   Title: "Section Title"
 *                                   SubPoints:
 *                                   - Subtopic 1: Description of subtopic.
 *                                   - Subtopic 2: Description of subtopic.
 *
 * @returns {object} - A requestBody object containing the prompt payload for the AI model.
 */
export const createDetailedContentForOutlineItem = async (
  contextOfProgram,
  fullOutline,
  outlineItemText,
) => {
  try {
    // Build the message content for the "user" role
    const userMessage = `
You are an expert educator producing academic-quality, world-class learning content that is extremely practical and focused on real-world skills transfer.

**PROGRAM CONTEXT**:
${contextOfProgram}

**COMPLETE PROGRAM OUTLINE**:
${fullOutline}

**OUTLINE ITEM TO DEVELOP**:
${outlineItemText}

**YOUR TASK**:
1. Develop detailed, professionally written learning content **only** for the specified outline item.
2. Ensure that your content is cohesive and flows logically; even though you must develop content solely for this item, consider the entire outline so the explanation integrates seamlessly with previous and subsequent topics.
3. Follow best academic writing principles: be clear, precise, thorough, and skill-oriented.
4. **DO NOT** include structural wrapper tags such as <header>, <section>, <article>, or <footer>. Instead, use these allowed tags:
   - **Headings**: <h1>, <h2>, <h3>, <h4>, <h5>
   - **Paragraphs**: <p>
   - **Lists**: <ul>, <li>, <ol>
   - **Code blocks**: <code>...</code>
   - **Tables**: <table>, <thead>, <tbody>, <tr>, <th>, <td>
   - **Inline emphasis**: <strong>, <em>, <span>
   - **Placeholders**: <insert: [brief explanation or code block]>
   - **Custom Tags** (for special styling in our front-end):
       - <statement> : For standout text or key terms that need highlight. Ideal for important statement or concept.
       - <separator> : For visual dividers or pacing between major segments.
       - <paragraphcard> : For emphasized paragraphs displayed in a card-like layout (use for especially important or featured text).
       - <executable> :  Tag for presenting **Executable JavaScript Code** Usage <executable>...</executable> where ... represents executable JavaScript. This is for valid JavaScript code that runs in a code editor terminal within the browser, producing visible output like console.log; The tag is used to practically demonstrate executable code that the learner can interact with. When using the executable tag please ensure to use code  comments.
5. If a table would enhance understanding, include it as valid HTML (e.g., <table>, <thead>, <tbody>, <tr>, <th>, <td>).
6. If images, graphs, or diagrams would add value, insert a placeholder <insert: [brief explanation of the image/graph/diagram]>.
7. If a screenshot (e.g., a code environment) can add value, include the same <insert: ...> placeholder.
8. If code examples are relevant (in any language), wrap them in <code>...</code> tags.
9. Do NOT generate content for other outline items, and do NOT include assessments or test questions. Focus solely on instructional content for this one outline item.
10. Return **only** the final written learning content (in plain HTML using the tags above). **No** extra commentary, markdown, or disclaimers.

Remember to use <p>, <emphasized>, <paragraphcard>, or <separator> if the text naturally fits those custom stylings or if a visual break is beneficial. Keep the writing skill-oriented and academically sound.

Return only the final material as HTML without <header>, <section>, <article>, or <footer>.
    `;

    // Construct the request payload for the AI model
    const requestBody = {
      // model: "gpt-4o-mini",
      model: "gpt-4.5-preview",
      messages: [
        {
          role: "user",
          content: userMessage,
        },
      ],
      max_tokens: 10500, // Adjust as needed
      temperature: 0.3,
    };

    return requestBody;
  } catch (error) {
    console.error("Error generating detailed content for outline item:", error);
    throw new Error("Unable to generate detailed content for outline item.");
  }
};

// PROMPT 2 UPDATED
/**
 * Build Prompt #2 – “Write full learning content for ONE outline item”
 *
 * @param programText   Multiline string printing the whole programme tree
 * @param programName   Name of the programme
 * @param unit          { name: string, sequence: number }
 * @param outcome       { name: string, globalSequence: number }
 * @param outcomeList   Bullet list (string) of ALL outcomes in this unit
 * @param fullOutline   The **whole** outline object produced in Prompt #1
 * @param outlineItem   One section object from fullOutline.outline ⇒ {
 *                        title: string,
 *                        subPoints: [{ heading, notes }]
 *                      }
 * @param outcomeId     Unique identifier for the outcome
 * @returns OpenAI request body for o3-mini
 */
export const createDetailedContentForOutlineItemV2 = (
  programText,
  programName,
  unit,
  outcome,
  outcomeList,
  fullOutline,
  outlineItem,
  outcomeId,
) => {
  /* ── 1 · Sanitise --------------------------------------------------- */
  const safeProgText = (programText || "").trim() || "No programme data";
  const safeProgName = (programName || "").trim() || "Unnamed Programme";

  const safeUnitName = (unit?.name || "").trim() || "Unnamed Unit";
  const unitSeq = Number.isFinite(unit?.sequence)
    ? unit.sequence
    : (console.warn("Missing unit.sequence, defaulting to 0"), 0);

  const safeOutcome = (outcome?.name || "").trim() || "Unnamed Outcome";
  const globalSeq = Number.isFinite(outcome?.globalSequence)
    ? outcome.globalSequence
    : "?";

  const safeList = (outcomeList || "• No outcomes").trim();

  const sectionTitle = outlineItem?.title?.trim() || "Untitled Section";
  const sectionPoints = (outlineItem?.subPoints || [])
    .map(
      (sp) =>
        `• ${sp.heading?.trim() || "No heading"}: ${sp.notes?.trim() || ""}`,
    )
    .join("\n");

  const safeOutcomeId = (outcomeId || "").trim() || "UNKNOWN_OUTCOME_ID";

  /* stringify full outline once for readability */
  const outlinePretty = JSON.stringify(fullOutline, null, 2);

  /* ── 2 · User prompt ------------------------------------------------ */
  const userMessage = `
  You are an expert educator producing academic-quality, world-class learning content that is extremely practical and focused on real-world skills transfer.
  You have the primary purpose of producing rigorous, practice-oriented learning material that flows naturally within the larger curriculum.
  The program structure that you are working on is well defined, and you will develop content within this structure.
  |- Program: multiple months of learning. Program is built on multiple modules.
  |-- Module: roughly one month of full-time tuition. Module is built on multiple components.
  |--- Component: roughly one week of full-time tuition. Component is built on multiple units.
  |---- Unit: roughly one day of full-time tuition. A component is built on multiple outcomes.
  |----- Learning Outcome: a single key concept or skill to be mastered in that day

  A Single Learning Outcome is further broken down into an outline of sections and subpoints (or also referred to as outline items). You will develop the complete learning content for a single outline item as specified below. 

────────────────────────────────────────
PROGRAMME CONTEXT
────────────────────────────────────────
Programme: "${safeProgName}"

${safeProgText}

────────────────────────────────────────
FOCUS FOR THIS TASK WILL BE ON A SINGLE OUTLINE ITEM INSIDE AN OUTCOME
────────────────────────────────────────
• Unit ${unitSeq}: "${safeUnitName}"
  Outcomes in this unit (teaching order):
  ${safeList}

• The specific outcome that you will be focusing on is:
  (globalSequence ${globalSeq}):
  "${safeOutcome}"

────────────────────────────────────────
COMPLETE OUTCOME OUTLINE
────────────────────────────────────────
${outlinePretty}

────────────────────────────────────────
OUTLINE ITEM TO DEVELOP - *write content for this only*
────────────────────────────────────────
Outline Item: "${sectionTitle}"

Sub-points
${sectionPoints}

────────────────────────────────────────
CONTENT REQUIREMENTS
────────────────────────────────────────
You are required to develop the completed, detailed, and professionally written learning content for the specified outline item. The learning content must be teachable and practical. The content must be cohesive and flow logically; even though you must develop content solely for this item, consider the entire outline so the explanation integrates seamlessly with previous and subsequent topics.
Instructions:
1. Write **only** the content for *this* outline section (ignore siblings). You can add more sub-points if the current sub-points do not sufficiently cover the topic.
2. Follow best academic writing: clear, precise, skill-centred.
2.1. Be thorough and complete. Make sure that all concepts and understanding are completely covered. 
3. Follow best academic writing principles: be clear, precise, thorough, and skill-oriented.
  3.1  **MUST start with one “hero” banner image** (photo-realistic).  
    • style **must be** "full-screen-dark" **or** "full-screen-light".  
    • "description" **must equal** the section title (“${sectionTitle}”).  
    • "prompt" – 40-100-word, vivid, *photo-realistic* guidance and
      **explicitly state** “dark theme” or “light theme” to match the chosen style.  
    • Scene prompt instructions: Set a real world scene that visually represents the section topic. Describe the scene and the characters and emotions in the scene in detail. Describe the lighting, mood, and colour palette. Ensure it to be real world scenarios with real world characters. 
4. Keep close attention to the tuition sequence and flow of the overall program and where the current outline item fits in the overall program. The sequence of the content must build logically and have a rational tuition sequence flow to it.
    • You can refer back to concepts from previous outline items, but do not repeat them.
    • If you are refering back to a concept from a previous outline item that is a foundational or important concept to understand the current outline item, then you can briefly explain it again in the context of the current outline item. In this case add a Quick Refresher part within your content to explain the concept again in the context of the content being covered in the current outline item.
    • You are allowed to make reference to where the learner is in the wider program, module, component, and unit if it is appropriate to do so within the context of the outline item.
    • If you are covering a concept that is important to understand for a future part of the program, module, component, or unit, then you can briefly explain it in the context of the current outline item. In this case add a Quick Preview part within your content to explain the concept in the context of the content being covered in the current outline item. This must have a natural flow to it and add to the learning experience.
    • If the concept you are covering now is considered a foundational concept or critical part to understand for a future part of the program, module, component, or unit, then you can outline directly in practical terms why the current concept is critical to fully understand and how it will impact the future parts of the program. 
4. ALLOWED HTML (no <header>, <section>, <article>, <footer>):
   • Headings: <h1-h5>
   • Paragraph: <p>
   • Lists: <ul>, <ol>, <li>
   • Code: <code>…</code>
   • Tables: <table>, <thead>, <tbody>, <tr>, <th>, <td>
   • Inline: <strong>, <em>, <span>

   • Custom tags recognised by our front-end
     ─────────────────────────────────────────
     <statement>      Highlight a key fact or definition.
     <separator>      Visual divider between major segments.
     <paragraphcard>  Emphasised paragraph shown in a “card”.
     <executable> … </executable>
                     JavaScript that actually runs in-browser. Python code will not work.

  • Code snippets (code examples)
     ─────────────
       – JavaScript that should run in-browser ⇒ wrap in:
         <executable> …valid JS with comments… </executable> -> try and always write JavaScript that is executable as far as possible.

       – **Any other language** (Python, Bash, SQL, etc.) ⇒ wrap in:
         <code> …non-JS code… </code>

  • Placeholders (<insert: …>)
     ─────────────
       Use when a *non-HTML* visual would add value (examples: graph / diagram / complex figure]):

       <insert: [brief explanation of the graph / diagram / complex figure]>
       <insert: [brief explanation of the screenshot]>

       ▸ Keep the explanation short but specific (what the learner will see).

  • Rich images (<image> … </image>).
     ────────────
       The <image> tag is used to include images that enhance the learning content in both style and in context. Each <image> tag must contain a JSON string specifying the image details, formatted as follows:
       <image>{"outcomeId":"${safeOutcomeId}","fileName":"image_[SEQUENCE].jpg","description":"[DESCRIPTION]","style":"[STYLE]","paragraph":"[PARAGRAPH]"}</image>

       **Fields**:
       - **outcomeId**: The unique identifier for the outcome, provided as "${safeOutcomeId}".
       - **fileName**: A unique file name for the image, e.g., "image_1.jpg", "image_2.png". Use ".jpg" or ".png" based on context.
       - **description**: *Very short* (≤ 15 words) caption that appears on-screen or below the image.  
       - **prompt**: *Detailed*, photorealistic text-to-image prompt (40–100 words). This is sent to the diffusion model; it should mention lighting, mood, colour palette, camera angle, etc. **Do not reuse description here.**
       - **style**: One of the following, chosen based on context:
         - "default": Centered image with a caption below (use for general illustrations).
         - "full-screen": Full-width image without overlay (use for large visuals).
         - "full-screen-dark": Full-width image with dark overlay caption (use for banners or headers).
         - "full-screen-light": Full-width image with light overlay caption (use for brighter visuals).
         - "square": 400x400px image with caption (use for compact diagrams).
         - "dark-overlay": Image with dark overlay caption (use for emphasized visuals).
         - "light-overlay": Image with light overlay caption (use for softer visuals).
         - "image-left": Image on the left with text on the right (use for side-by-side content).
         - "image-right": Image on the right with text on the left (use for side-by-side content).
       - **paragraph**: Text to accompany the image for "image-left" or "image-right" styles; use "" for other styles.

       **Usage**:
       - Use <image> tags when an image enhances understanding (e.g., diagrams, charts, or illustrative visuals).
       - You can use multiple <image> tags in a single section, but ensure they are relevant to the content.
       - You are encouraged to use no academic but still content relevant image tags for section titles or large visuals.
       - Ensure the JSON string is valid and properly escaped within the <image> tag.
       - Choose the "style" based on the content’s context (e.g., use "full-screen-dark" for section headers, "image-left" for explanatory text with visuals).
       - The "description" should be clear and specific to aid accessibility and provide context.
       - **Always** supply both "description" **and** "prompt".  
         • description is learner-facing alt-text / overlay (concise).  
         • prompt is artist-facing – long, vivid, guiding the AI to produce a photorealistic result that matches the lesson’s theme.

       **Examples**:
             <image>{
         "outcomeId":"${safeOutcomeId}",
         "fileName":"image_1.jpg",
         "description":"Diagram of a network topology",
         "prompt":"Ultra-realistic 3-D network topology visual, soft rim-light, dark background, neon cable paths, depth-of-field",
         "style":"default",
         "paragraph":""
       }</image>

       <image>{
         "outcomeId":"${safeOutcomeId}",
         "fileName":"hero_${sectionTitle
           .replace(/\\s+/g, "_")
           .toLowerCase()}.jpg",
         "description":"${sectionTitle}",
         "prompt":"Photo-realistic modern software studio, diverse developers clustered around a huge display presenting the <${sectionTitle}> headline, rich depth-of-field, dark theme, cinematic contrast, blue accent LEDs",
         "style":"full-screen-dark",
         "paragraph":""
       }</image>

       <image>{
         "outcomeId":"${safeOutcomeId}",
         "fileName":"packet_header.png",
         "description":"Structure of a TCP packet header",
         "prompt":"Clean flat-design infographic of TCP packet header layers, minimalistic palette, clear labels, 16:9 ratio, white background",
         "style":"image-right",
         "paragraph":"This diagram illustrates the structure of a TCP packet header."
       }</image>

   Anything not in this whitelist must **not** be used.
   Please note that images can be used to add both academic or visual value to the learning content. Examples of just adding visual content is a large section title overlay with some image behind it. You can also use realistic looking images as illustrations that fit with the theme of the content. (e.g. a team of software developers drinking coffee and talking infront of a screen).

Notes to Remember:
- Do NOT generate content for other outline items, and do NOT include assessments or test questions. Focus solely on instructional content for this one outline item.
- Ensure that the content is cohesive and flows logically with the overall learning outcome.
- Use code examples or <executable> tags (or <code> for non-JS languages) only if the topic is programming or technical in nature.
- Return **only** the final written learning content (in plain HTML using the tags above). **No** extra commentary, markdown, or disclaimers.
- Remember to use <p>, <statement>, <paragraphcard>, or <separator> if the text naturally fits those custom stylings or if a visual break is beneficial. Keep the writing skill-oriented and academically sound.
- No content for other outline items, no assessments or test questions.
- Return **only** the final HTML – no markdown, comments, or explanations.
`.trim();

  /* ── 3 · OpenAI request body --------------------------------------- */
  return {
    model: "o3",
    messages: [{ role: "user", content: userMessage }],
    max_completion_tokens: 16500,
  };
};

export const createRawContentForOutlineItemV1GPT5 = (
  programText,
  programName,
  unit,
  outcome,
  outcomeList,
  fullOutline,
  outlineItem,
) => {
  const safeProgText = (programText || "").trim() || "No programme data";
  const safeProgName = (programName || "").trim() || "Unnamed Programme";

  const safeUnitName = (unit?.name || "").trim() || "Unnamed Unit";
  const unitSeq = Number.isFinite(unit?.sequence)
    ? unit.sequence
    : (console.warn("Missing unit.sequence, defaulting to 0"), 0);

  const safeOutcome = (outcome?.name || "").trim() || "Unnamed Outcome";
  const globalSeq = Number.isFinite(outcome?.globalSequence)
    ? outcome.globalSequence
    : "?";

  const safeList = (outcomeList || "• No outcomes").trim();

  const sectionTitle = outlineItem?.title?.trim() || "Untitled Section";
  const sectionPoints = (outlineItem?.subPoints || [])
    .map(
      (sp) =>
        `• ${sp.heading?.trim() || "No heading"}: ${sp.notes?.trim() || ""}`,
    )
    .join("\n");

  const outlinePretty = JSON.stringify(fullOutline, null, 2);

  const userMessage = `
You are an expert educator and learning experience writer producing academically rigorous but highly readable learning material.
Write as if a skilled teacher is guiding a learner through the topic in real time.

Your task: write the complete instructional content for ONE outline item only.
The result should feel like a coherent lesson segment inside a larger sequence, not a full mini-course that tries to reteach the whole outcome.
It must fit the wider outcome and unit context, but you must not write content for sibling outline items.

────────────────────────────────────────
PROGRAMME CONTEXT
────────────────────────────────────────
Programme: "${safeProgName}"

${safeProgText}

────────────────────────────────────────
FOCUS FOR THIS TASK WILL BE ON A SINGLE OUTLINE ITEM INSIDE AN OUTCOME
────────────────────────────────────────
• Unit ${unitSeq}: "${safeUnitName}"
  Outcomes in this unit (teaching order):
  ${safeList}

• Target outcome (globalSequence ${globalSeq}):
  "${safeOutcome}"

────────────────────────────────────────
COMPLETE OUTCOME OUTLINE (for context only)
────────────────────────────────────────
${outlinePretty}

────────────────────────────────────────
OUTLINE ITEM TO DEVELOP — write content for this only
────────────────────────────────────────
Outline Item: "${sectionTitle}"

Sub-points
${sectionPoints}

────────────────────────────────────────
LANGUAGE & COMPLEXITY RULES
────────────────────────────────────────
• Audience: mixed-ability learners, including students who are new to the topic.
• Aim for a reading level similar to upper high school.
• Use short, direct sentences and clear vocabulary.
• When a technical term is necessary:
  - explain it in plain language first,
  - then give the precise meaning.
• Avoid unnecessary jargon, heavy abstraction, and long chains of clauses.
• Break complex ideas into small, understandable steps.
• Do not assume deep prior knowledge. If background knowledge is needed, include a short, friendly recap inside the flow.

────────────────────────────────────────
SEQUENCE AWARENESS — VERY IMPORTANT
────────────────────────────────────────
Use the full outcome outline and the notes above to infer this section’s role in the overall sequence.

Assume that:
• outline items earlier in the teaching order have already been taught,
• this current outline item is the learner’s present focus,
• later outline items have not yet been taught.

Therefore:
• You MAY briefly remind the learner of an earlier concept when needed for continuity.
• You must NOT fully reteach earlier outline items unless a very short refresher is essential.
• You must NOT pre-teach later outline items in detail.
• If the notes mention a named framework, model, matrix, tool, or key term, preserve that exact name and do not invent a parallel label.

This section should make its own contribution to the sequence, not repeat the whole outcome.

────────────────────────────────────────
THE CORE WRITING GOAL
────────────────────────────────────────
Teach the concept by unfolding it.
Do not just name points, label them, and move on.
Stay with important ideas long enough to explain:
- what they mean,
- why they matter,
- how they work,
- where they help,
- where they fail,
- what the learner should notice in practice.

At the same time, be selective.
Complete this section well, but do not try to exhaustively cover everything the entire outcome could say.

────────────────────────────────────────
IMPORTANT FAILURE MODES TO AVOID
────────────────────────────────────────
Do NOT produce content that reads like:
- heading
- one sentence
- heading
- one sentence
- short list
- another heading
- another isolated point

Do NOT turn the section into:
- a long inventory of examples,
- a toolkit compendium,
- a stack of mini-definitions,
- a sequence of cards without connective teaching,
- a repeated summary of ideas already covered elsewhere in the outcome.

Not every section needs the full teaching kit.
Do not automatically include every type of support block in every section.

────────────────────────────────────────
NARRATIVE-FIRST LESSON DESIGN RULES
────────────────────────────────────────
• Default to connected explanatory paragraphs.
• Most important ideas should be taught in normal prose first.
• Use headings to organise the lesson, not to fragment it.
• Avoid serial micro-headings where each carries only one tiny point.
• Use lists only as support, not as the main teaching method.
• Use tables only when side-by-side comparison, classification, structured values, or data patterns are genuinely clearer in a table.
• If a point can be taught more clearly as prose, write prose.
• If you use a list or table, introduce it with a short explanatory paragraph and, where helpful, follow it with a short interpreting sentence or paragraph.
• A learner should never be left with a bare definition, bullet list, or table without explanation.

────────────────────────────────────────
ROLE-SPECIFIC TEACHING
────────────────────────────────────────
Let this section’s role shape the teaching style.

If this section mainly introduces a concept or model:
• spend more time explaining and clarifying,
• use one strong example to ground it,
• avoid turning the section into a long case library.

If this section mainly applies a framework or concept:
• keep recap brief,
• move quickly into cases, comparisons, judgement, or worked reasoning,
• focus on how to use the framework rather than redefining it from scratch.

If this section is mainly reflective, planning-oriented, or action-oriented:
• keep conceptual restatement brief,
• focus on choices, implementation, guardrails, next steps, or personal application,
• include only the minimum reminder needed for continuity.

────────────────────────────────────────
HOW TO TEACH IMPORTANT CONCEPTS
────────────────────────────────────────
For each important concept in this outline item, do as many of the following as the content genuinely needs:
• show why it matters,
• explain it in plain English,
• give the precise meaning or definition,
• walk through how it works,
• show a worked, practical, or real-world example,
• point out a common mistake or misconception where relevant,
• add a helpful tip, contrast, or nuance where useful,
• connect it to a workplace, real-world, or everyday setting,
• briefly recap the key idea before moving on.

Use these teaching blocks selectively and naturally when they help:
• Why This Matters
• In Plain English
• Key Term
• Worked Example
• Common Mistake
• Pro Tip
• Try It Yourself
• Checkpoint
• Real-World Connection
• Remember This
• Mini Recap
• Reflection Prompt
• Challenge Extension
• Before You Move On

These are learning-support blocks, not formal assessments.
A few strong support blocks are better than many small ones.
Choose only the blocks that truly strengthen this section’s role.
Try It Yourself, Checkpoint, Reflection Prompt, Challenge Extension, and Before You Move On may appear as brief learner prompts, but do not provide answer keys and do not turn them into a quiz bank.

────────────────────────────────────────
EXAMPLES, PRACTICALITY, AND TRANSFER
────────────────────────────────────────
• Every important concept should be grounded in at least one concrete example, scenario, or practical illustration unless the concept is already being taught through an example.
• Prefer one or two richer, more developed examples over a long inventory of thin examples.
• When the topic involves a process, workflow, comparison, judgement call, or decision, walk the learner through it step by step.
• Show what good practice looks like, not just what the concept means.
• Where useful, include short real-world consequences, edge cases, or everyday applications.
• If the section includes a tool, checklist, matrix, rubric, or framework, first explain how to think with it before presenting the condensed version.

────────────────────────────────────────
ANALOGIES AND MINI-STORIES
────────────────────────────────────────
• You may use brief analogies or mini-stories when they make an abstract idea easier to understand.
• Keep them faithful to the real concept.
• Always link the analogy back to the actual topic.
• If you use a mini-story, let it carry a concept forward rather than appearing as a disconnected extra.

────────────────────────────────────────
VISUALS
────────────────────────────────────────
There are two distinct visual roles in this lesson flow:

1) Section title / title-treatment visuals
• At the very start of this outline item, include one <image description="..."/> placeholder intended to become a full-screen opening title image later.
• This opening title image is required.
• Its job is decorative and atmospheric. It is not an instructional figure.
• It must be photorealistic.
• It must NOT be a graph, matrix, diagram, process flow, chart, infographic, screenshot, dashboard, interface, labelled visual, whiteboard explanation, slide, poster, document spread, or any other educational graphic.
• It must NOT contain readable words, letters, numbers, captions, labels, logos, branding, watermarks, signage, interface text, whiteboard writing, sticky-note text, slide text, document text, book-cover text, packaging text, screen text, or any other embedded typography that competes with the title overlay.
• The LMS will place the section title on top of the image later, so the image itself must remain visually clean and text-free.
• It should depict a realistic, photorealistic situation, environment, landscape, building, workspace, activity, or human scene that captures the theme of the section in a creative and inviting way.
• It should feel premium, modern, cinematic, realistic, and visually rich.
• It should visually relate to the subject matter without becoming literal instructional content.
• It should include clear negative space for later title overlay. The title area should remain calm, uncluttered, and free from distracting objects or text-like details.
• Do NOT default to "people sitting in a meeting room" unless that is genuinely the strongest visual expression of the section theme.
• Vary the style of title images across sections.
• Choose the most visually expressive scene archetype for the topic, such as:
  - a city or architectural scene,
  - a workplace environment,
  - a decision-making moment,
  - a hands-on professional activity,
  - a symbolic real-world object scene,
  - a studio, lab, operations floor, or control room,
  - a landscape or environmental scene with thematic relevance,
  - a corridor, lobby, skyline, transport hub, campus, or building exterior,
  - a human moment that suggests the topic without turning into a cliché corporate meeting.
• Think like a high-end editorial or cinematic photo director.
• The scene should feel alive, purposeful, and visually memorable.
• Make the opening image description richly detailed and explicit. Do not make it short.
• Use the description to give the later image-generation step a vivid and highly specific brief.
• Include whatever detail is useful, such as:
  - subject matter,
  - setting,
  - activity,
  - people and roles if present,
  - objects and props,
  - architecture or environment,
  - composition,
  - camera angle,
  - depth of field,
  - lighting,
  - mood,
  - colour palette,
  - realism cues,
  - what should be prominent,
  - what should be absent,
  - where negative space should remain clean for title overlay.
• Explicitly state that there must be no readable text, letters, numbers, logos, signage, or watermarks in the image.
• Write the description as plain text inside the attribute. Do not use internal double quotation marks inside the description attribute.

2) Instructional visuals inside the body
• Also look actively for opportunities to include visuals that genuinely help the learner understand the section.
• Bias strongly toward instructional visuals when they can replace or reduce explanatory text.
• Good reasons for an instructional visual include:
  - hierarchy or layered relationship,
  - process flow,
  - decision path,
  - comparison,
  - timeline,
  - graph or trend,
  - labelled diagram,
  - matrix or quadrant,
  - annotated real-world scene,
  - workflow or handoff,
  - step sequence,
  - before/after contrast.
• Instructional visuals may be diagrams, process graphics, comparison visuals, or other teaching figures where appropriate.

Additional title-treatment guidance
• For especially important major internal section breaks or very large title moments inside the section, you MAY add another <image description="..."/> placeholder intended as a full-screen or overlay title treatment.
• Use this sparingly and only for major conceptual shifts, not ordinary subsections.
• These internal title-treatment visuals should also be photorealistic, decorative, and free of readable text.

General image guidance
• Prefer visuals that teach a relationship, process, structure, or decision rule when the image is not a title-treatment image.
• Decorative visuals are correct and desirable for opening and major title-treatment use.
• Make image descriptions clear, explicit, and richly detailed.
• Do not limit yourself to brief descriptions.
• If a body visual carries part of the explanation, do not fully duplicate every label or cell in prose unless needed for interpretation.
• Do not add visuals only to fill space.

────────────────────────────────────────
STRUCTURE & PACING
────────────────────────────────────────
• Start with a short orienting explanation so the learner knows what this section is about.
• Build from foundation → explanation → example → application → consolidation where appropriate.
• Maintain a clear teaching spine through the section.
• Use bridge paragraphs when moving from one major idea to the next.
• If the section contains a framework, matrix, or list of patterns, explain the pattern in prose before compressing it into a summary form.
• Do not automatically end with a full recap, reflection, or action plan unless this section’s role genuinely calls for it.
• The overall feel should be that of a guided lesson, not lecture notes or reference material.

────────────────────────────────────────
MINIMAL HTML FOR CLARITY (optional)
────────────────────────────────────────
• You MAY use these tags only where they improve readability or structure:
  - Headings: <h1>, <h2>, <h3>, <h4>, <h5>
  - Paragraphs: <p>
  - Lists: <ul>, <ol>, <li>
  - Tables: <table>, <thead>, <tbody>, <tr>, <th>, <td>
• Do not use any other HTML tags here.

────────────────────────────────────────
STYLE & SCOPE CONSTRAINTS
────────────────────────────────────────
• No formal assessments or answer keys in this step.
• No Markdown, no code fences, and no HTML beyond the minimal tags above and optional <image/> placeholders.
• Do not wander into sibling outline items; stay scoped to "${sectionTitle}".

────────────────────────────────────────
OUTPUT FORMAT
────────────────────────────────────────
Return the content directly as plain text with optional minimal HTML and optional <image/> placeholders—nothing else.
`.trim();

  return {
    endpoint: "responses",
    responses: {
      model: "gpt-5",
      input: [
        {
          role: "user",
          content: [{ type: "input_text", text: userMessage }],
        },
      ],
      reasoning: { effort: "high" },
      text: { verbosity: "high" },
      max_output_tokens: 16500,
    },
  };
};

// export const createRawContentForOutlineItemV1GPT5 = (
//   programText,
//   programName,
//   unit,
//   outcome,
//   outcomeList,
//   fullOutline,
//   outlineItem,
// ) => {
//   // — sanitize inputs (same pattern as your other creators)
//   const safeProgText = (programText || "").trim() || "No programme data";
//   const safeProgName = (programName || "").trim() || "Unnamed Programme";

//   const safeUnitName = (unit?.name || "").trim() || "Unnamed Unit";
//   const unitSeq = Number.isFinite(unit?.sequence)
//     ? unit.sequence
//     : (console.warn("Missing unit.sequence, defaulting to 0"), 0);

//   const safeOutcome = (outcome?.name || "").trim() || "Unnamed Outcome";
//   const globalSeq = Number.isFinite(outcome?.globalSequence)
//     ? outcome.globalSequence
//     : "?";

//   const safeList = (outcomeList || "• No outcomes").trim();

//   const sectionTitle = outlineItem?.title?.trim() || "Untitled Section";
//   const sectionPoints = (outlineItem?.subPoints || [])
//     .map(
//       (sp) =>
//         `• ${sp.heading?.trim() || "No heading"}: ${sp.notes?.trim() || ""}`,
//     )
//     .join("\n");

//   const outlinePretty = JSON.stringify(fullOutline, null, 2);

//   // — prompt (content-first; minimal HTML permission + simplified language)
//   const userMessage = `
// You are an expert educator producing academically rigorous **but very accessible** learning material.
// Write in clear, simple language so that learners of different levels can follow confidently.

// Your task: **Write the complete instructional content for ONE outline item only**.
// Focus on clarity, correctness, completeness, and practical skills transfer.
// The content must be cohesive in the broader outcome/unit context, but do not write content for sibling outline items.

// ────────────────────────────────────────
// PROGRAMME CONTEXT
// ────────────────────────────────────────
// Programme: "${safeProgName}"

// ${safeProgText}

// ────────────────────────────────────────
// FOCUS FOR THIS TASK WILL BE ON A SINGLE OUTLINE ITEM INSIDE AN OUTCOME
// ────────────────────────────────────────
// • Unit ${unitSeq}: "${safeUnitName}"
//   Outcomes in this unit (teaching order):
//   ${safeList}

// • Target outcome (globalSequence ${globalSeq}):
//   "${safeOutcome}"

// ────────────────────────────────────────
// COMPLETE OUTCOME OUTLINE (for context only)
// ────────────────────────────────────────
// ${outlinePretty}

// ────────────────────────────────────────
// OUTLINE ITEM TO DEVELOP — *write content for this only*
// ────────────────────────────────────────
// Outline Item: "${sectionTitle}"

// Sub-points
// ${sectionPoints}

// ────────────────────────────────────────
// LANGUAGE & COMPLEXITY RULES
// ────────────────────────────────────────
// • Audience: mixed-ability learners, including students who are new to the topic.
// • Aim for a reading level similar to **upper high school** (around age 15–17).
// • Use **short sentences** (most under ~20 words).
// • Prefer **simple, direct vocabulary** instead of very technical or academic wording.
// • When you must use a technical term:
//   – Introduce it in **plain language first** (“In simple terms…”).
//   – Then give the precise definition.
// • Avoid long chains of commas, nested clauses, and unnecessary jargon.
// • Break complex ideas into **small, clear steps**.
// • Use headings, short paragraphs, and bullet lists where it helps learners follow.
// • Do not assume deep prior knowledge. If prior knowledge is needed, give a short, friendly recap.

// ────────────────────────────────────────
// INSTRUCTIONS (WRITE WORLD-CLASS, LEARNER-FIRST MATERIAL)
// ────────────────────────────────────────
// 1) Learner-first, world-class quality
//    • Imagine the learner’s full journey through this topic. Make it engaging, motivating, and humane.
//    • Write to inspire curiosity while remaining precise and academically sound.
//    • Always favour **clarity over showing off complexity**.

// 2) Thorough conceptual coverage
//    • Ensure the learner achieves a **firm understanding** by the end.
//    • Define key terms clearly, connect ideas logically, and build understanding step-by-step.
//    • Call out **common misconceptions** and correct them in simple language.
//    • When prior knowledge is assumed, include a brief refresher (integrated inline, not as a separate lesson).

// 3) Practicality and transfer
//    • If the topic is practical, include **several concrete, real-world examples** and micro-walkthroughs.
//    • Show typical use cases, edge cases, and “what good looks like”.
//    • Where helpful, include brief bullet checklists of actions/considerations.

// 4) Narrative devices that teach
//    • Use **short analogies** and **mini stories** where appropriate to make abstract ideas concrete.
//    • Keep analogies faithful; explicitly link each part of the analogy back to the real concept.

// 5) Pacing & structure for comprehension
//    • Sequence ideas from foundation → application → nuance.
//    • Insert short “why this matters” moments to sustain motivation.
//    • Offer brief recaps at natural breaks to consolidate learning.

// 6) Minimal HTML for clarity (optional)
//    • You MAY use these tags **only** where they improve readability or structure:
//      – Headings: <h1>, <h2>, <h3>, <h4>, <h5>
//      – Paragraphs: <p>
//      – Lists: <ul>, <ol>, <li>
//      – Tables: <table>, <thead>, <tbody>, <tr>, <th>, <td>
//    • Do **not** use any other HTML tags here.

// 7) Image placeholders (optional, only if valuable)
//    • Where a visual would meaningfully aid understanding, insert:
//      <image description="[very short description of what the image should depict]"/>
//      – Keep description ≤ 15 words.
//      – Do not include any other image metadata.

// 8) Style & scope constraints
//    • **No** assessments/test questions in this step.
//    • **No** Markdown, **no** code fences, and **no** HTML beyond the minimal tags above.
//    • Do **not** wander into sibling outline items; stay scoped to **"${sectionTitle}"**.

// 9) Output format
//    • Return the content directly as plain text with optional minimal HTML (lists/tables) and optional <image/> placeholders—nothing else.
// `.trim();

//   return {
//     endpoint: "responses",
//     responses: {
//       model: "gpt-5",
//       input: [
//         {
//           role: "user",
//           content: [{ type: "input_text", text: userMessage }],
//         },
//       ],
//       reasoning: { effort: "high" },
//       text: { verbosity: "high" },
//       max_output_tokens: 16500,
//     },
//   };
// };

export const createDetailedContentForOutlineItemV2GPT5 = (
  programText,
  programName,
  unit,
  outcome,
  outcomeList,
  fullOutline,
  outlineItem,
  outcomeId,
) => {
  /* ── 1 · Sanitise --------------------------------------------------- */
  const safeProgText = (programText || "").trim() || "No programme data";
  const safeProgName = (programName || "").trim() || "Unnamed Programme";

  const safeUnitName = (unit?.name || "").trim() || "Unnamed Unit";
  const unitSeq = Number.isFinite(unit?.sequence)
    ? unit.sequence
    : (console.warn("Missing unit.sequence, defaulting to 0"), 0);

  const safeOutcome = (outcome?.name || "").trim() || "Unnamed Outcome";
  const globalSeq = Number.isFinite(outcome?.globalSequence)
    ? outcome.globalSequence
    : "?";

  const safeList = (outcomeList || "• No outcomes").trim();

  const sectionTitle = outlineItem?.title?.trim() || "Untitled Section";
  const sectionPoints = (outlineItem?.subPoints || [])
    .map(
      (sp) =>
        `• ${sp.heading?.trim() || "No heading"}: ${sp.notes?.trim() || ""}`,
    )
    .join("\n");

  const safeOutcomeId = (outcomeId || "").trim() || "UNKNOWN_OUTCOME_ID";

  /* stringify full outline once for readability */
  const outlinePretty = JSON.stringify(fullOutline, null, 2);

  /* ── 2 · User prompt (same structure, simpler language rules added) -- */
  const userMessage = `
You are an expert educator producing academic-quality, world-class learning content that is extremely practical, but **easy to follow for learners at different levels**.

Write in **clear, simple, direct** language. The goal is that:
• a strong student feels challenged and interested, and
• a struggling student can still understand and keep up.

The program structure you are working in:
|- Program: multiple months of learning. Program is built on multiple modules.
|-- Module: roughly one month of full-time tuition. Module is built on multiple components.
|--- Component: roughly one week of full-time tuition. Component is built on multiple units.
|---- Unit: roughly one day of full-time tuition. A component is built on multiple outcomes.
|----- Learning Outcome: a single key concept or skill to be mastered in that day

A Single Learning Outcome is further broken down into an outline of sections and subpoints (outline items).
You will develop the complete learning content for **one outline item only**, as specified below. 

────────────────────────────────────────
PROGRAMME CONTEXT
────────────────────────────────────────
Programme: "${safeProgName}"

${safeProgText}

────────────────────────────────────────
FOCUS FOR THIS TASK WILL BE ON A SINGLE OUTLINE ITEM INSIDE AN OUTCOME
────────────────────────────────────────
• Unit ${unitSeq}: "${safeUnitName}"
  Outcomes in this unit (teaching order):
  ${safeList}

• The specific outcome that you will be focusing on is:
  (globalSequence ${globalSeq}):
  "${safeOutcome}"

────────────────────────────────────────
COMPLETE OUTCOME OUTLINE
────────────────────────────────────────
${outlinePretty}

────────────────────────────────────────
OUTLINE ITEM TO DEVELOP - *write content for this only*
────────────────────────────────────────
Outline Item: "${sectionTitle}"

Sub-points
${sectionPoints}

────────────────────────────────────────
LANGUAGE & COMPLEXITY RULES
────────────────────────────────────────
• Audience: mixed-ability cohort, including learners who are new to the topic.
• Target: reading level similar to upper high school (around age 15–17).
• Use **short, direct sentences**. Avoid long, academic sentences.
• Use **common words** wherever possible. Only use technical terms when needed.
• When you introduce a technical term:
  – First explain it in **plain language**.
  – Then give the precise or formal definition if needed.
• Avoid heavy jargon, Latin phrases, “management speak”, or legalistic language.
• Break content into **short paragraphs** and use lists to make scanning easier.
• For difficult ideas, use phrases like “In simple terms…” or “Put differently…” and then restate the idea more simply.
• The writing should feel **friendly, supportive, and confidence-building**, not intimidating.

────────────────────────────────────────
CONTENT REQUIREMENTS
────────────────────────────────────────
You are required to develop the completed, detailed, and professionally written learning content for the specified outline item.
The learning content must be teachable, practical, and clearly structured.
The content must be cohesive and flow logically; even though you must develop content solely for this item, consider the entire outline so the explanation integrates seamlessly with previous and subsequent topics.

Instructions:
1. Write **only** the content for *this* outline section (ignore siblings). You may add more sub-points if the current sub-points do not sufficiently cover the topic.
2. Follow best academic writing, but expressed in **simple, accessible language**:
   • clear, precise, skill-centred
   • no unnecessary complexity
3. Be thorough and complete. Make sure that all concepts and understanding are fully covered, but expressed in small, understandable steps.

3.1  **MUST start with one “hero” banner image** (photo-realistic).  
    • style **must be** "full-screen-dark" **or** "full-screen-light".  
    • "description" **must equal** the section title (“${sectionTitle}”).  
    • "prompt" – 40-100-word, vivid, *photo-realistic* guidance and
      **explicitly state** “dark theme” or “light theme” to match the chosen style.  
    • Scene prompt instructions: Set a real world scene that visually represents the section topic. Describe the scene and the characters and emotions in the scene in detail. Describe the lighting, mood, and colour palette. Ensure it is a real world scenario with real world characters. 

4. Keep close attention to the tuition sequence and flow of the overall program and where the current outline item fits in the overall program.
    • You can refer back to concepts from previous outline items, but do not repeat them in full.
    • If you refer back to a foundational concept, add a short “Quick Refresher” in simple language.
    • You may add “Quick Preview” parts if the current concept is important for later topics, explaining why it matters in plain, concrete terms.

4. ALLOWED HTML (no <header>, <section>, <article>, <footer>):
   • Headings: <h1-h5>
   • Paragraph: <p>
   • Lists: <ul>, <ol>, <li>
   • Code: <code>…</code>
   • Tables: <table>, <thead>, <tbody>, <tr>, <th>, <td>
   • Inline: <strong>, <em>, <span>

   • Custom tags recognised by our front-end
     ─────────────────────────────────────────
     <statement>      Highlight a key fact or definition.
     <separator>      Visual divider between major segments.
     <paragraphcard>  Emphasised paragraph shown in a “card”.
     <executable> … </executable>
                     JavaScript that actually runs in-browser. Python code will not work.

  • Code snippets (code examples)
     ─────────────
       – JavaScript that should run in-browser ⇒ wrap in:
         <executable> …valid JS with comments… </executable>

       – **Any other language** (Python, Bash, SQL, etc.) ⇒ wrap in:
         <code> …non-JS code… </code>

  • Placeholders (<insert: …>)
     ─────────────
       Use when a *non-HTML* visual would add value (graph / diagram / screenshot, etc.):

       <insert: [brief explanation of the graph / diagram / complex figure]>
       <insert: [brief explanation of the screenshot]>

       ▸ Keep the explanation short but specific (what the learner will see).

  • Rich images (<image> … </image>).
     ────────────
       Use the exact JSON shape below:
       <image>{"outcomeId":"${safeOutcomeId}","fileName":"image_[SEQUENCE].jpg","description":"[DESCRIPTION]","prompt":"[PROMPT]","style":"[STYLE]","paragraph":"[PARAGRAPH]"}</image>

       (all field rules stay the same as in your existing spec)

Notes to Remember:
- Do NOT generate content for other outline items, and do NOT include assessments or test questions.
- Ensure that the content is cohesive and flows logically with the overall learning outcome.
- Use code examples or <executable> tags (or <code> for non-JS languages) only if the topic is programming or technical in nature.
- Return **only** the final written learning content (in plain HTML using the tags above). **No** extra commentary, markdown, or disclaimers.
- Remember to use <p>, <statement>, <paragraphcard>, or <separator> where helpful. Keep the writing **skill-oriented and easy to understand**.
- No content for other outline items, no assessments or test questions.
- Return **only** the final HTML – no markdown, comments, or explanations.
`.trim();

  /* ── 3 · GPT-5 (Responses API) request body ------------------------ */
  return {
    endpoint: "responses",
    responses: {
      model: "gpt-5",
      input: [
        {
          role: "user",
          content: [{ type: "input_text", text: userMessage }],
        },
      ],
      reasoning: { effort: "high" },
      text: { verbosity: "high" },
      max_output_tokens: 16500,
    },
  };
};

// A) CONTENT-FIRST (minimal HTML allowed)
// A) CONTENT-FIRST (minimal HTML allowed) — ENHANCED INSTRUCTIONS
// -------------------------------------------------------------
/**
 * Create rich learning content for ONE outline item.
 * Focus on content quality first; minimal HTML is allowed:
 *   - You MAY use <ul>/<ol>/<li> and <table>/<thead>/<tbody>/<tr>/<th>/<td>
 *   - Where an image would add value, insert: <image description="…"/>
 *   - No other HTML tags and no Markdown.
 * Output: plain text with optional minimal HTML and <image description="…"/>.
 */
export const createRawContentForOutlineItemV1GPT5Safe = (
  programText,
  programName,
  unit,
  outcome,
  outcomeList,
  fullOutline,
  outlineItem,
) => {
  // — sanitize inputs (same pattern as your other creators)
  const safeProgText = (programText || "").trim() || "No programme data";
  const safeProgName = (programName || "").trim() || "Unnamed Programme";

  const safeUnitName = (unit?.name || "").trim() || "Unnamed Unit";
  const unitSeq = Number.isFinite(unit?.sequence)
    ? unit.sequence
    : (console.warn("Missing unit.sequence, defaulting to 0"), 0);

  const safeOutcome = (outcome?.name || "").trim() || "Unnamed Outcome";
  const globalSeq = Number.isFinite(outcome?.globalSequence)
    ? outcome.globalSequence
    : "?";

  const safeList = (outcomeList || "• No outcomes").trim();

  const sectionTitle = outlineItem?.title?.trim() || "Untitled Section";
  const sectionPoints = (outlineItem?.subPoints || [])
    .map(
      (sp) =>
        `• ${sp.heading?.trim() || "No heading"}: ${sp.notes?.trim() || ""}`,
    )
    .join("\n");

  const outlinePretty = JSON.stringify(fullOutline, null, 2);

  // — prompt (content-first; minimal HTML permission)
  const userMessage = `
You are an expert educator producing academically rigorous, practical learning material that flows within a clearly defined programme.

Your task: **Write the complete instructional content for ONE outline item only**. Focus on clarity, correctness, completeness, and practical skills transfer. The content must be cohesive in the broader outcome/unit context, but do not write content for sibling outline items.

────────────────────────────────────────
PROGRAMME CONTEXT
────────────────────────────────────────
Programme: "${safeProgName}"

${safeProgText}

────────────────────────────────────────
FOCUS FOR THIS TASK WILL BE ON A SINGLE OUTLINE ITEM INSIDE AN OUTCOME
────────────────────────────────────────
• Unit ${unitSeq}: "${safeUnitName}"
  Outcomes in this unit (teaching order):
  ${safeList}

• Target outcome (globalSequence ${globalSeq}):
  "${safeOutcome}"

────────────────────────────────────────
COMPLETE OUTCOME OUTLINE (for context only)
────────────────────────────────────────
${outlinePretty}

────────────────────────────────────────
OUTLINE ITEM TO DEVELOP — *write content for this only*
────────────────────────────────────────
Outline Item: "${sectionTitle}"

Sub-points
${sectionPoints}

────────────────────────────────────────
INSTRUCTIONS (WRITE WORLD-CLASS LEARNING MATERIAL)
────────────────────────────────────────
1) Learner-first, world-class quality
   • Imagine the learner’s full journey through this topic. Make it engaging, motivating, and humane.
   • Write to inspire curiosity and passion for the subject while remaining precise and academically sound.
   • Balance for different paces: delight fast/advanced learners with depth and insight, while supporting struggling learners with scaffolding and gentle on-ramps.

2) Thorough conceptual coverage
   • Ensure the learner achieves a **firm understanding** by the end.
   • Define key terms clearly, connect ideas logically, and build understanding step-by-step.
   • Call out **common misconceptions** and correct them.
   • When prior knowledge is assumed, include a brief refresher (integrated inline, not as a separate lesson).

3) Practicality and transfer
   • If the topic is practical, include **MANY concrete, real-world examples** and micro-walkthroughs.
   • Show typical use cases, edge cases, and “what good looks like”.
   • Where helpful, include brief pseudo-steps or bullet checklists of actions/considerations.

4) Narrative devices that teach
   • Use **analogies** and **short stories** where appropriate to make abstract ideas concrete.
   • Keep analogies faithful (do not distort the concept); close the loop by mapping analogy parts back to real terms.

5) Pacing & structure for comprehension
   • Sequence ideas from foundation → application → nuance.
   • Insert short “why this matters” moments to sustain motivation.
   • Offer brief recaps at natural breaks to consolidate learning.

6) Minimal HTML for clarity (optional)
   • You MAY use these tags **only** where they improve readability or structure:
     – Headings: <h1>, <h2>, <h3>, <h4>, <h5>
     - Paragraphs: <p>
     – Lists: <ul>, <ol>, <li>
     – Tables: <table>, <thead>, <tbody>, <tr>, <th>, <td>
   • Do **not** use any other HTML tags here.

7) Image placeholders (optional, only if valuable)
   • Where a visual would meaningfully aid understanding, insert:
     <image description="[very short description of what the image should depict]"/>
     – Keep description ≤ 15 words.
     – Do not include any other image metadata. (A later step will convert these to rich LMS images.)

8) Style & scope constraints
   • **No** assessments/test questions in this step.
   • **No** Markdown, **no** code fences, and **no** HTML beyond the minimal tags above.
   • Do **not** wander into sibling outline items; stay scoped to **"${sectionTitle}"**.

9) Output format
   • Return the content directly as plain text with optional minimal HTML (lists/tables) and optional <image/> placeholders—nothing else.
`.trim();

  return {
    endpoint: "responses",
    responses: {
      model: "gpt-5",
      input: [
        {
          role: "user",
          content: [{ type: "input_text", text: userMessage }],
        },
      ],
      reasoning: { effort: "high" },
      text: { verbosity: "high" },
      max_output_tokens: 16500,
    },
  };
};

// PROMPT 2 — GPT-5 variant
/**
 * Build Prompt #2 – “Write full learning content for ONE outline item”
 * GPT-5 (Responses API) request body
 */
export const createDetailedContentForOutlineItemV2GPT5safe = (
  programText,
  programName,
  unit,
  outcome,
  outcomeList,
  fullOutline,
  outlineItem,
  outcomeId,
) => {
  /* ── 1 · Sanitise --------------------------------------------------- */
  const safeProgText = (programText || "").trim() || "No programme data";
  const safeProgName = (programName || "").trim() || "Unnamed Programme";

  const safeUnitName = (unit?.name || "").trim() || "Unnamed Unit";
  const unitSeq = Number.isFinite(unit?.sequence)
    ? unit.sequence
    : (console.warn("Missing unit.sequence, defaulting to 0"), 0);

  const safeOutcome = (outcome?.name || "").trim() || "Unnamed Outcome";
  const globalSeq = Number.isFinite(outcome?.globalSequence)
    ? outcome.globalSequence
    : "?";

  const safeList = (outcomeList || "• No outcomes").trim();

  const sectionTitle = outlineItem?.title?.trim() || "Untitled Section";
  const sectionPoints = (outlineItem?.subPoints || [])
    .map(
      (sp) =>
        `• ${sp.heading?.trim() || "No heading"}: ${sp.notes?.trim() || ""}`,
    )
    .join("\n");

  const safeOutcomeId = (outcomeId || "").trim() || "UNKNOWN_OUTCOME_ID";

  /* stringify full outline once for readability */
  const outlinePretty = JSON.stringify(fullOutline, null, 2);

  /* ── 2 · User prompt (unchanged) ----------------------------------- */
  const userMessage = `
  You are an expert educator producing academic-quality, world-class learning content that is extremely practical and focused on real-world skills transfer.
  You have the primary purpose of producing rigorous, practice-oriented learning material that flows naturally within the larger curriculum.
  The program structure that you are working on is well defined, and you will develop content within this structure.
  |- Program: multiple months of learning. Program is built on multiple modules.
  |-- Module: roughly one month of full-time tuition. Module is built on multiple components.
  |--- Component: roughly one week of full-time tuition. Component is built on multiple units.
  |---- Unit: roughly one day of full-time tuition. A component is built on multiple outcomes.
  |----- Learning Outcome: a single key concept or skill to be mastered in that day

  A Single Learning Outcome is further broken down into an outline of sections and subpoints (or also referred to as outline items). You will develop the complete learning content for a single outline item as specified below. 

────────────────────────────────────────
PROGRAMME CONTEXT
────────────────────────────────────────
Programme: "${safeProgName}"

${safeProgText}

────────────────────────────────────────
FOCUS FOR THIS TASK WILL BE ON A SINGLE OUTLINE ITEM INSIDE AN OUTCOME
────────────────────────────────────────
• Unit ${unitSeq}: "${safeUnitName}"
  Outcomes in this unit (teaching order):
  ${safeList}

• The specific outcome that you will be focusing on is:
  (globalSequence ${globalSeq}):
  "${safeOutcome}"

────────────────────────────────────────
COMPLETE OUTCOME OUTLINE
────────────────────────────────────────
${outlinePretty}

────────────────────────────────────────
OUTLINE ITEM TO DEVELOP - *write content for this only*
────────────────────────────────────────
Outline Item: "${sectionTitle}"

Sub-points
${sectionPoints}

────────────────────────────────────────
CONTENT REQUIREMENTS
────────────────────────────────────────
You are required to develop the completed, detailed, and professionally written learning content for the specified outline item. The learning content must be teachable and practical. The content must be cohesive and flow logically; even though you must develop content solely for this item, consider the entire outline so the explanation integrates seamlessly with previous and subsequent topics.
Instructions:
1. Write **only** the content for *this* outline section (ignore siblings). You can add more sub-points if the current sub-points do not sufficiently cover the topic.
2. Follow best academic writing: clear, precise, skill-centred.
2.1. Be thorough and complete. Make sure that all concepts and understanding are completely covered. 
3. Follow best academic writing principles: be clear, precise, thorough, and skill-oriented.
  3.1  **MUST start with one “hero” banner image** (photo-realistic).  
    • style **must be** "full-screen-dark" **or** "full-screen-light".  
    • "description" **must equal** the section title (“${sectionTitle}”).  
    • "prompt" – 40-100-word, vivid, *photo-realistic* guidance and
      **explicitly state** “dark theme” or “light theme” to match the chosen style.  
    • Scene prompt instructions: Set a real world scene that visually represents the section topic. Describe the scene and the characters and emotions in the scene in detail. Describe the lighting, mood, and colour palette. Ensure it to be real world scenarios with real world characters. 
4. Keep close attention to the tuition sequence and flow of the overall program and where the current outline item fits in the overall program. The sequence of the content must build logically and have a rational tuition sequence flow to it.
    • You can refer back to concepts from previous outline items, but do not repeat them.
    • If you are refering back to a concept from a previous outline item that is a foundational or important concept to understand the current outline item, then you can briefly explain it again in the context of the current outline item. In this case add a Quick Refresher part within your content to explain the concept again in the context of the content being covered in the current outline item.
    • You are allowed to make reference to where the learner is in the wider program, module, component, and unit if it is appropriate to do so within the context of the outline item.
    • If you are covering a concept that is important to understand for a future part of the program, module, component, or unit, then you can briefly explain it in the context of the current outline item. In this case add a Quick Preview part within your content to explain the concept in the context of the content being covered in the current outline item. This must have a natural flow to it and add to the learning experience.
    • If the concept you are covering now is considered a foundational concept or critical part to understand for a future part of the program, module, component, or unit, then you can outline directly in practical terms why the current concept is critical to fully understand and how it will impact the future parts of the program. 
4. ALLOWED HTML (no <header>, <section>, <article>, <footer>):
   • Headings: <h1-h5>
   • Paragraph: <p>
   • Lists: <ul>, <ol>, <li>
   • Code: <code>…</code>
   • Tables: <table>, <thead>, <tbody>, <tr>, <th>, <td>
   • Inline: <strong>, <em>, <span>

   • Custom tags recognised by our front-end
     ─────────────────────────────────────────
     <statement>      Highlight a key fact or definition.
     <separator>      Visual divider between major segments.
     <paragraphcard>  Emphasised paragraph shown in a “card”.
     <executable> … </executable>
                     JavaScript that actually runs in-browser. Python code will not work.

  • Code snippets (code examples)
     ─────────────
       – JavaScript that should run in-browser ⇒ wrap in:
         <executable> …valid JS with comments… </executable> -> try and always write JavaScript that is executable as far as possible.

       – **Any other language** (Python, Bash, SQL, etc.) ⇒ wrap in:
         <code> …non-JS code… </code>

  • Placeholders (<insert: …>)
     ─────────────
       Use when a *non-HTML* visual would add value (examples: graph / diagram / complex figure]):

       <insert: [brief explanation of the graph / diagram / complex figure]>
       <insert: [brief explanation of the screenshot]>

       ▸ Keep the explanation short but specific (what the learner will see).

  • Rich images (<image> … </image>).
     ────────────
       The <image> tag is used to include images that enhance the learning content in both style and in context. Each <image> tag must contain a JSON string specifying the image details, formatted as follows:
       <image>{"outcomeId":"${safeOutcomeId}","fileName":"image_[SEQUENCE].jpg","description":"[DESCRIPTION]","style":"[STYLE]","paragraph":"[PARAGRAPH]"}</image>

       **Fields**:
       - **outcomeId**: The unique identifier for the outcome, provided as "${safeOutcomeId}".
       - **fileName**: A unique file name for the image, e.g., "image_1.jpg", "image_2.png". Use ".jpg" or ".png" based on context.
       - **description**: *Very short* (≤ 15 words) caption that appears on-screen or below the image.  
       - **prompt**: *Detailed*, photorealistic text-to-image prompt (40–100 words). This is sent to the diffusion model; it should mention lighting, mood, colour palette, camera angle, etc. **Do not reuse description here.**
       - **style**: One of the following, chosen based on context:
         - "default": Centered image with a caption below (use for general illustrations).
         - "full-screen": Full-width image without overlay (use for large visuals).
         - "full-screen-dark": Full-width image with dark overlay caption (use for banners or headers).
         - "full-screen-light": Full-width image with light overlay caption (use for brighter visuals).
         - "square": 400x400px image with caption (use for compact diagrams).
         - "dark-overlay": Image with dark overlay caption (use for emphasized visuals).
         - "light-overlay": Image with light overlay caption (use for softer visuals).
         - "image-left": Image on the left with text on the right (use for side-by-side content).
         - "image-right": Image on the right with text on the left (use for side-by-side content).
       - **paragraph**: Text to accompany the image for "image-left" or "image-right" styles; use "" for other styles.

       **Usage**:
       - Use <image> tags when an image enhances understanding (e.g., diagrams, charts, or illustrative visuals).
       - You can use multiple <image> tags in a single section, but ensure they are relevant to the content.
       - You are encouraged to use no academic but still content relevant image tags for section titles or large visuals.
       - Ensure the JSON string is valid and properly escaped within the <image> tag.
       - Choose the "style" based on the content’s context (e.g., use "full-screen-dark" for section headers, "image-left" for explanatory text with visuals).
       - The "description" should be clear and specific to aid accessibility and provide context.
       - **Always** supply both "description" **and** "prompt".  
         • description is learner-facing alt-text / overlay (concise).  
         • prompt is artist-facing – long, vivid, guiding the AI to produce a photorealistic result that matches the lesson’s theme.

       **Examples**:
             <image>{
         "outcomeId":"${safeOutcomeId}",
         "fileName":"image_1.jpg",
         "description":"Diagram of a network topology",
         "prompt":"Ultra-realistic 3-D network topology visual, soft rim-light, dark background, neon cable paths, depth-of-field",
         "style":"default",
         "paragraph":""
       }</image>

       <image>{
         "outcomeId":"${safeOutcomeId}",
         "fileName":"hero_${sectionTitle
           .replace(/\\s+/g, "_")
           .toLowerCase()}.jpg",
         "description":"${sectionTitle}",
         "prompt":"Photo-realistic modern software studio, diverse developers clustered around a huge display presenting the <${sectionTitle}> headline, rich depth-of-field, dark theme, cinematic contrast, blue accent LEDs",
         "style":"full-screen-dark",
         "paragraph":""
       }</image>

       <image>{
         "outcomeId":"${safeOutcomeId}",
         "fileName":"packet_header.png",
         "description":"Structure of a TCP packet header",
         "prompt":"Clean flat-design infographic of TCP packet header layers, minimalistic palette, clear labels, 16:9 ratio, white background",
         "style":"image-right",
         "paragraph":"This diagram illustrates the structure of a TCP packet header."
       }</image>

   Anything not in this whitelist must **not** be used.
   Please note that images can be used to add both academic or visual value to the learning content. Examples of just adding visual content is a large section title overlay with some image behind it. You can also use realistic looking images as illustrations that fit with the theme of the content. (e.g. a team of software developers drinking coffee and talking infront of a screen).

Notes to Remember:
- Do NOT generate content for other outline items, and do NOT include assessments or test questions. Focus solely on instructional content for this one outline item.
- Ensure that the content is cohesive and flows logically with the overall learning outcome.
- Use code examples or <executable> tags (or <code> for non-JS languages) only if the topic is programming or technical in nature.
- Return **only** the final written learning content (in plain HTML using the tags above). **No** extra commentary, markdown, or disclaimers.
- Remember to use <p>, <statement>, <paragraphcard>, or <separator> if the text naturally fits those custom stylings or if a visual break is beneficial. Keep the writing skill-oriented and academically sound.
- No content for other outline items, no assessments or test questions.
- Return **only** the final HTML – no markdown, comments, or explanations.
`.trim();

  /* ── 3 · GPT-5 (Responses API) request body ------------------------ */
  return {
    endpoint: "responses",
    responses: {
      model: "gpt-5",
      input: [
        {
          role: "user",
          content: [{ type: "input_text", text: userMessage }],
        },
      ],
      // Keep reasoning light; we mainly need high-quality text output
      reasoning: { effort: "high" },
      // You asked for "verbose": the correct key is `verbosity`
      text: { verbosity: "high" },
      max_output_tokens: 16500,
      // temperature: 0.2, // optional
    },
  };
};
// B) STRUCTURE CONVERTER (from raw content → full HTML structure)
// -------------------------------------------------------------
/**
 * Convert previously generated content for ONE outline item into your
 * strict structured HTML format (allowed tags + rich <image>{ JSON }</image>).
 *
 * @param rawContent  The output string from createRawContentForOutlineItemV1GPT5
 * @returns Responses API payload (GPT-5)
 */
// =============================================================================
// Prompt C2 — Convert raw content into strict LMS HTML
// =============================================================================
export const createStructuredHtmlFromContentV1GPT5 = (
  programText,
  programName,
  unit,
  outcome,
  outcomeList,
  fullOutline,
  outlineItem,
  outcomeId,
  rawContent,
) => {
  const safeProgText = (programText || "").trim() || "No programme data";
  const safeProgName = (programName || "").trim() || "Unnamed Programme";

  const safeUnitName = (unit?.name || "").trim() || "Unnamed Unit";
  const unitSeq = Number.isFinite(unit?.sequence)
    ? unit.sequence
    : (console.warn("Missing unit.sequence, defaulting to 0"), 0);

  const safeOutcome = (outcome?.name || "").trim() || "Unnamed Outcome";
  const globalSeq = Number.isFinite(outcome?.globalSequence)
    ? outcome.globalSequence
    : "?";

  const safeList = (outcomeList || "• No outcomes").trim();

  const sectionTitle = outlineItem?.title?.trim() || "Untitled Section";
  const outlinePretty = JSON.stringify(fullOutline, null, 2);
  const safeOutcomeId = (outcomeId || "").trim() || "UNKNOWN_OUTCOME_ID";

  const userMessage = `
You will convert the provided content-only draft for a single outline item into the required structured HTML that our LMS expects.

The draft may contain minimal HTML, plain text headings, lists, tables, and <image description="..."/> placeholders.
Use the draft as the authoritative source for concepts, scope, and examples.
Preserve meaning faithfully, but improve teaching flow, readability, and structure.
Do not merely wrap every point in allowed tags. Your job is to produce a lesson page that reads smoothly and teaches clearly.

Do not add unrelated concepts or formal assessment items.

────────────────────────────────────────
PROGRAMME CONTEXT (for continuity only)
────────────────────────────────────────
Programme: "${safeProgName}"
Unit ${unitSeq}: "${safeUnitName}"
Outcome (globalSequence ${globalSeq}): "${safeOutcome}"
Outcomes in this unit (teaching order):
${safeList}

COMPLETE OUTCOME OUTLINE (contextual reference)
${outlinePretty}

────────────────────────────────────────
OUTLINE ITEM (the one to structure)
────────────────────────────────────────
"${sectionTitle}"

────────────────────────────────────────
RAW CONTENT TO STRUCTURE (input)
────────────────────────────────────────
${rawContent}

────────────────────────────────────────
LANGUAGE & COMPLEXITY RULES
────────────────────────────────────────
• Keep the content suitable for mixed-ability learners.
• Do not make the text more complex or more academic than the draft.
• You may simplify sentences and wording to improve clarity and readability:
  - break very long sentences into shorter ones,
  - replace rare or overly technical words with simpler alternatives where possible,
  - briefly clarify technical terms in plain language when needed.
• Never remove important ideas, examples, warnings, or learner-support prompts.
• Do not introduce new advanced concepts that are not already present or clearly implied.

────────────────────────────────────────
SEQUENCE & CONTINUITY RULES
────────────────────────────────────────
Use the full outline to preserve this section’s role in the overall sequence.

Assume:
• earlier outline items have already been taught,
• this section is one step in a larger lesson flow,
• later outline items are still to come.

Therefore:
• preserve brief bridges to earlier material when useful,
• remove or compress full re-teaching of earlier sibling sections when it appears unnecessarily,
• do not turn this section into a self-sufficient mini-course,
• keep named frameworks, models, tools, and matrices consistent; do not rename them or create parallel labels.

────────────────────────────────────────
LESSON-FIRST CONVERSION RULES
────────────────────────────────────────
The final HTML should feel like a guided lesson page, not a cleaned-up note dump, reference sheet, or slide deck.

Use these priorities:
1. Narrative teaching first.
2. Structural elements only where they genuinely help.
3. Emphasis blocks selectively, not constantly.
4. Decorative title-treatment images where they support the programme look and feel.
5. Instructional visuals where they reduce or clarify teaching load.
6. Focus on this section’s unique teaching role, not the whole outcome.

────────────────────────────────────────
ANTI-FRAGMENTATION RULES
────────────────────────────────────────
• Paragraphs are the default teaching vehicle.
• Do not turn every important idea into its own isolated block.
• If several adjacent points express one coherent idea, merge them into normal prose under one sensible heading.
• If the raw content is overly list-heavy, convert non-essential lists into explanatory prose while preserving meaning.
• Keep lists for steps, criteria, short grouped examples, brief learner prompts, or compact end-of-section summaries.
• Reserve tables for dense comparisons, classifications, structured values, or data that are genuinely easier to read side by side.
• Do not preserve a list or table simply because one exists in the draft.
• Any list or table that remains should be introduced or interpreted with surrounding prose so it feels taught, not merely displayed.
• Avoid long runs of short isolated blocks that create a fragmented reading experience.
• If several consecutive support blocks would appear back-to-back, collapse related material into normal paragraphs and keep only the strongest emphasis block.
• Remove redundant openings, repeated slogans, and near-duplicate summary lines where they weaken the flow.

────────────────────────────────────────
ROLE-SPECIFIC PRESENTATION
────────────────────────────────────────
Let this section’s role shape the structure.

If the section is mainly concept or framework building:
• prioritise explanatory paragraphs,
• use one clear figure or summary device where helpful,
• avoid overloading the page with many separate support blocks.

If the section is mainly application or case analysis:
• keep conceptual recap brief,
• move quickly into worked reasoning, comparisons, cases, or judgement,
• use visuals such as matrices, flows, or comparison figures when they reduce text repetition.

If the section is mainly action, reflection, or implementation:
• keep theory brief,
• focus on practical steps, choices, templates, or guardrails,
• do not bloat the section with repeated conceptual restatement.

────────────────────────────────────────
HOW TO USE THE LEARNING BLOCKS
────────────────────────────────────────
Standardise helpful teaching blocks using the allowed HTML, but use them selectively.

Use ordinary paragraphs <p> for:
• most explanation,
• In Plain English,
• Worked Example,
• most Real-World Connection content,
• longer bridges and teaching passages,
• most comparisons and interpretations.

Use <paragraphcard> for short, high-value learner-support moments such as:
• Why This Matters,
• Common Mistake,
• Pro Tip,
• Mini Recap,
• Reflection Prompt,
• Before You Move On,
• a strong rule of thumb or caution.

Use <statement> only for very short items such as:
• Key Term,
• Remember This,
• a concise definition,
• one-sentence takeaway.
If it takes more than a sentence or two to explain, do not force it into <statement>.

Use <separator> only for major shifts in the lesson, not after every subsection.

Worked Example, Try It Yourself, Checkpoint, and Challenge Extension should usually be rendered with headings plus normal <p>, <ol>, <ul>, and <code> only where those formats genuinely help the learner.

These are embedded learning supports, not formal assessments. Do not provide answer keys.

Not every section needs all support block types.
Keep only the blocks that genuinely strengthen this section’s role.

────────────────────────────────────────
HEADINGS AND FLOW
────────────────────────────────────────
• Use a small number of meaningful headings.
• Avoid micro-headings that only label single-sentence points.
• Keep a clear teaching spine:
  orient → explain → illustrate → apply → consolidate
  when that suits the topic.
• If the raw draft includes a framework, tool, matrix, checklist, or table, ensure the reader first gets a short prose explanation of how to read or use it.
• If a title-treatment image already performs the job of a large title, do not immediately repeat the exact same title in a large heading below it.
• When a major internal section break is represented by a full-screen or overlay title image, follow it with orienting prose or the next content block rather than an unnecessary duplicate heading.
• If a visual already carries part of the explanation, do not restate every label or cell in full prose unless needed for interpretation.

────────────────────────────────────────
OUTPUT REQUIREMENTS (STRICT)
────────────────────────────────────────
Produce a cohesive, professional, HTML-only lesson for this single outline item using the exact whitelist below.

ALLOWED HTML (no <header>, <section>, <article>, <footer>):
• Headings: <h1-h5>
• Paragraph: <p>
• Lists: <ul>, <ol>, <li>
• Code: <code>…</code>
• Tables: <table>, <thead>, <tbody>, <tr>, <th>, <td>
• Inline: <strong>, <em>, <span>

Custom LMS tags:
────────────────────────────────────────
<statement>      Highlight a key fact, key term, or key takeaway.
<separator>      Visual divider between major segments.
<paragraphcard>  Emphasised learner-support paragraph.
<executable> … </executable>
                 JavaScript that actually runs in-browser (use sparingly and only when relevant to the learning objective).

────────────────────────────────────────
IMAGE HANDLING
────────────────────────────────────────
There are two image roles in this LMS page:

1) Required opening title-treatment image
At the very beginning of this section, include ONE opening full-screen title image block.
This opening image is required unless the raw content already clearly provides a suitable opening title image placeholder.

Rules for the opening title image:
• It serves as the decorative entry into the section and supports the premium look and feel of the programme.
• It must be photorealistic.
• It must be decorative and thematic, not instructional.
• It must NOT be a diagram, graph, chart, process flow, matrix, infographic, screenshot, interface, dashboard, labelled figure, whiteboard explanation, slide, poster, document layout, or any other educational visual.
• It must NOT contain readable text, letters, numerals, labels, captions, logos, branding, signage, watermarks, UI text, presentation text, sticky-note text, whiteboard writing, document text, or any other embedded typography.
• The visible title will be placed on top of the image by the LMS, so the image itself must stay text-free and visually clean.
• It should depict a realistic, photorealistic scene, setting, environment, building, landscape, professional activity, or human situation that captures the section theme in a creative and inviting way.
• It should feel premium, cinematic, modern, realistic, and visually strong.
• It should include clean negative space for title placement so the title overlay remains easy to read.
• Do NOT default to a generic meeting-room scene unless that is genuinely the strongest thematic choice for this section.
• Vary scene types across sections. Strong alternatives include architecture, city scenes, environments, workspaces, symbolic real-world settings, hands-on activity, travel-like movement, operations spaces, landscapes, or other thematically relevant photorealistic scenes.
• Avoid environments that naturally introduce readable signs, presentation screens, whiteboards, posters, or documents unless those elements remain absent, obscured, or fully unreadable.

Opening title image JSON requirements:
• style must be "full-screen-dark" or "full-screen-light"
• description must be exactly "${sectionTitle}"
• prompt must explicitly state "dark theme" or "light theme"
• prompt must explicitly state that there must be no readable text, letters, numerals, labels, logos, signage, interface text, watermarks, or embedded typography in the image
• prompt must explicitly request clean negative space for the overlaid section title
• if the raw content includes a detailed opening title-image placeholder, use that detailed placeholder as source material for the rich image prompt while still setting the JSON description field to "${sectionTitle}"

2) All other visuals
For any other <image description="..."/> placeholders, convert them into rich image blocks and favour instructional value once the lesson body begins.

Convert any placeholder into:
<image>{
  "outcomeId":"${safeOutcomeId}",
  "fileName":"descriptive_image_name.png",
  "description":"[Learner-facing caption or title text as appropriate. No artificial length limit for non-title images.]",
  "prompt":"[Use a rich, explicit, highly detailed visual prompt. Do not optimise for brevity. Include whatever detail is needed for accuracy and image quality, such as subject matter, setting, people, actions, props, composition, camera angle, lens feel, depth of field, lighting, mood, colour palette, environment, realism cues, spatial layout, prominence of elements, clean areas for title overlay if relevant, and elements that must be avoided.]",
  "style":"default" | "full-screen" | "full-screen-dark" | "full-screen-light" | "square" | "dark-overlay" | "light-overlay" | "image-left" | "image-right",
  "paragraph":"[text for side-by-side styles; otherwise \"\"]"
}</image>

How to write image prompts
• No artificial length limit applies to image descriptions or prompts.
• Use as much detail as needed to guide the image generator accurately.
• Be explicit about what the image should contain and what it must avoid.
• For opening title images, the prompt should describe a premium, photorealistic, decorative scene related to the section theme.
• For body images, choose the visual form that best teaches the concept.

Additional image guidance
• Preserve the intent of the original description.
• If the original description implies a diagram, matrix, illustration, graph, process visual, or decision visual, do not force photorealism.
• Prefer visuals that clarify structure, relationships, decisions, or processes once the lesson body begins.
• Decorative visuals are correct and desirable for section-opening and title-treatment use.
• Instructional visuals should dominate once the lesson body begins.
• If the section teaches a hierarchy, stack, matrix, framework, process loop, comparison, workflow, or decision rule and no useful instructional visual exists, add one.

Major internal title images
• For major internal section breaks or very large title moments inside the section, you MAY add an additional full-screen or overlay title-treatment image instead of a plain large heading.
• Use this only for major conceptual shifts, not ordinary subsections.
• These internal title-treatment images should also be photorealistic, decorative, text-free, and not instructional diagrams.
• Suitable styles for these internal title images include:
  - "full-screen-dark"
  - "full-screen-light"
  - "dark-overlay"
  - "light-overlay"

Side-by-side figures
• For figures that need explanation beside them, consider "image-left" or "image-right" with a useful paragraph.
• Use the paragraph to interpret the visual, not just repeat its caption.

────────────────────────────────────────
STRUCTURE & SCOPE
────────────────────────────────────────
• Focus exclusively on this outline item.
• Organise with sensible headings and <separator> where it improves pacing.
• Ensure major ideas are explained in prose, not just displayed as bullets, cards, or table cells.
• No formal assessments or test questions here.
• No Markdown, no code fences, no non-whitelisted tags.

────────────────────────────────────────
FINAL OUTPUT
────────────────────────────────────────
Return only the final HTML using the allowed tags. No extra commentary.
`.trim();

  return {
    endpoint: "responses",
    responses: {
      model: "gpt-5",
      input: [
        {
          role: "user",
          content: [{ type: "input_text", text: userMessage }],
        },
      ],
      reasoning: { effort: "high" },
      text: { verbosity: "high" },
      max_output_tokens: 16500,
    },
  };
};

// export const createStructuredHtmlFromContentV1GPT5 = (
//   programText,
//   programName,
//   unit,
//   outcome,
//   outcomeList,
//   fullOutline,
//   outlineItem,
//   outcomeId,
//   rawContent,
// ) => {
//   // — sanitize (same pattern)
//   const safeProgText = (programText || "").trim() || "No programme data";
//   const safeProgName = (programName || "").trim() || "Unnamed Programme";

//   const safeUnitName = (unit?.name || "").trim() || "Unnamed Unit";
//   const unitSeq = Number.isFinite(unit?.sequence)
//     ? unit.sequence
//     : (console.warn("Missing unit.sequence, defaulting to 0"), 0);

//   const safeOutcome = (outcome?.name || "").trim() || "Unnamed Outcome";
//   const globalSeq = Number.isFinite(outcome?.globalSequence)
//     ? outcome.globalSequence
//     : "?";

//   const safeList = (outcomeList || "• No outcomes").trim();

//   const sectionTitle = outlineItem?.title?.trim() || "Untitled Section";
//   const outlinePretty = JSON.stringify(fullOutline, null, 2);
//   const safeOutcomeId = (outcomeId || "").trim() || "UNKNOWN_OUTCOME_ID";

//   // — prompt (transform raw → structured HTML per your detailed rules)
//   const userMessage = `
// You will convert the provided *content-only* draft for a single outline item into the required **structured HTML** that our LMS expects.

// The draft content (below) may include minimal HTML lists/tables and <image description="…"/> placeholders.
// Preserve the intent and meaning faithfully; improve structure and clarity only.
// Do not add new concepts or assessments. Do not drop important details.

// ────────────────────────────────────────
// PROGRAMME CONTEXT (for continuity only)
// ────────────────────────────────────────
// Programme: "${safeProgName}"
// Unit ${unitSeq}: "${safeUnitName}"
// Outcome (globalSequence ${globalSeq}): "${safeOutcome}"
// Outcomes in this unit (teaching order):
// ${safeList}

// COMPLETE OUTCOME OUTLINE (contextual reference)
// ${outlinePretty}

// ────────────────────────────────────────
// OUTLINE ITEM (the one to structure)
// ────────────────────────────────────────
// "${sectionTitle}"

// ────────────────────────────────────────
// RAW CONTENT TO STRUCTURE (input)
// ────────────────────────────────────────
// ${rawContent}

// ────────────────────────────────────────
// LANGUAGE & COMPLEXITY RULES
// ────────────────────────────────────────
// • Keep the content suitable for **mixed-ability learners**.
// • Do **not** make the text more complex or more academic than the draft.
// • You **may** simplify sentences and wording to improve clarity and readability:
//   – Break very long sentences into two or more shorter sentences.
//   – Replace rare or very technical words with simpler alternatives where possible.
//   – When a technical term appears without explanation, briefly clarify it in plain language.
// • Never remove important ideas, examples, or warnings.
// • Do not introduce new advanced concepts that are not already present in the draft.

// ────────────────────────────────────────
// OUTPUT REQUIREMENTS (STRICT)
// ────────────────────────────────────────
// Produce a cohesive, professional, **HTML-only** lesson for this *single* outline item using the exact whitelist below.
// Use the draft content above as the sole source; restructure (and lightly simplify wording) for clarity, flow, and teachability.

// ALLOWED HTML (no <header>, <section>, <article>, <footer>):
// • Headings: <h1-h5>
// • Paragraph: <p>
// • Lists: <ul>, <ol>, <li>
// • Code: <code>…</code>
// • Tables: <table>, <thead>, <tbody>, <tr>, <th>, <td>
// • Inline: <strong>, <em>, <span>

// Custom LMS tags:
// ─────────────────────────────────────────
// <statement>      Highlight a key fact or definition.
// <separator>      Visual divider between major segments.
// <paragraphcard>  Emphasised paragraph shown in a “card”.
// <executable> … </executable>
//                  JavaScript that actually runs in-browser (use sparingly).

// IMAGE HANDLING
// ─────────────────────────────────────────
// 1) Convert any <image description="…"/> placeholders into our rich image blocks:
//    <image>{
//      "outcomeId":"${safeOutcomeId}",
//      "fileName":"descriptive_image_name.jpg",
//      "description":"[≤15 words learner-facing caption]",
//      "prompt":"[40–100 word photorealistic prompt with scene, lighting, mood, palette, and camera]",
//      "style":"default" | "full-screen" | "full-screen-dark" | "full-screen-light" | "square" | "dark-overlay" | "light-overlay" | "image-left" | "image-right",
//      "paragraph":"[text for side-by-side styles; otherwise \"\"]
//    }</image>
//    • Preserve the intent of the original description.
//    • Choose style contextually; "full-screen-dark" works well for hero banners.
//    • Keep description concise; make the prompt vivid and photorealistic.

// 2) If the content clearly needs a hero banner at the start, add ONE:
//    style must be "full-screen-dark" **or** "full-screen-light"
//    description must equal the section title (“${sectionTitle}”)
//    prompt must explicitly state “dark theme” or “light theme”.

// STRUCTURE & SCOPE
// ─────────────────────────────────────────
// • Focus exclusively on this outline item.
// • Organise with sensible headings, <separator> where it aids pacing, and <paragraphcard>/<statement> for emphasis.
// • Keep any previously minimal HTML tables/lists, but convert them to proper HTML per the whitelist above.
// • No assessments or test questions here.
// • No Markdown, no code fences, no non-whitelisted tags.

// FINAL OUTPUT
// ─────────────────────────────────────────
// Return **only** the final HTML using the allowed tags. No extra commentary.
// `.trim();

//   return {
//     endpoint: "responses",
//     responses: {
//       model: "gpt-5",
//       input: [
//         {
//           role: "user",
//           content: [{ type: "input_text", text: userMessage }],
//         },
//       ],
//       reasoning: { effort: "high" },
//       text: { verbosity: "high" },
//       max_output_tokens: 16500,
//     },
//   };
// };

// C) ENRICHMENT (structured HTML → improved structured HTML)
// -------------------------------------------------------------
/**
 * Review and enrich previously structured HTML content for ONE outline item.
 * The model should:
 *  - Check academic completeness and coherence within the programme context.
 *  - Fill gaps (missing bridge explanations, common misconceptions, edge cases,
 *    practical examples, quick refreshers/previews) while staying within scope.
 *  - Improve teachability, flow, and pacing (use <separator>, <statement>,
 *    <paragraphcard> judiciously).
 *  - Preserve and/or add images using our strict <image>{…}</image> schema.
 *  - Keep to the allowed LMS tags; no assessments.
 *
 * @param programText    String with programme tree/context
 * @param programName    Programme name
 * @param unit           { name, sequence }
 * @param outcome        { name, globalSequence }
 * @param outcomeList    Bullet string of all outcomes in the unit
 * @param fullOutline    Full outline object (for context)
 * @param outlineItem    The specific section object from fullOutline.outline
 * @param outcomeId      Canonical outcome id (for image blocks)
 * @param structuredHtml The HTML produced by createStructuredHtmlFromContentV1GPT5
 * @returns              GPT-5 Responses API payload
 */

// =============================================================================
// Prompt C3 — Enrich structured HTML for pedagogy and completeness
// =============================================================================
export const createEnrichedStructuredHtmlForOutlineItemV1GPT5 = (
  programText,
  programName,
  unit,
  outcome,
  outcomeList,
  fullOutline,
  outlineItem,
  outcomeId,
  structuredHtml,
) => {
  const safeProgText = (programText || "").trim() || "No programme data";
  const safeProgName = (programName || "").trim() || "Unnamed Programme";

  const safeUnitName = (unit?.name || "").trim() || "Unnamed Unit";
  const unitSeq = Number.isFinite(unit?.sequence)
    ? unit.sequence
    : (console.warn("Missing unit.sequence, defaulting to 0"), 0);

  const safeOutcome = (outcome?.name || "").trim() || "Unnamed Outcome";
  const globalSeq = Number.isFinite(outcome?.globalSequence)
    ? outcome.globalSequence
    : "?";

  const safeList = (outcomeList || "• No outcomes").trim();
  const safeOutcomeId = (outcomeId || "").trim() || "UNKNOWN_OUTCOME_ID";

  const sectionTitle = outlineItem?.title?.trim() || "Untitled Section";
  const outlinePretty = JSON.stringify(fullOutline, null, 2);

  const baseHtml =
    typeof structuredHtml === "string" && structuredHtml.trim()
      ? structuredHtml.trim()
      : "<p>(no content)</p>";

  const userMessage = `
You are a senior learning designer reviewing and enriching an already structured HTML lesson for a single outline item.
Your job is to turn it into a strong guided lesson for a first-time learner while keeping scope, correctness, and language level under control.

Important:
• Do not make the content more academic, denser, or harder to read than the input.
• Stay within this outline item only.
• Preserve what is already strong, but strengthen places where the lesson feels thin, abrupt, too fragmented, too reference-like, too repetitive, or too long for the role this section should play.

This is not only an “add more” pass.
You may add, merge, shorten, trim, compress, remove, or restyle material when that produces a better lesson.

────────────────────────────────────────
PROGRAMME CONTEXT
────────────────────────────────────────
Programme: "${safeProgName}"
${safeProgText}

Unit ${unitSeq}: "${safeUnitName}"
Target outcome (globalSequence ${globalSeq}): "${safeOutcome}"

Outcomes in this unit (teaching order):
${safeList}

COMPLETE OUTCOME OUTLINE (context reference)
${outlinePretty}

────────────────────────────────────────
OUTLINE ITEM (to enrich)
────────────────────────────────────────
"${sectionTitle}"

────────────────────────────────────────
CURRENT STRUCTURED HTML (input)
────────────────────────────────────────
${baseHtml}

────────────────────────────────────────
LANGUAGE & COMPLEXITY RULES
────────────────────────────────────────
• Audience: mixed-ability learners, including students who are new to the topic.
• Keep the language at most as complex as the input; never make it more academic or dense.
• Aim for clear, direct, reader-friendly prose.
• You may:
  - split long or complex sentences into shorter ones,
  - replace rare or highly technical words with simpler alternatives when precision is not lost,
  - briefly explain technical terms in plain language when they appear.
• Do not remove important ideas, warnings, examples, or practice opportunities.
• Do not add advanced side topics or heavy theory that is not already present or clearly implied.

────────────────────────────────────────
SEQUENCE & ROLE AWARENESS
────────────────────────────────────────
Use the full outline to infer this section’s role in the larger learning sequence.

Assume:
• earlier outline items have already been taught,
• this section should contribute its own step in the sequence,
• later outline items are still to come.

Therefore:
• compress unnecessary re-teaching of earlier sibling sections,
• keep only the brief reminder needed for continuity,
• do not pad this section into a full standalone lesson if that causes repetition,
• preserve the exact names of frameworks, models, tools, and matrices already established or specified in the outline notes.

Not every section needs:
• a full conceptual reset,
• a full recap,
• multiple cards,
• many examples,
• a reflection prompt,
• an action plan,
• and a conclusion.

Keep what serves this section’s role. Remove what does not.

────────────────────────────────────────
COMMON PROBLEMS TO FIX
────────────────────────────────────────
Look for and correct these failure patterns when they appear:
• slide-deck feel,
• reference-manual feel,
• long runs of short cards or isolated blocks,
• concepts named but not fully explained,
• too many lists or tables doing the teaching,
• weak transitions between ideas,
• example lists without enough unpacking,
• section trying to do too many jobs at once,
• duplicated points or repeated slogans,
• framework renamed or reintroduced inconsistently,
• missing or weak title-treatment image at the opening,
• title-treatment image that is instructional rather than decorative,
• title-treatment image that includes readable words or text-like elements,
• body imagery that is decorative when the learner really needs an instructional figure.

────────────────────────────────────────
ENRICHMENT PRIORITIES
────────────────────────────────────────
1) Restore continuous teaching
• Each major subsection should read like a real explanation, not a stack of labels.
• If a key idea appears only as a short statement, bullet, card, or table entry, add the missing prose.
• No major concept should feel like an unexplained note.

2) Keep the section loyal to its role
• If this section is mainly conceptual, keep the focus on understanding and one or two strong examples.
• If it is mainly application-focused, keep recap brief and move into cases, judgements, or worked reasoning.
• If it is mainly reflective or action-oriented, keep theory brief and focus on decisions, implementation, templates, or next steps.
• Do not let the section drift into doing the jobs of its sibling outline items.

3) Rebalance length and density
• If the content feels over-complete for this section’s role, shorten it by removing repetition, merging similar points, and compressing low-value extras.
• Aim for the shortest version that still teaches the section well.
• Prefer one strong worked example over several similar mini-examples.
• If several case studies teach the same pattern, keep the most informative ones and compress the rest.

4) Rebalance structure
• If the HTML still feels too card-heavy, list-heavy, or table-heavy, convert non-essential structural elements into prose.
• Merge consecutive short blocks that belong to the same concept.
• Keep lists for steps, checks, categories, or concise end summaries.
• Keep tables only when side-by-side structure genuinely helps.
• Surround remaining lists and tables with brief explanatory prose when needed.
• Remove duplication or near-duplication when it weakens the flow.

5) Deepen the explanation
• Expand compressed concepts so the learner can follow not just what the idea is, but how it works and why it matters.
• Use plain-language bridge sentences and interpretive commentary.
• If a framework, tool, matrix, or checklist appears, ensure the lesson explains how to read or use it before relying on it.

6) Add concrete teaching value
• Every important concept should have at least one concrete example, use case, scenario, comparison, or walkthrough where helpful.
• Prefer a smaller number of richer, more developed examples over long inventories of thin examples.
• If the current section already has enough examples, do not add more just for coverage.

7) Surface confusion points
• Add plain-language clarifications, common mistakes, edge cases, or decision cues where learners are likely to struggle.
• Use friendly caution, not alarmist language.

8) Use learning blocks selectively
• Helpful blocks include:
  - Why This Matters
  - In Plain English
  - Key Term
  - Worked Example
  - Common Mistake
  - Pro Tip
  - Try It Yourself
  - Checkpoint
  - Real-World Connection
  - Remember This
  - Mini Recap
  - Reflection Prompt
  - Challenge Extension
  - Before You Move On
• These blocks should support the lesson, not dominate it.
• A few strong blocks are better than many small ones.
• Keep only the blocks that genuinely improve this section.
• Checkpoint, Try It Yourself, Reflection Prompt, Challenge Extension, and Before You Move On are allowed as non-graded learner prompts with no answers.

9) Visuals
• Preserve and strengthen the opening full-screen title-treatment image. If it is missing, add it.
• The opening hero is allowed and expected to be decorative and atmospheric because it serves as the visual entry into the section.
• The opening hero must be photorealistic and theme-led, not a process diagram, graph, chart, matrix, labelled figure, infographic, dashboard, screenshot, or educational visual.
• If the current opening hero contains any readable words, letters, numbers, captions, labels, logos, branding, signage, interface text, slide text, document text, whiteboard writing, sticky-note text, or watermark-like elements, replace or rewrite it so it becomes a clean decorative title image with no embedded typography.
• If the current opening hero is too literal, too instructional, too busy for title overlay, or too generic, refine it.
• Avoid defaulting to “people in a meeting room” unless that is genuinely the strongest thematic scene.
• Prefer more creative and varied photorealistic title images when they fit the topic better, including landscapes, architecture, work environments, symbolic real-world scenes, movement, locations, or other visually expressive subject matter.
• The opening hero should feel premium, cinematic, inviting, and modern.
• It should include clean negative space so the overlaid title remains visually strong and readable.
• For major internal chapter-like transitions, you may add or refine additional full-screen or overlay title-treatment images when they clearly improve the look and feel.
• Use these internal title-treatment images sparingly and only for major section breaks, not ordinary subsections.
• Once the lesson body begins, favour instructional visuals over decorative ones.
• Replace or refine generic or mismatched visuals when a diagram, process view, decision flow, matrix, comparison graphic, graph, timeline, labelled structure, annotated example, or real-world scene would make the concept clearer.
• When a figure can carry some explanatory load, let it do so and shorten duplicated prose around it.

────────────────────────────────────────
HOW TO USE THE AVAILABLE LMS TAGS
────────────────────────────────────────
• Most teaching should happen in normal paragraphs and headings.
• <statement> = concise definition, key fact, key term, or takeaway
• <paragraphcard> = short supportive teaching block, caution, motivation, simplification, or practical note
• <separator> = major transition in the learning flow
• Do not overuse cards or statements.
• If several consecutive cards or statements are carrying the main explanation, convert some of them into normal prose.
• Use headings and normal paragraphs for the main teaching body.
• If a full-screen title-treatment image already performs the job of a large title, remove immediate duplicate large headings when they are redundant.

────────────────────────────────────────
ALLOWED HTML (strict whitelist)
────────────────────────────────────────
• Headings: <h1-h5>
• Paragraph: <p>
• Lists: <ul>, <ol>, <li>
• Code: <code>…</code>
• Tables: <table>, <thead>, <tbody>, <tr>, <th>, <td>
• Inline: <strong>, <em>, <span>

Custom LMS tags:
<statement>      Highlight a key fact or definition.
<separator>      Visual divider between major segments.
<paragraphcard>  Emphasised paragraph shown in a card.
<executable> … </executable>   // Only for runnable JavaScript; use sparingly

────────────────────────────────────────
IMAGE BLOCKS (strict schema)
────────────────────────────────────────
Insert images as:
<image>{
  "outcomeId":"${safeOutcomeId}",
  "fileName":"image_[SEQUENCE].png",
  "description":"[Learner-facing caption or title text as appropriate. No artificial length limit.]",
  "prompt":"[Use a rich, explicit, highly detailed visual prompt. Do not optimise for brevity. Include whatever detail is needed for accuracy and image quality, such as subject matter, setting, people, actions, props, composition, camera angle, lens feel, depth of field, lighting, mood, colour palette, environment, realism cues, spatial layout, prominence of elements, clean areas for title overlay if relevant, and elements that must be avoided.]",
  "style":"default" | "full-screen" | "full-screen-dark" | "full-screen-light" | "square" | "dark-overlay" | "light-overlay" | "image-left" | "image-right",
  "paragraph":"[text for side-by-side styles; otherwise \"\"]"
}</image>

Opening title-treatment image requirements
• The section should begin with one opening title-treatment image.
• It should use style "full-screen-dark" or "full-screen-light".
• Its "description" should be "${sectionTitle}" so the frontend can use it as the visible title treatment.
• Its prompt must explicitly state "dark theme" or "light theme".
• It must be photorealistic, decorative, and thematically aligned to the section.
• It must NOT be a graph, process flow, matrix, labelled diagram, chart, dashboard, UI, screenshot, infographic, or other educational visual.
• It must NOT contain readable text, letters, numbers, captions, labels, logos, branding, signage, interface text, slide text, document text, whiteboard writing, sticky-note text, or watermarks.
• It should depict a realistic, visually compelling scene, environment, location, building, activity, workspace, landscape, human moment, or symbolic real-world setting that captures the subject matter creatively.
• It should be composed with calm negative space for the overlaid title.
• It should feel premium, cinematic, modern, inviting, and photorealistic.
• Avoid defaulting to a generic boardroom or meeting-room composition unless the section theme genuinely calls for that.
• Prefer scene variety across sections.
• Avoid scene elements that naturally introduce readable signage, visible presentation screens, whiteboards, posters, or documents unless those elements remain absent, obscured, or completely unreadable.

Major internal title images
• For major internal section breaks, you may use "full-screen-dark", "full-screen-light", "dark-overlay", or "light-overlay" as title-treatment styles.
• These internal title-treatment images should also be photorealistic, decorative, text-free, and not instructional.
• Use them only when the break genuinely feels like a big title moment.

Instructional body visuals
• If the section teaches a stack, hierarchy, process loop, matrix, workflow, comparison, decision framework, or diagnostic tool, a teaching figure is often more valuable than another decorative image.
• Keep useful existing images intact unless a prompt is generic, decorative in the wrong place, or mismatched; in that case, refine it while keeping the same teaching intent.
• When helpful, use "image-left" or "image-right" so the visual and explanatory prose reinforce each other.

Prompt-writing expectations
• No artificial length limit applies to image descriptions or prompts.
• Use as much detail as needed for the image generator to produce an accurate result.
• Be explicit about both positive requirements and negative exclusions.
• For title-treatment images, explicitly forbid any readable text or embedded typography in the image itself.

────────────────────────────────────────
OUTPUT
────────────────────────────────────────
Return only the final enriched HTML using the allowed tags.
No Markdown, no comments, no code fences, no non-whitelisted tags, and no formal assessments.
`.trim();

  return {
    endpoint: "responses",
    responses: {
      model: "gpt-5",
      input: [
        {
          role: "user",
          content: [{ type: "input_text", text: userMessage }],
        },
      ],
      reasoning: { effort: "high" },
      text: { verbosity: "high" },
      max_output_tokens: 16500,
    },
  };
};
// export const createEnrichedStructuredHtmlForOutlineItemV1GPT5 = (
//   programText,
//   programName,
//   unit,
//   outcome,
//   outcomeList,
//   fullOutline,
//   outlineItem,
//   outcomeId,
//   structuredHtml,
// ) => {
//   // — sanitize inputs (same pattern as other creators)
//   const safeProgText = (programText || "").trim() || "No programme data";
//   const safeProgName = (programName || "").trim() || "Unnamed Programme";

//   const safeUnitName = (unit?.name || "").trim() || "Unnamed Unit";
//   const unitSeq = Number.isFinite(unit?.sequence)
//     ? unit.sequence
//     : (console.warn("Missing unit.sequence, defaulting to 0"), 0);

//   const safeOutcome = (outcome?.name || "").trim() || "Unnamed Outcome";
//   const globalSeq = Number.isFinite(outcome?.globalSequence)
//     ? outcome.globalSequence
//     : "?";

//   const safeList = (outcomeList || "• No outcomes").trim();
//   const safeOutcomeId = (outcomeId || "").trim() || "UNKNOWN_OUTCOME_ID";

//   const sectionTitle = outlineItem?.title?.trim() || "Untitled Section";
//   const outlinePretty = JSON.stringify(fullOutline, null, 2);

//   const baseHtml =
//     typeof structuredHtml === "string" && structuredHtml.trim()
//       ? structuredHtml.trim()
//       : "<p>(no content)</p>";

//   const userMessage = `
// You are a senior learning designer. Enrich the **already structured** HTML lesson for a single outline item so it is academically complete, highly teachable, and practical—without drifting beyond the intended scope.
// Important aspect is to keep to the exact same level of complexity as the input; do not make it more academic or dense.

// Your goals:
// • Ensure conceptual completeness and correct sequencing for a first-time learner.
// • Add bridge explanations and quick refreshers when a prerequisite concept is assumed.
// • Address common misconceptions and edge cases succinctly.
// • Add practical, real-world examples, mini walkthroughs, and lightweight guided steps.
// • Improve pacing and readability using allowed LMS tags judiciously.
// • Maintain coherence with the broader outcome and unit; do not introduce unrelated topics.
// • Preserve existing images; add new images only where they concretely improve understanding.

// ────────────────────────────────────────
// PROGRAMME CONTEXT
// ────────────────────────────────────────
// Programme: "${safeProgName}"
// ${safeProgText}

// Unit ${unitSeq}: "${safeUnitName}"
// Target outcome (globalSequence ${globalSeq}): "${safeOutcome}"

// Outcomes in this unit (teaching order):
// ${safeList}

// COMPLETE OUTCOME OUTLINE (context reference)
// ${outlinePretty}

// ────────────────────────────────────────
// OUTLINE ITEM (to enrich)
// ────────────────────────────────────────
// "${sectionTitle}"

// ────────────────────────────────────────
// CURRENT STRUCTURED HTML (INPUT)
// ────────────────────────────────────────
// ${baseHtml}

// ────────────────────────────────────────
// LANGUAGE & COMPLEXITY RULES
// ────────────────────────────────────────
// • Audience: mixed-ability learners, including students who are new to the topic.
// • Keep the language **at most** as complex as the input; **never** make it more academic or dense.
// • Aim for a reading level similar to **upper high school** (around age 15–17).
// • Use **short, direct sentences** and clear, everyday vocabulary.
// • You may:
//   – Split long or complex sentences into two or more simpler sentences.
//   – Replace rare or highly technical words with simpler alternatives, **unless** the precise term is required.
//   – Briefly explain technical terms in plain language when they appear.
// • Do not remove important ideas, warnings, or examples.
// • Do not add advanced side topics or heavy theory that is not already implied by the current content or outline.

// ────────────────────────────────────────
// ENRICHMENT GUIDELINES
// ────────────────────────────────────────
// 1) Stay within this outline item’s scope. Keep content rigorous, practical, and learner-centred.
// 2) Improve clarity and flow. Use:
//    • <statement> for key definitions/facts.
//    • <paragraphcard> for important takeaways or “teacher’s tip”.
//    • <separator> to mark logical transitions.
// 3) Provide missing glue where needed:
//    • Short “Quick Refresher” blocks for essential prior knowledge (in simple language).
//    • Short forward-looking notes ("Quick Preview") only when it improves motivation and orientation.
// 4) Practicality & examples:
//    • Add short, concrete examples or step-by-step walkthroughs where the learner would likely struggle.
//    • If appropriate, provide a tiny hands-on snippet or pseudo-process (use <code> for non-JS).
//    • Keep examples concise and easy to follow. No assessments or test questions here.
// 5) Tables/lists:
//    • Convert/retain as proper HTML using the allowed tags if they improve clarity.
// 6) Image policy:
//    • Keep existing <image> blocks intact.
//    • Add new images **only** where they meaningfully aid comprehension (diagram, conceptual visual, process overview, real-world context).
//    • Use the strict LMS schema below.

// ────────────────────────────────────────
// ALLOWED HTML (strict whitelist)
// ────────────────────────────────────────
// • Headings: <h1-h5>
// • Paragraph: <p>
// • Lists: <ul>, <ol>, <li>
// • Code: <code>…</code>
// • Tables: <table>, <thead>, <tbody>, <tr>, <th>, <td>
// • Inline: <strong>, <em>, <span>

// Custom LMS tags:
// <statement>      Highlight a key fact or definition.
// <separator>      Visual divider between major segments.
// <paragraphcard>  Emphasised paragraph shown in a “card”.
// <executable> … </executable>   // Only for runnable JavaScript (use sparingly)

// IMAGE BLOCKS (strict schema)
// ────────────────────────────────────────
// Insert images as:
// <image>{
//   "outcomeId":"${safeOutcomeId}",
//   "fileName":"image_[SEQUENCE].jpg",
//   "description":"[≤15 words learner-facing caption]",
//   "prompt":"[40–100 word photorealistic prompt with scene, lighting, mood, colour palette, and camera]",
//   "style":"default" | "full-screen" | "full-screen-dark" | "full-screen-light" | "square" | "dark-overlay" | "light-overlay" | "image-left" | "image-right",
//   "paragraph":"[text for side-by-side styles; otherwise \"\"]
// }</image>

// • If a clear hero banner is missing and appropriate, add ONE at the start:
//   - style: "full-screen-dark" **or** "full-screen-light"
//   - description: "${sectionTitle}"
//   - prompt explicitly states “dark theme” or “light theme”.

// ────────────────────────────────────────
// OUTPUT
// ────────────────────────────────────────
// Return **only** the final enriched HTML using the allowed tags.
// No Markdown, no comments, no code fences, no non-whitelisted tags, and no assessments.
// `.trim();

//   return {
//     endpoint: "responses",
//     responses: {
//       model: "gpt-5",
//       input: [
//         {
//           role: "user",
//           content: [{ type: "input_text", text: userMessage }],
//         },
//       ],
//       reasoning: { effort: "high" },
//       text: { verbosity: "high" },
//       max_output_tokens: 16500,
//     },
//   };
// };

/**
 * Generates Prompt #3 for enriching existing learning content with practical examples.
 *
 * @param {string} existingContent - The detailed learning content generated previously (from Prompt #2).
 *
 * @returns {object} - A requestBody object containing the prompt payload for the AI model.
 *
 * Instructions for the AI:
 * 1. Evaluate the provided learning content closely, including any practical examples already present.
 * 2. Determine where real-world, hands-on examples, demonstrations, code snippets, or practical simulations
 *    could add value. These may include:
 *    - Code examples (e.g., JavaScript, Python) wrapped in <code>...</code> tags.
 *    - Real-world scenarios, calculations, or simulations (e.g., technical chart analysis, step-by-step demonstrations).
 *    - Practical examples that are relevant to the subject matter.
 * 3. Only add additional practical examples if they clearly enhance the understanding of the topic.
 *    Do not add examples lightly.
 * 4. If no additional practical examples are necessary, return the original content unchanged.
 * 5. Return the entire enriched learning content (or the original content if no changes are made),
 *    with no extra commentary, markdown formatting, or meta text.
 *
 * Return only the final enriched learning content.
 */
export const createPracticalExamplesEnrichmentPrompt = async (
  existingContent,
) => {
  try {
    const userMessage = `
You are an expert educator and instructional designer. You have been provided with detailed, academically rigorous learning content below. Your task is to carefully evaluate this content to determine if real-world, hands-on examples or demonstrations (such as code snippets, calculations, simulations, or real-world scenarios) could further enhance the material.

Instructions:
1. Examine the existing content closely, including any practical examples already present.
2. Only add additional practical examples if they provide clear additional value and enhance understanding.
3. Practical examples may include:
   - Code examples (wrapped in <code>...</code> tags) in languages like JavaScript or Python.
   - Real-world scenarios or step-by-step demonstrations (e.g., technical chart analysis, environment setup steps).
   - Calculations or simulations relevant to the topic.
4. If the content already contains sufficient practical examples, do not add more.
5. Return the entire final learning content with your enhancements. If no enhancements are needed, simply return the original content.
6. Return only the final enriched learning content, with no extra commentary, markdown formatting, or meta text.

Existing Content:
${existingContent}

Return only the final enriched learning content.
    `;

    const requestBody = {
      model: "gpt-4.5-preview",
      // model: "gpt-4o-mini",
      // model: "gpt-o1-mini",
      messages: [
        {
          role: "user",
          content: userMessage,
        },
      ],
      max_tokens: 6500, // Adjust based on expected content length
      temperature: 0.3,
    };

    return requestBody;
  } catch (error) {
    console.error(
      "Error generating practical examples enrichment prompt:",
      error,
    );
    throw new Error("Unable to generate practical examples enrichment prompt.");
  }
};

/**
 * Creates a request payload for converting HTML content into a flat JSON structure using function-calling.
 * Preserves the original prompt content and adds function-calling for structured output.
 * @param {string} finalHtmlString - Raw HTML content to be flattened.
 * @returns {object} Request payload for the AI model with function-calling.
 * @throws {Error} If the input is invalid or payload generation fails.
 */
export const createFlatHTMLStructurePrompt = (finalHtmlString) => {
  try {
    // Validate input
    if (!finalHtmlString || typeof finalHtmlString !== "string") {
      throw new Error("Invalid or missing finalHtmlString");
    }

    // Prompt content, updated to explicitly include <image> and clarify JSON handling
    const userMessage = `
You are provided with HTML content that may include tags like <h1>, <h2>, <p>, <code>, <ul>, <li>, <ol>, <table>, <image>, <insert:...>, <strong>, <em>, <span>, and wrapper tags like <header>, <section>, <article>, or <footer>.

Your task:
1. Convert the HTML into a flat, sequential JSON array of elements, ignoring wrapper tags (<header>, <section>, <article>, <footer>).
2. Each element in the array must have:
   - "sequence": A strictly increasing integer (1, 2, 3, ...) in top-down reading order.
   - "tag": The HTML tag name (e.g., "h1", "p", "code", "ul", "table", "image", "insert").
   - "content": The inner text or HTML, preserving line breaks and sub-tags (e.g., <li> within <ul>, or <td> within <table>).
3. Special handling:
   - For <ul>, <ol>, or <table>, include all sub-tags (<li>, <tr>, <td>) in the "content" field as a single entry.
   - For <insert: description>, use "tag": "insert" and "content": "description".
   - For <image>, include the entire JSON string content (e.g., "{\"outcomeId\":\"abc123\",...}") in the "content" field, ensuring it is a valid JSON string.
   - Keep inline tags (<strong>, <em>, <span>) within the "content" field; do not split them into separate entries.
4. Use the provided function "output_flat_html_structure" to return the result as a JSON array of objects.

**HTML Content**:
-------------
${finalHtmlString}
-------------

Important:
- Return ONLY valid JSON – an array of objects. **Do not** wrap it in \`\`\`json fences. No extra text at all. Just the raw JSON array.
`;

    // Define the function schema for function-calling
    const functionSchema = {
      name: "output_flat_html_structure",
      description:
        "Returns a flat JSON array of HTML elements with sequence, tag, and content.",
      parameters: {
        type: "object",
        properties: {
          elements: {
            type: "array",
            items: {
              type: "object",
              properties: {
                sequence: {
                  type: "integer",
                  description: "Increasing sequence number",
                },
                tag: { type: "string", description: "HTML tag name" },
                content: {
                  type: "string",
                  description: "Inner text or HTML with sub-tags",
                },
              },
              required: ["sequence", "tag", "content"],
            },
          },
        },
        required: ["elements"],
      },
    };

    // Construct the request payload
    return {
      model: "gpt-5-mini",
      messages: [
        {
          role: "user",
          content: userMessage,
        },
      ],
      functions: [functionSchema],
      function_call: { name: "output_flat_html_structure" },
      max_completion_tokens: 16500,
    };
  } catch (error) {
    console.error(
      "Error generating flat HTML structure prompt:",
      error.message,
    );
    throw new Error(`Failed to generate prompt: ${error.message}`);
  }
};

/**
 * createAssessmentGradingPrompt
 * --------------------------------------------------
 * Build an o3-mini request that grades a learner’s response
 * and returns structured feedback using function-calling.
 *
 * @param {Object}  q        – one question object from Firestore
 * @param {string}  learner  – learner’s raw answer
 * @returns {object} payload – ready for axios/fetch to OpenAI / CF call
 *
 * The model must call `grade_learner_answer` and return:
 *   {
 *     score:      1 | 0.5 | 0,
 *     bucket:     "Great" | "Almost" | "Incorrect",
 *     feedback:   "One-sentence explanation",
 *     correctRef: "Optional brief ideal answer"     (echo back correctAnswer)
 *   }
 */
export function createAssessmentGradingPrompt(q, learner) {
  if (!q || !learner) throw new Error("Question & learner answer required");

  const { type, prompt, correctAnswer } = q;

  /* ---------- user-side message ---------- */
  const userMessage = `
You're an expert assessor.  
Grade the learner's answer against the reference solution.  
Use the rubric:

• Great —— fully correct → score 1  
• Almost — partially correct (≥70 % key points) → score 0.5  
• Incorrect — mostly or entirely wrong → score 0  

Return ONLY via the provided function.

--- question ---
${prompt}

--- reference (ideal) answer ---
${correctAnswer}

--- learner answer ---
${learner}
`;

  /* ---------- function schema ---------- */
  const functionSchema = {
    name: "grade_learner_answer",
    description:
      "Scores learner answer and gives concise feedback according to the rubric.",
    parameters: {
      type: "object",
      properties: {
        score: {
          type: "number",
          enum: [1, 0.5, 0],
          description: "Numerical score per rubric",
        },
        bucket: {
          type: "string",
          enum: ["Great", "Almost", "Incorrect"],
          description: "Text bucket matching the score",
        },
        feedback: {
          type: "string",
          description:
            "One-sentence explanation (max ~25 words) why it was graded so.",
        },
        correctRef: {
          type: "string",
          description:
            "Short ideal answer for learner reference; can echo correctAnswer.",
        },
      },
      required: ["score", "bucket", "feedback"],
    },
  };

  /* ---------- final payload ---------- */
  return {
    model: "o3-mini",
    messages: [{ role: "user", content: userMessage }],
    functions: [functionSchema],
    function_call: { name: "grade_learner_answer" },
    max_completion_tokens: 512,
  };
}

  /**
   * Generates Prompt #4 for creating assessment questions based on enriched learning content.
   * Uses function-calling with o3-mini model to ensure structured JSON output.
   *
   * @param {string} enrichedContent - The enriched learning content to base questions on.
   * @returns {Promise<object>} Request payload for the AI model with function-calling.
   * @throws {Error} If input is invalid or payload generation fails.
   */

  // =============================================================================
  // Prompt 4 — Assessment questions
  // =============================================================================
  export const createAssessmentQuestionsPrompt = ({
    programText,
    programName,
    unit,
    outcome,
    outcomeList,
    fullOutline,
    outlineItem,
    enrichedContent,
  }) => {
    try {
      const safeProgText = (programText || "").trim() || "No programme data";
      const safeProgName = (programName || "").trim() || "Unnamed Programme";

      const safeUnitName = (unit?.name || "").trim() || "Unnamed Unit";
      const unitSeq = Number.isFinite(unit?.sequence) ? unit.sequence : 0;

      const safeOutcome = (outcome?.name || "").trim() || "Unnamed Outcome";
      const globalSeq = Number.isFinite(outcome?.globalSequence)
        ? outcome.globalSequence
        : "?";

      const safeList = (outcomeList || "• No outcomes").trim();

      const sectionTitle = outlineItem?.title?.trim() || "Untitled Section";
      const outlinePretty = JSON.stringify(fullOutline, null, 2);

      console.log(
        "@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@",
      );
      console.log(outlinePretty);
      console.log(sectionTitle);
      console.log(safeList);
      console.log(globalSeq);
      console.log(safeOutcome);
      console.log(unitSeq);
      console.log(safeUnitName);
      console.log(safeProgName);
      console.log(safeProgText);

      if (!enrichedContent || typeof enrichedContent !== "string") {
        throw new Error("Invalid or missing enrichedContent");
      }

      const userMessage = `
  You are an expert educator designing assessment questions for a Learning Management System focused on practical, skill-oriented education.

  Your task is to create a strong question bank for the single outline item that has just been taught.

  ────────────────────────────────────────
  PROGRAMME CONTEXT
  ────────────────────────────────────────
  Programme: "${safeProgName}"

  ${safeProgText}

  ────────────────────────────────────────
  FOCUS FOR THIS ASSESSMENT (one outline item)
  ────────────────────────────────────────
  • Unit ${unitSeq}: "${safeUnitName}"
    Outcomes in teaching order:
    ${safeList}

  • Current outcome (globalSequence ${globalSeq}):
    "${safeOutcome}"

  ────────────────────────────────────────
  COMPLETE OUTCOME OUTLINE
  ────────────────────────────────────────
  ${outlinePretty}

  ────────────────────────────────────────
  OUTLINE ITEM JUST TAUGHT (create questions for this only)
  ────────────────────────────────────────
  "${sectionTitle}"

  ────────────────────────────────────────
  CONTENT TO ASSESS
  ────────────────────────────────────────
  ${enrichedContent}

  ────────────────────────────────────────
  IMPORTANT INPUT HANDLING
  ────────────────────────────────────────
  • The content may contain HTML tags, custom LMS tags, and image JSON blocks.
  • Treat all tags and image metadata as presentation only.
  • Assess the underlying concepts, distinctions, examples, frameworks, decisions, misconceptions, and practical applications.
  • If the content includes learning-support blocks such as Why This Matters, Checkpoint, Try It Yourself, Common Mistake, Remember This, Reflection Prompt, or Before You Move On, treat them as signals of important learning targets, but write fresh formal assessment questions rather than copying those prompts.

  ────────────────────────────────────────
  SEQUENCE AWARENESS
  ────────────────────────────────────────
  Use the full outline to understand this section’s role inside the wider outcome.

  Assume:
  • earlier outline items in the outcome may already have introduced some foundations,
  • this section contributes one specific step in the learning sequence,
  • brief reminders of earlier material may appear in the content.

  Therefore:
  • scope the questions to what this section uniquely teaches or applies,
  • do not generate many questions on brief recap material that mainly belongs to an earlier section,
  • preserve exact framework names and terminology if the section uses a named model, tool, or matrix,
  • do not invent a parallel label or renamed version of the framework.

  ────────────────────────────────────────
  QUESTION BANK DESIGN GOAL
  ────────────────────────────────────────
  Build a question set that tests real understanding of the section’s teaching role.
  Do not generate one shallow question for every tiny point or bullet.
  Consolidate related micro-points into stronger questions that test:
  • understanding of the main concepts,
  • ability to distinguish related ideas,
  • application to realistic scenarios,
  • recognition of common errors or misconceptions,
  • transfer of the idea to new cases,
  • practical judgement where relevant.

  If this section is mainly:
  • conceptual or framework-building — ask interpretation, distinction, explanation, and use questions.
  • application-focused — ask scenario, case, comparison, and judgement questions.
  • reflection/action-oriented — ask design, planning, guardrail, implementation, and decision questions.

  ────────────────────────────────────────
  YOUR TASK
  ────────────────────────────────────────
  1. Carefully analyse the content to identify the key concepts, skills, distinctions, examples, frameworks, decisions, misconceptions, and practical applications that students must master from this section.

  2. Generate between 20 and 50 questions.
    - Choose the number based on the breadth, density, and importance of the content.
    - Use the smallest number that still gives strong coverage.
    - Do not pad with trivial rewordings, near-duplicates, or shallow variants of the same idea.
    - Include at least 10 learner-runnable objective questions using "multiple-choice" or "true-false" so the LMS can assemble a complete automatically graded attempt.
    - The remaining questions may use the written or applied types below when they add genuine assessment value.

  3. Use an appropriate mix of question types based on the content.
    You may use:
    - "multiple-choice": 4 options with one correct answer. Use for recall, discrimination, quick checks, and scenario-based choices.
    - "true-false": exactly 2 options, "True" and "False", with the correct answer matching one of those options. Use only for a clear statement with one unambiguous truth value.
    - "short-answer": concise factual or explanatory responses, usually 1–2 sentences.
    - "long-answer": deeper explanation, reasoning, comparison, judgement, or application, usually 3 or more sentences.
    - "practical": task-based questions such as code, procedure, algorithm, workflow completion, output interpretation, or applied problem-solving. Use only when the content is technical, procedural, or practice-driven.
    - "dynamic": questions that ask the learner to use an AI tool and explain, evaluate, improve, or critique the result. Use only when AI-assisted work is genuinely relevant to the subject or workplace practice described in the content.

  4. Ensure the question set is:
    - comprehensive across the actual content of this outline item,
    - balanced across recall, explanation, application, comparison, error diagnosis, and judgement where relevant,
    - clear, concise, and unambiguous,
    - grounded in the content that was taught,
    - appropriate for the learner level implied by the content.

  5. Prefer stronger conceptual and scenario-based questions over many narrow fact checks.
    When the content teaches a framework, stack, matrix, decision tool, comparison, or process:
    - include questions that ask learners to interpret it,
    - apply it to a new example,
    - distinguish correct from incorrect use,
    - identify what changes under different conditions.

  6. For multiple-choice questions:
    - provide exactly 4 options,
    - make distractors plausible and non-overlapping,
    - avoid trick wording,
    - ensure only one option is clearly correct.
    For true-false questions:
    - provide exactly the 2 options "True" and "False",
    - set "correctAnswer" to exactly "True" or "False",
    - avoid partly true, context-dependent, or ambiguous statements.

  7. For each question, provide a "correctAnswer" that is precise, directly scorable, and aligned with the expected depth of response.
    - For practical questions, provide the expected solution, output, or model answer.
    - For long-answer questions, provide a compact but sufficient model answer.
    - For dynamic questions, provide a grounded model answer that explains what a strong student response should contain.

  8. Return only valid JSON via the function call using this exact shape:
  {
    "questions": [
      {
        "type": "multiple-choice | true-false | short-answer | long-answer | practical | dynamic",
        "question": "string",
        "options": ["string", "string", "string", "string"],
        "correctAnswer": "string"
      }
    ]
  }

  Important
  • Stay scoped to this outline item only.
  • Do not mention HTML tags, image prompts, file names, or LMS presentation details in the questions.
  • Do not include commentary, markdown, or any text outside the function call.
  `.trim();

      const functionSchema = {
        name: "output_assessment_questions",
        description:
          "Returns a JSON array of assessment questions with their types, questions, options, and correct answers.",
        parameters: {
          type: "object",
          properties: {
            questions: {
              type: "array",
              minItems: 20,
              maxItems: 50,
              items: {
                type: "object",
                properties: {
                  type: {
                    type: "string",
                    enum: [
                      "multiple-choice",
                      "true-false",
                      "short-answer",
                      "long-answer",
                      "practical",
                      "dynamic",
                    ],
                    description: "Type of the question",
                  },
                  question: {
                    type: "string",
                    description: "The question text",
                  },
                  options: {
                    type: "array",
                    items: { type: "string" },
                    description:
                      "Exactly 4 options for multiple-choice, or True and False for true-false",
                  },
                  correctAnswer: {
                    type: "string",
                    description: "The correct answer (text or code snippet)",
                  },
                },
                required: ["type", "question", "correctAnswer"],
              },
            },
          },
          required: ["questions"],
        },
      };

      const requestBody = {
        model: "gpt-5-mini",
        messages: [
          {
            role: "user",
            content: userMessage,
          },
        ],
        functions: [functionSchema],
        function_call: { name: "output_assessment_questions" },
        max_completion_tokens: 8500,
      };

      return requestBody;
    } catch (error) {
      console.error(
        "Error generating assessment questions prompt:",
        error.message,
      );
      throw new Error(
        `Failed to generate assessment questions prompt: ${error.message}`,
      );
    }
  };

// export const createAssessmentQuestionsPrompt = ({
//   programText,
//   programName,
//   unit,
//   outcome,
//   outcomeList,
//   fullOutline,
//   outlineItem,
//   enrichedContent,
// }) => {
//   try {
//     // Validate input
//     /* 1 ─────────── Sanitise inputs (same style as your content prompt) */
//     const safeProgText = (programText || "").trim() || "No programme data";
//     const safeProgName = (programName || "").trim() || "Unnamed Programme";

//     const safeUnitName = (unit?.name || "").trim() || "Unnamed Unit";
//     const unitSeq = Number.isFinite(unit?.sequence) ? unit.sequence : 0;

//     const safeOutcome = (outcome?.name || "").trim() || "Unnamed Outcome";
//     const globalSeq = Number.isFinite(outcome?.globalSequence)
//       ? outcome.globalSequence
//       : "?";

//     const safeList = (outcomeList || "• No outcomes").trim();

//     const sectionTitle = outlineItem?.title?.trim() || "Untitled Section";
//     const outlinePretty = JSON.stringify(fullOutline, null, 2);

//     console.log(
//       "@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@",
//     );
//     console.log(outlinePretty);
//     console.log(sectionTitle);
//     console.log(safeList);
//     console.log(globalSeq);
//     console.log(safeOutcome);
//     console.log(unitSeq);
//     console.log(safeUnitName);
//     console.log(safeProgName);
//     console.log(safeProgText);

//     if (!enrichedContent || typeof enrichedContent !== "string") {
//       throw new Error("Invalid or missing enrichedContent");
//     }

//     const userMessage = `
// You are an expert educator designing assessments for a Learning Management System (LMS) focused on practical, skill-oriented education. Your task is to create a comprehensive set of assessment questions based on the provided enriched learning content to thoroughly test a student’s understanding, supporting large groups of learners.
// ────────────────────────────────────────
// PROGRAMME CONTEXT
// ────────────────────────────────────────
// Programme: "${safeProgName}"

// ${safeProgText}

// ────────────────────────────────────────
// FOCUS FOR THIS ASSESSMENT (one outline item)
// ────────────────────────────────────────
// • Unit ${unitSeq}: "${safeUnitName}"
//   Outcomes in teaching order:
//   ${safeList}

// • Current outcome (globalSequence ${globalSeq}):
//   "${safeOutcome}"

// ────────────────────────────────────────
// COMPLETE OUTCOME OUTLINE
// ────────────────────────────────────────
// ${outlinePretty}

// ────────────────────────────────────────
// OUTLINE ITEM JUST TAUGHT (create questions for THIS only)
// ────────────────────────────────────────
// "${sectionTitle}"
// ────────────────────────────────────────
// !IMPORTANT: **CONTENT TO ASSESS**:
// ────────────────────────────────────────
// ${enrichedContent}
// ────────────────────────────────────────

// **YOUR TASK**:
// 1. Carefully analyze the content to identify all key concepts, skills, facts, and practical applications that students must master.
// 2. Generate exactly 20 questions to cover the entire content, ensuring sufficient depth and breadth. For each key topic, create multiple questions to test different aspects (e.g., comprehension, application, critical thinking). You must generate exactly 20 questions - no more, no less.
// 3. Use a balanced mix of question types based on the content:
//   - **Multiple-choice**: Provide 4 options, with one correct answer. Use for factual recall, concept differentiation, or quick checks.
//   - **Short-answer**: Require concise, factual responses (1-2 sentences). Use for definitions, quick explanations, or simple applications.
//   - **Long-answer**: Require detailed responses (3+ sentences). Use for core or critical concepts requiring in-depth analysis or explanation.
//   - **Practical**: Focus on coding or algorithmic tasks (e.g., "Write a code snippet," "Complete this algorithm," "What is the output of this code?"). Include code examples where relevant. IMPORTANT: Only use Practical questions if the content is technical or programming-focused. Practical questions should be practically relevant in terms of the content provided.
//   - **Dynamic**: Prompt students to use AI tools (e.g., "Use an AI tool to optimize this code and explain your result"). Test insight and modern problem-solving skills.
// 4. Ensure questions are:
//   - **Comprehensive**: Cover all key topics in the content, with multiple questions per topic to test different perspectives or skills.
//   - **Practical**: Relate directly to real-world applications or skills in the content. Only ask programing or coding questions if the content is technical or programming-focused. Practical questions should be practically relevant in terms of the content provided.
//   - **Clear and concise**: Avoid ambiguity in phrasing.
//   - **Balanced**: Include a mix of question types, with appropriate complexity for each topic.
//   - **Diverse**: Test comprehension, application, and critical thinking within each topic.
// 5. For each question, provide a **correctAnswer** that is precise and matches the expected response (e.g., a code snippet for practical questions, a detailed explanation for long-answer).
// 6. Use the provided function "output_assessment_questions" to return a JSON array of question objects with this structure:
//   - "type": "multiple-choice", "short-answer", "long-answer", "practical", or "dynamic"
//   - "question": The question text (string)
//   - "options": Array of 4 answer options (strings, for multiple-choice only)
//   - "correctAnswer": The correct answer (string, or code snippet for practical questions)
// 7. Return **ONLY valid JSON** via the function call. No extra text, commentary, or markdown.

// **Example Output**:
// [
//  {
//    "type": "multiple-choice",
//    "question": "What is the primary purpose of the event loop in JavaScript?",
//    "options": ["To handle UI events", "To manage asynchronous operations", "To compile code", "To store variables"],
//    "correctAnswer": "To manage asynchronous operations"
//  },
//  {
//    "type": "multiple-choice",
//    "question": "Which component of the event loop processes macrotasks?",
//    "options": ["Task queue", "Microtask queue", "Call stack", "Event emitter"],
//    "correctAnswer": "Task queue"
//  },
//  {
//    "type": "short-answer",
//    "question": "Define a closure in JavaScript in one sentence.",
//    "correctAnswer": "A closure is a function that retains access to variables from its outer scope even after the outer function has finished executing."
//  },
//  {
//    "type": "short-answer",
//    "question": "What is the scope chain in the context of a JavaScript closure?",
//    "correctAnswer": "The scope chain is the hierarchy of variable scopes that a closure can access, starting from its own scope and extending to outer scopes."
//  },
//  {
//    "type": "long-answer",
//    "question": "Explain how the call stack and event loop interact to handle asynchronous code in JavaScript.",
//    "correctAnswer": "The call stack executes synchronous code in a LIFO order, while the event loop manages asynchronous tasks by pushing them to the task queue once their conditions (e.g., timers) are met, then moving them to the call stack when it’s empty, ensuring non-blocking execution."
//  },
//  {
//    "type": "long-answer",
//    "question": "Describe the role of microtasks in the JavaScript event loop and their priority over macrotasks.",
//    "correctAnswer": "Microtasks, such as promises, are processed in a separate queue with higher priority than macrotasks (e.g., setTimeout). After each macrotask, the event loop clears the microtask queue before proceeding, ensuring microtasks execute sooner and can handle critical asynchronous operations."
//  },
//  {
//    "type": "practical",
//    "question": "Write a JavaScript function that reverses a string.",
//    "correctAnswer": "function reverseString(str) { return str.split('').reverse().join(''); }"
//  },
//  {
//    "type": "practical",
//    "question": "Given the code 'const arr = [1, 2, 3]; console.log(arr.reverse());', what is the output?",
//    "correctAnswer": "[3, 2, 1]"
//  },
//  {
//    "type": "dynamic",
//    "question": "Use an AI tool to generate a sorting algorithm in Python, then explain why it’s efficient.",
//    "correctAnswer": "An AI might generate a quicksort algorithm like this: def quicksort(arr): if len(arr) <= 1: return arr; pivot = arr[len(arr) // 2]; left = [x for x in arr if x < pivot]; middle = [x for x in arr if x == pivot]; right = [x for x in arr if x > pivot]; return quicksort(left) + middle + quicksort(right). It’s efficient because it has an average time complexity of O(n log n) due to its divide-and-conquer approach."
//  },
//  {
//    "type": "dynamic",
//    "question": "Use an AI tool to optimize the following JavaScript loop for performance: 'for (let i = 0; i < arr.length; i++) { arr[i] *= 2; }', then explain the improvement.",
//    "correctAnswer": "An AI might suggest using 'arr = arr.map(x => x * 2);'. This is more declarative and potentially optimized by JavaScript engines for bulk operations, improving readability and possibly performance on large arrays due to native optimizations."
//  }
// ]
//    `;

//     // Define the function schema for function-calling
//     const functionSchema = {
//       name: "output_assessment_questions",
//       description:
//         "Returns a JSON array of assessment questions with their types, questions, options, and correct answers.",
//       parameters: {
//         type: "object",
//         properties: {
//           questions: {
//             type: "array",
//             items: {
//               type: "object",
//               properties: {
//                 type: {
//                   type: "string",
//                   enum: [
//                     "multiple-choice",
//                     "short-answer",
//                     "long-answer",
//                     "practical",
//                     "dynamic",
//                   ],
//                   description: "Type of the question",
//                 },
//                 question: {
//                   type: "string",
//                   description: "The question text",
//                 },
//                 options: {
//                   type: "array",
//                   items: { type: "string" },
//                   description:
//                     "Array of 4 answer options (for multiple-choice only)",
//                 },
//                 correctAnswer: {
//                   type: "string",
//                   description: "The correct answer (text or code snippet)",
//                 },
//               },
//               required: ["type", "question", "correctAnswer"],
//             },
//           },
//         },
//         required: ["questions"],
//       },
//     };

//     const requestBody = {
//       model: "gpt-5-mini",
//       messages: [
//         {
//           role: "user",
//           content: userMessage,
//         },
//       ],
//       functions: [functionSchema],
//       function_call: { name: "output_assessment_questions" },
//       max_completion_tokens: 8500, // Increased for comprehensive question set
//     };

//     return requestBody;
//   } catch (error) {
//     console.error(
//       "Error generating assessment questions prompt:",
//       error.message,
//     );
//     throw new Error(
//       `Failed to generate assessment questions prompt: ${error.message}`,
//     );
//   }
// };

// createAssessmentQuestionsPayloadGPT5.js

/**
 * GPT-5 (Responses API) payload for creating assessment questions.
 * Prompt content remains the same; output is a strict JSON array.
 *
 * Call your CF with this object as-is (no { data: … } wrapper).
 * Parse with: const out = JSON.parse(res.data.output_text)
 */
export const createAssessmentQuestionsPayloadGPT5 = ({
  programText,
  programName,
  unit,
  outcome,
  outcomeList,
  fullOutline,
  outlineItem,
  enrichedContent,
}) => {
  // ── Sanitise (same style as other creators)
  const safeProgText = (programText || "").trim() || "No programme data";
  const safeProgName = (programName || "").trim() || "Unnamed Programme";

  const safeUnitName = (unit?.name || "").trim() || "Unnamed Unit";
  const unitSeq = Number.isFinite(unit?.sequence) ? unit.sequence : 0;

  const safeOutcome = (outcome?.name || "").trim() || "Unnamed Outcome";
  const globalSeq = Number.isFinite(outcome?.globalSequence)
    ? outcome.globalSequence
    : "?";

  const safeList = (outcomeList || "• No outcomes").trim();

  const sectionTitle = outlineItem?.title?.trim() || "Untitled Section";
  const outlinePretty = JSON.stringify(fullOutline, null, 2);

  if (!enrichedContent || typeof enrichedContent !== "string") {
    throw new Error("Invalid or missing enrichedContent");
  }

  // ── Prompt (unchanged, but with JSON-only output instruction)
  const userMessage = `
You are an expert educator designing assessments for a Learning Management System (LMS) focused on practical, skill-oriented education. Your task is to create a comprehensive set of assessment questions based on the provided enriched learning content to thoroughly test a student’s understanding, supporting large groups of learners.
────────────────────────────────────────
PROGRAMME CONTEXT
────────────────────────────────────────
Programme: "${safeProgName}"

${safeProgText}

────────────────────────────────────────
FOCUS FOR THIS ASSESSMENT (one outline item)
────────────────────────────────────────
• Unit ${unitSeq}: "${safeUnitName}"
  Outcomes in teaching order:
  ${safeList}

• Current outcome (globalSequence ${globalSeq}):
  "${safeOutcome}"

────────────────────────────────────────
COMPLETE OUTCOME OUTLINE
────────────────────────────────────────
${outlinePretty}

────────────────────────────────────────
OUTLINE ITEM JUST TAUGHT (create questions for THIS only)
────────────────────────────────────────
"${sectionTitle}"
────────────────────────────────────────
!IMPORTANT: **CONTENT TO ASSESS**:
────────────────────────────────────────
${enrichedContent}
────────────────────────────────────────


**YOUR TASK**:
1. Carefully analyze the content to identify all key concepts, skills, facts, and practical applications that students must master.
2. Generate exactly 20 questions to cover the entire content, ensuring sufficient depth and breadth. For each key topic, create multiple questions to test different aspects (e.g., comprehension, application, critical thinking). You must generate exactly 20 questions - no more, no less.
3. Use a balanced mix of question types based on the content:
  - **Multiple-choice**: Provide 4 options, with one correct answer. Use for factual recall, concept differentiation, or quick checks.
  - **Short-answer**: Require concise, factual responses (1-2 sentences). Use for definitions, quick explanations, or simple applications.
  - **Long-answer**: Require detailed responses (3+ sentences). Use for core or critical concepts requiring in-depth analysis or explanation.
  - **Practical**: Focus on coding or algorithmic tasks (e.g., "Write a code snippet," "Complete this algorithm," "What is the output of this code?"). Include code examples where relevant. IMPORTANT: Only use Practical questions if the content is technical or programming-focused. Practical questions should be practically relevant in terms of the content provided.
  - **Dynamic**: Prompt students to use AI tools (e.g., "Use an AI tool to optimize this code and explain your result"). Test insight and modern problem-solving skills.
4. Ensure questions are:
  - **Comprehensive**: Cover all key topics in the content, with multiple questions per topic to test different perspectives or skills.
  - **Practical**: Relate directly to real-world applications or skills in the content. Only ask programming or coding questions if the content is technical or programming-focused. Practical questions should be practically relevant in terms of the content provided.
  - **Clear and concise**: Avoid ambiguity in phrasing.
  - **Balanced**: Include a mix of question types, with appropriate complexity for each topic.
  - **Diverse**: Test comprehension, application, and critical thinking within each topic.
5. For each question, provide a **correctAnswer** that is precise and matches the expected response (e.g., a code snippet for practical questions, a detailed explanation for long-answer).
6. **Output format (STRICT)**: Return **ONLY** a valid JSON **array** of question objects (no wrapper object). Do not include any explanatory text or Markdown.
   Each question object MUST have:
     - "type": "multiple-choice" | "short-answer" | "long-answer" | "practical" | "dynamic"
     - "question": string
     - "correctAnswer": string
     - For "multiple-choice" only, include:
         "options": [string, string, string, string]  // exactly 4 options

**Example Output**:
[
 {
   "type": "multiple-choice",
   "question": "What is the primary purpose of the event loop in JavaScript?",
   "options": ["To handle UI events", "To manage asynchronous operations", "To compile code", "To store variables"],
   "correctAnswer": "To manage asynchronous operations"
 },
 {
   "type": "short-answer",
   "question": "Define a closure in JavaScript in one sentence.",
   "correctAnswer": "A closure is a function that retains access to variables from its outer scope even after the outer function has finished executing."
 }
]
`.trim();

  // ── GPT-5 Responses payload (high settings)
  return {
    endpoint: "responses",
    responses: {
      model: "gpt-5",
      input: [
        {
          role: "user",
          content: [{ type: "input_text", text: userMessage }],
        },
      ],
      reasoning: { effort: "high" },
      text: { verbosity: "high" },
      max_output_tokens: 12000,
      // temperature: 0.2, // optional, can keep default
    },
  };
};

/**
 * Creates a prompt for editing/enhancing a snippet of LMS content.
 *
 * @param {object} context - Full context data.
 *   {
 *     programName: string,
 *     unitName: string,
 *     outcomeName: string,
 *     outcomeOutline: array of sections,
 *     snippetTag: string, // e.g. "p", "h1", etc.
 *     snippetContent: string, // Original text user wants to modify
 *     editingGoal: string, // e.g. "enhance", "summarize", "changeTone: Formal"
 *   }
 *
 * Example:
 * {
 *   programName: "Full Stack Developer",
 *   unitName: "Databases 101",
 *   outcomeName: "Understanding SQL",
 *   outcomeOutline: [
 *     { title: "Introduction to SQL", sectionId: "Introduction_to_SQL" },
 *     { title: "Basic Queries", sectionId: "Basic_Queries" },
 *     // ...
 *   ],
 *   snippetTag: "p",
 *   snippetContent: "<p>This is some sample text about SQL queries...</p>",
 *   editingGoal: "Enhance"
 * }
 *
 * Return:
 *   A request body object that you can pass to your AI cloud function (OpenAIapiCalls).
 */
export const createEditEnhancementPrompt = (context) => {
  const {
    unitName,
    outcomeName,
    allSections = [],
    currentSectionTitle,
    currentSectionContent,
    snippetTag,
    snippetContent,
  } = context;

  const sectionsText = allSections
    .map(
      (sec, index) =>
        `- Section ${index + 1}: "${sec.title}" (ID: ${sec.sectionId})`,
    )
    .join("\n");

  const sectionContentText = currentSectionContent?.structure
    ? currentSectionContent.structure
        .map(
          (item) =>
            `- Sequence ${item.sequence} (<${item.tag}>): "${item.content}"`,
        )
        .join("\n")
    : "No structured content available";

  const userMessage = `
You are an expert educator and writer tasked with reviewing and refining academic content designed to deliver practical skills through an online visual learning platform. Your role is to enhance educational material, making it clearer, more engaging, and highly effective for learners.

This content is created for a Learning Management System (LMS), where it is structured as HTML snippets stored within a JSON data object. The LMS uses these data objects to display content to learners in a sequenced, interactive format. For example, a data object for a section might look like this:

{
  "complexity": "base",
  "structure": [
    { "sequence": 1, "tag": "h2", "content": "Introduction to Version Control" },
    { "sequence": 2, "tag": "p", "content": "Version control systems track changes to code." }
  ]
}

Here, the "structure" array defines the order and type of content (e.g., headings, paragraphs), and each item’s "content" is rendered as HTML within its specified "tag" (e.g., <h2>, <p>).

The subject you are reviewing content for is called "${unitName}". This subject includes multiple learning outcomes, and the specific learning outcome you are currently editing content for is titled "${outcomeName}". This learning outcome is structured into the following sections:

${sectionsText}

You are working within the section titled "${currentSectionTitle}", which contains the following full content:

${sectionContentText}

This section includes a specific snippet that you must edit, identified by the tag "<${snippetTag}>". The current HTML content for this snippet is:

${snippetContent}

Your task is to enhance this snippet, meaning you must substantially reimagine the context and explanation to significantly improve clarity on the topic. Elevate the content by adding depth with precise terminology, structured reasoning, and additional detail, avoiding reusing exact phrases from the input unless essential. Include at least one detailed example (e.g., a tool like SVN with a specific use case) to illustrate the point, ensuring these additions integrate naturally without overshadowing the snippet’s core focus. There is no strict word limit—your priority is to convey the concept more clearly and effectively, making substantial improvements over the original.

- Ensure the enhanced snippet aligns with the subject "${unitName}" and the learning outcome "${outcomeName}".
- Output only the final HTML snippet, using the original "<${snippetTag}>" tag. Do not introduce new tag types (e.g., do not change a <p> to a <ul> or add nested tags like <ul> inside a <p>).
- For single-element tags (e.g., <p>, <h1>...<h5>, <statement>, <paragraphcard>, <code>), refine and expand the content within the "<${snippetTag}>" tag, making substantial improvements without altering its structure.
- For list tags (<ul>, <ol>), enhance the existing <li> items with deeper explanations or examples, adding or removing <li> items as needed to improve clarity, but keep the output as a single "<${snippetTag}>" with updated <li> content.
- For table tags (<table>), enrich the <tr> and <td> content with added detail or examples, adjusting rows or cells as needed for clarity, but keep the output as a single "<${snippetTag}>" with updated table structure.
- The LMS supports these HTML tags: <h1>...<h5>, <p>, <ul>, <li>, <ol>, <code>, <table>, <tr>, <td>, <statement>, <paragraphcard>, <separator>, <insert:...>, <strong>, <em>, <span>. Use only the provided "<${snippetTag}>" for your output tag type.

Return only the updated HTML snippet.
`;

  console.log("createEditEnhancementPrompt: Final user message:", userMessage);

  const requestBody = {
    model: "gpt-4o-mini",
    messages: [
      {
        role: "user",
        content: userMessage,
      },
    ],
    max_tokens: 2000,
    temperature: 0.7, // Increased for more creativity
  };

  return requestBody;
};

// In heroManagementDashboard/functions/AICalls.js
export const createEditSummaryPrompt = (context) => {
  const {
    unitName,
    outcomeName,
    allSections = [],
    currentSectionTitle,
    currentSectionContent,
    snippetTag,
    snippetContent,
  } = context;

  // Convert allSections to a short bullet list
  const sectionsText = allSections
    .map(
      (sec, index) =>
        `- Section ${index + 1}: "${sec.title}" (ID: ${sec.sectionId})`,
    )
    .join("\n");

  // Convert currentSectionContent.structure to a readable format for the prompt
  const sectionContentText = currentSectionContent?.structure
    ? currentSectionContent.structure
        .map(
          (item) =>
            `- Sequence ${item.sequence} (<${item.tag}>): "${item.content}"`,
        )
        .join("\n")
    : "No structured content available";

  // Build the user message, specific to "Summarize"
  const userMessage = `
You are an expert educator and writer tasked with reviewing and refining academic content designed to deliver practical skills through an online visual learning platform. Your role is to summarize educational material, ensuring it remains clear, concise, and effective for learners while preserving its core meaning.

This content is created for a Learning Management System (LMS), where it is structured as HTML snippets stored within a JSON data object. The LMS uses these data objects to display content to learners in a sequenced, interactive format. For example, a data object for a section might look like this:

{
  "complexity": "base",
  "structure": [
    { "sequence": 1, "tag": "h2", "content": "Introduction to Version Control" },
    { "sequence": 2, "tag": "p", "content": "Version control systems track changes to code." }
  ]
}

Here, the "structure" array defines the order and type of content (e.g., headings, paragraphs), and each item’s "content" is rendered as HTML within its specified "tag" (e.g., <h2>, <p>).

The subject you are reviewing content for is called "${unitName}". This subject includes multiple learning outcomes, and the specific learning outcome you are currently editing content for is titled "${outcomeName}". This learning outcome is structured into the following sections:

${sectionsText}

You are working within the section titled "${currentSectionTitle}", which contains the following full content:

${sectionContentText}

This section includes a specific snippet that you must edit, identified by the tag "<${snippetTag}>". The current HTML content for this snippet is:

${snippetContent}

Your task is to summarize this snippet, meaning you must condense the content to its essential ideas, significantly reducing its length while maintaining academic coherence and clarity. Focus on preserving the core meaning and key points, eliminating unnecessary details or elaboration. Ensure the summary is concise, direct, and effective for quick comprehension.

- Ensure the summarized snippet aligns with the subject "${unitName}" and the learning outcome "${outcomeName}".
- Output only the final HTML snippet, using the original "<${snippetTag}>" tag. Do not introduce new tag types (e.g., do not change a <p> to a <ul> or add nested tags like <ul> inside a <p>).
- For single-element tags (e.g., <p>, <h1>...<h5>, <statement>, <paragraphcard>, <code>), condense the content within the "<${snippetTag}>" tag into a brief, clear summary without altering its structure.
- For list tags (<ul>, <ol>), summarize the existing <li> items into fewer, concise points, removing or combining <li> items as needed, but keep the output as a single "<${snippetTag}>" with updated <li> content.
- For table tags (<table>), reduce the <tr> and <td> content to the most critical information, minimizing rows or cells as needed, but keep the output as a single "<${snippetTag}>" with updated table structure.
- The LMS supports these HTML tags: <h1>...<h5>, <p>, <ul>, <li>, <ol>, <code>, <table>, <tr>, <td>, <statement>, <paragraphcard>, <separator>, <insert:...>, <strong>, <em>, <span>. Use only the provided "<${snippetTag}>" for your output tag type.

Return only the updated HTML snippet.
`;

  // Log the final user message for debugging in production
  console.log("createEditSummaryPrompt: Final user message:", userMessage);

  const requestBody = {
    model: "gpt-4o-mini",
    messages: [
      {
        role: "user",
        content: userMessage,
      },
    ],
    max_tokens: 2000,
    temperature: 0.5,
  };

  return requestBody;
};

// heroManagementDashboard/functions/AICalls.js

// Existing imports and createEditEnhancementPrompt, createEditSummaryPrompt...

export const createEditSimplifyPrompt = (context) => {
  const {
    unitName,
    outcomeName,
    allSections = [],
    currentSectionTitle,
    currentSectionContent,
    snippetTag,
    snippetContent,
  } = context;

  const sectionsText = allSections
    .map(
      (sec, index) =>
        `- Section ${index + 1}: "${sec.title}" (ID: ${sec.sectionId})`,
    )
    .join("\n");

  const sectionContentText = currentSectionContent?.structure
    ? currentSectionContent.structure
        .map(
          (item) =>
            `- Sequence ${item.sequence} (<${item.tag}>): "${item.content}"`,
        )
        .join("\n")
    : "No structured content available";

  const userMessage = `
You are an expert educator and writer tasked with reviewing and refining academic content designed to deliver practical skills through an online visual learning platform. Your role is to simplify educational material, making it more accessible and easier to understand for learners while retaining its essential meaning.

This content is created for a Learning Management System (LMS), where it is structured as HTML snippets stored within a JSON data object. The LMS uses these data objects to display content to learners in a sequenced, interactive format. For example, a data object for a section might look like this:

{
  "complexity": "base",
  "structure": [
    { "sequence": 1, "tag": "h2", "content": "Introduction to Version Control" },
    { "sequence": 2, "tag": "p", "content": "Version control systems track changes to code." }
  ]
}

Here, the "structure" array defines the order and type of content (e.g., headings, paragraphs), and each item’s "content" is rendered as HTML within its specified "tag" (e.g., <h2>, <p>).

The subject you are reviewing content for is called "${unitName}". This subject includes multiple learning outcomes, and the specific learning outcome you are currently editing content for is titled "${outcomeName}". This learning outcome is structured into the following sections:

${sectionsText}

You are working within the section titled "${currentSectionTitle}", which contains the following full content:

${sectionContentText}

This section includes a specific snippet that you must edit, identified by the tag "<${snippetTag}>". The current HTML content for this snippet is:

${snippetContent}

Your task is to simplify this snippet, meaning you must rephrase the content into plain, straightforward language that is easy for beginners to grasp, while preserving its core meaning. Reduce complexity by using simpler terms, shorter sentences, and a clear structure, avoiding technical jargon unless necessary. Ensure the simplified snippet remains accurate and aligned with the subject "${unitName}" and the learning outcome "${outcomeName}".

- Output only the final HTML snippet, using the original "<${snippetTag}>" tag. Do not introduce new tag types (e.g., do not change a <p> to a <ul> or add nested tags like <ul> inside a <p>).
- For single-element tags (e.g., <p>, <h1>...<h5>, <statement>, <paragraphcard>, <code>), simplify the content within the "<${snippetTag}>" tag without altering its structure.
- For list tags (<ul>, <ol>), rephrase the existing <li> items into simpler language, combining or reducing items if needed, but keep the output as a single "<${snippetTag}>" with updated <li> content.
- For table tags (<table>), simplify the <tr> and <td> content into basic terms, minimizing rows or cells as needed, but keep the output as a single "<${snippetTag}>" with updated table structure.
- The LMS supports these HTML tags: <h1>...<h5>, <p>, <ul>, <li>, <ol>, <code>, <table>, <tr>, <td>, <statement>, <paragraphcard>, <separator>, <insert:...>, <strong>, <em>, <span>. Use only the provided "<${snippetTag}>" for your output tag type.

Return only the updated HTML snippet.
`;

  console.log("createEditSimplifyPrompt: Final user message:", userMessage);

  return {
    model: "gpt-4o-mini",
    messages: [{ role: "user", content: userMessage }],
    max_tokens: 2000,
    temperature: 0.5,
  };
};

export const createEditAddContextPrompt = (context) => {
  const {
    unitName,
    outcomeName,
    allSections = [],
    currentSectionTitle,
    currentSectionContent,
    snippetTag,
    snippetContent,
  } = context;

  const sectionsText = allSections
    .map(
      (sec, index) =>
        `- Section ${index + 1}: "${sec.title}" (ID: ${sec.sectionId})`,
    )
    .join("\n");

  const sectionContentText = currentSectionContent?.structure
    ? currentSectionContent.structure
        .map(
          (item) =>
            `- Sequence ${item.sequence} (<${item.tag}>): "${item.content}"`,
        )
        .join("\n")
    : "No structured content available";

  const userMessage = `
You are an expert educator and writer tasked with reviewing and refining academic content designed to deliver practical skills through an online visual learning platform. Your role is to add context to educational material, enriching it with relevant background or situational detail to improve learner understanding.

This content is created for a Learning Management System (LMS), where it is structured as HTML snippets stored within a JSON data object. The LMS uses these data objects to display content to learners in a sequenced, interactive format. For example, a data object for a section might look like this:

{
  "complexity": "base",
  "structure": [
    { "sequence": 1, "tag": "h2", "content": "Introduction to Version Control" },
    { "sequence": 2, "tag": "p", "content": "Version control systems track changes to code." }
  ]
}

Here, the "structure" array defines the order and type of content (e.g., headings, paragraphs), and each item’s "content" is rendered as HTML within its specified "tag" (e.g., <h2>, <p>).

The subject you are reviewing content for is called "${unitName}". This subject includes multiple learning outcomes, and the specific learning outcome you are currently editing content for is titled "${outcomeName}". This learning outcome is structured into the following sections:

${sectionsText}

You are working within the section titled "${currentSectionTitle}", which contains the following full content:

${sectionContentText}

This section includes a specific snippet that you must edit, identified by the tag "<${snippetTag}>". The current HTML content for this snippet is:

${snippetContent}

Your task is to add context to this snippet, meaning you must enrich it with relevant background information, situational details, or connections to the broader section content to enhance learner comprehension. Incorporate one concise example or reference (e.g., a tool, historical note, or practical scenario) that ties the snippet to the subject "${unitName}" and learning outcome "${outcomeName}", ensuring the addition is seamless and supports the original message without overwhelming it.

- Output only the final HTML snippet, using the original "<${snippetTag}>" tag. Do not introduce new tag types (e.g., do not change a <p> to a <ul> or add nested tags like <ul> inside a <p>).
- For single-element tags (e.g., <p>, <h1>...<h5>, <statement>, <paragraphcard>, <code>), enrich the content within the "<${snippetTag}>" tag with added context without altering its structure.
- For list tags (<ul>, <ol>), enhance the existing <li> items with contextual details or examples, adding <li> items if needed, but keep the output as a single "<${snippetTag}>" with updated <li> content.
- For table tags (<table>), add contextual details to the <tr> and <td> content, adjusting rows or cells as needed, but keep the output as a single "<${snippetTag}>" with updated table structure.
- The LMS supports these HTML tags: <h1>...<h5>, <p>, <ul>, <li>, <ol>, <code>, <table>, <tr>, <td>, <statement>, <paragraphcard>, <separator>, <insert:...>, <strong>, <em>, <span>. Use only the provided "<${snippetTag}>" for your output tag type.

Return only the updated HTML snippet.
`;

  console.log("createEditAddContextPrompt: Final user message:", userMessage);

  return {
    model: "gpt-4o-mini",
    messages: [{ role: "user", content: userMessage }],
    max_tokens: 2000,
    temperature: 0.5,
  };
};

export const createEditExpandPrompt = (context) => {
  const {
    unitName,
    outcomeName,
    allSections = [],
    currentSectionTitle,
    currentSectionContent,
    snippetTag,
    snippetContent,
  } = context;

  const sectionsText = allSections
    .map(
      (sec, index) =>
        `- Section ${index + 1}: "${sec.title}" (ID: ${sec.sectionId})`,
    )
    .join("\n");

  const sectionContentText = currentSectionContent?.structure
    ? currentSectionContent.structure
        .map(
          (item) =>
            `- Sequence ${item.sequence} (<${item.tag}>): "${item.content}"`,
        )
        .join("\n")
    : "No structured content available";

  const userMessage = `
You are an expert educator and writer tasked with reviewing and refining academic content designed to deliver practical skills through an online visual learning platform. Your role is to expand educational material, providing a more comprehensive explanation to deepen learner understanding.

This content is created for a Learning Management System (LMS), where it is structured as HTML snippets stored within a JSON data object. The LMS uses these data objects to display content to learners in a sequenced, interactive format. For example, a data object for a section might look like this:

{
  "complexity": "base",
  "structure": [
    { "sequence": 1, "tag": "h2", "content": "Introduction to Version Control" },
    { "sequence": 2, "tag": "p", "content": "Version control systems track changes to code." }
  ]
}

Here, the "structure" array defines the order and type of content (e.g., headings, paragraphs), and each item’s "content" is rendered as HTML within its specified "tag" (e.g., <h2>, <p>).

The subject you are reviewing content for is called "${unitName}". This subject includes multiple learning outcomes, and the specific learning outcome you are currently editing content for is titled "${outcomeName}". This learning outcome is structured into the following sections:

${sectionsText}

You are working within the section titled "${currentSectionTitle}", which contains the following full content:

${sectionContentText}

This section includes a specific snippet that you must edit, identified by the tag "<${snippetTag}>". The current HTML content for this snippet is:

${snippetContent}

Your task is to expand this snippet, meaning you must provide a more detailed and comprehensive explanation to deepen understanding of the topic. Extend the content with additional details, precise terminology, and structured reasoning, drawing from the full section context. Include at least one detailed example (e.g., a tool like SVN with a use case) to reinforce the explanation, ensuring the expansion remains relevant and aligned with the subject "${unitName}" and the learning outcome "${outcomeName}". There is no strict word limit—focus on clarity and completeness.

- Output only the final HTML snippet, using the original "<${snippetTag}>" tag. Do not introduce new tag types (e.g., do not change a <p> to a <ul> or add nested tags like <ul> inside a <p>).
- For single-element tags (e.g., <p>, <h1>...<h5>, <statement>, <paragraphcard>, <code>), expand the content within the "<${snippetTag}>" tag with a fuller explanation without altering its structure.
- For list tags (<ul>, <ol>), expand the existing <li> items with detailed explanations or examples, adding <li> items as needed, but keep the output as a single "<${snippetTag}>" with updated <li> content.
- For table tags (<table>), expand the <tr> and <td> content with comprehensive details or examples, adding rows or cells as needed, but keep the output as a single "<${snippetTag}>" with updated table structure.
- The LMS supports these HTML tags: <h1>...<h5>, <p>, <ul>, <li>, <ol>, <code>, <table>, <tr>, <td>, <statement>, <paragraphcard>, <separator>, <insert:...>, <strong>, <em>, <span>. Use only the provided "<${snippetTag}>" for your output tag type.

Return only the updated HTML snippet.
`;

  console.log("createEditExpandPrompt: Final user message:", userMessage);

  return {
    model: "gpt-4o-mini",
    messages: [{ role: "user", content: userMessage }],
    max_tokens: 2000,
    temperature: 0.5,
  };
};

// heroManagementDashboard/functions/AICalls.js

// Existing imports and prompts (createEditEnhancementPrompt, createEditSummaryPrompt, etc.)

export const createEditChangeTonePrompt = (context) => {
  const {
    unitName,
    outcomeName,
    allSections = [],
    currentSectionTitle,
    currentSectionContent,
    snippetTag,
    snippetContent,
    tone, // New parameter for desired tone
  } = context;

  const sectionsText = allSections
    .map(
      (sec, index) =>
        `- Section ${index + 1}: "${sec.title}" (ID: ${sec.sectionId})`,
    )
    .join("\n");

  const sectionContentText = currentSectionContent?.structure
    ? currentSectionContent.structure
        .map(
          (item) =>
            `- Sequence ${item.sequence} (<${item.tag}>): "${item.content}"`,
        )
        .join("\n")
    : "No structured content available";

  const userMessage = `
You are an expert educator and writer tasked with reviewing and refining academic content designed to deliver practical skills through an online visual learning platform. Your role is to adjust the tone of educational material to make it more suitable for learners, ensuring it remains clear, effective, and aligned with its purpose.

This content is created for a Learning Management System (LMS), where it is structured as HTML snippets stored within a JSON data object. The LMS uses these data objects to display content to learners in a sequenced, interactive format. For example, a data object for a section might look like this:

{
  "complexity": "base",
  "structure": [
    { "sequence": 1, "tag": "h2", "content": "Introduction to Version Control" },
    { "sequence": 2, "tag": "p", "content": "Version control systems track changes to code." }
  ]
}

Here, the "structure" array defines the order and type of content (e.g., headings, paragraphs), and each item’s "content" is rendered as HTML within its specified "tag" (e.g., <h2>, <p>).

The subject you are reviewing content for is called "${unitName}". This subject includes multiple learning outcomes, and the specific learning outcome you are currently editing content for is titled "${outcomeName}". This learning outcome is structured into the following sections:

${sectionsText}

You are working within the section titled "${currentSectionTitle}", which contains the following full content:

${sectionContentText}

This section includes a specific snippet that you must edit, identified by the tag "<${snippetTag}>". The current HTML content for this snippet is:

${snippetContent}

Your task is to change the tone of this snippet to a "${tone}" style (e.g., "Formal," "Casual," "Conversational"). Rewrite the content to reflect this tone while preserving its core meaning and ensuring it remains appropriate for the subject "${unitName}" and the learning outcome "${outcomeName}". Adjust wording, sentence structure, and style to match the specified tone, keeping the explanation clear and engaging.

- Output only the final HTML snippet content, without the "<${snippetTag}>" and "</${snippetTag}>" tags. Do not introduce new tag types (e.g., do not change a <p> to a <ul> or add nested tags like <ul> inside a <p>).
- For single-element tags (e.g., <p>, <h1>...<h5>, <statement>, <paragraphcard>, <code>), adjust the tone within the content without altering its structure.
- For list tags (<ul>, <ol>), rewrite the existing <li> items to match the tone, adjusting items as needed, but keep the output as content for a single "<${snippetTag}>".
- For table tags (<table>), modify the <tr> and <td> content to reflect the tone, adjusting rows or cells as needed, but keep the output as content for a single "<${snippetTag}>".
- The LMS supports these HTML tags: <h1>...<h5>, <p>, <ul>, <li>, <ol>, <code>, <table>, <tr>, <td>, <statement>, <paragraphcard>, <separator>, <insert:...>, <strong>, <em>, <span>. Return only the content for the provided "<${snippetTag}>", without the tags.

Return only the updated snippet content.
`;

  console.log("createEditChangeTonePrompt: Final user message:", userMessage);

  return {
    model: "gpt-4o-mini",
    messages: [{ role: "user", content: userMessage }],
    max_tokens: 2000,
    temperature: 0.5,
  };
};

export const createEditTogetherPrompt = (context) => {
  const {
    unitName,
    outcomeName,
    allSections = [],
    currentSectionTitle,
    currentSectionContent,
    snippetTag,
    snippetContent,
    userInstruction, // New parameter for custom user input
  } = context;

  const sectionsText = allSections
    .map(
      (sec, index) =>
        `- Section ${index + 1}: "${sec.title}" (ID: ${sec.sectionId})`,
    )
    .join("\n");

  const sectionContentText = currentSectionContent?.structure
    ? currentSectionContent.structure
        .map(
          (item) =>
            `- Sequence ${item.sequence} (<${item.tag}>): "${item.content}"`,
        )
        .join("\n")
    : "No structured content available";

  const userMessage = `
You are an expert educator and writer tasked with reviewing and refining academic content designed to deliver practical skills through an online visual learning platform. Your role is to collaboratively edit educational material based on specific user instructions, ensuring it remains clear, effective, and aligned with its educational goals.

This content is created for a Learning Management System (LMS), where it is structured as HTML snippets stored within a JSON data object. The LMS uses these data objects to display content to learners in a sequenced, interactive format. For example, a data object for a section might look like this:

{
  "complexity": "base",
  "structure": [
    { "sequence": 1, "tag": "h2", "content": "Introduction to Version Control" },
    { "sequence": 2, "tag": "p", "content": "Version control systems track changes to code." }
  ]
}

Here, the "structure" array defines the order and type of content (e.g., headings, paragraphs), and each item’s "content" is rendered as HTML within its specified "tag" (e.g., <h2>, <p>).

The subject you are reviewing content for is called "${unitName}". This subject includes multiple learning outcomes, and the specific learning outcome you are currently editing content for is titled "${outcomeName}". This learning outcome is structured into the following sections:

${sectionsText}

You are working within the section titled "${currentSectionTitle}", which contains the following full content:

${sectionContentText}

This section includes a specific snippet that you must edit, identified by the tag "<${snippetTag}>". The current HTML content for this snippet is:

${snippetContent}

Your task is to edit this snippet based on the following user instruction: "${userInstruction}". Interpret the instruction carefully and apply it to the snippet, using the full section content for context. Modify the content as directed while ensuring it remains clear, academically sound, and aligned with the subject "${unitName}" and the learning outcome "${outcomeName}". There are no strict limits—focus on fulfilling the user’s intent effectively.

- Output only the final HTML snippet content, without the "<${snippetTag}>" and "</${snippetTag}>" tags. Do not introduce new tag types (e.g., do not change a <p> to a <ul> or add nested tags like <ul> inside a <p>).
- For single-element tags (e.g., <p>, <h1>...<h5>, <statement>, <paragraphcard>, <code>), edit the content within the "<${snippetTag}>" as per the instruction without altering its structure.
- For list tags (<ul>, <ol>), adjust the existing <li> items according to the instruction, adding or removing items as needed, but keep the output as content for a single "<${snippetTag}>".
- For table tags (<table>), modify the <tr> and <td> content based on the instruction, adjusting rows or cells as needed, but keep the output as content for a single "<${snippetTag}>".
- The LMS supports these HTML tags: <h1>...<h5>, <p>, <ul>, <li>, <ol>, <code>, <table>, <tr>, <td>, <statement>, <paragraphcard>, <separator>, <insert:...>, <strong>, <em>, <span>. Return only the content for the provided "<${snippetTag}>", without the tags.

Return only the updated snippet content.
`;

  console.log("createEditTogetherPrompt: Final user message:", userMessage);

  return {
    model: "gpt-4o-mini",
    messages: [{ role: "user", content: userMessage }],
    max_tokens: 2000,
    temperature: 0.5,
  };
};

export const createEditCheckCodeCorrectnessPrompt = (context) => {
  const {
    unitName,
    outcomeName,
    allSections = [],
    currentSectionTitle,
    currentSectionContent,
    snippetTag,
    snippetContent,
  } = context;

  const sectionsText = allSections
    .map(
      (sec, index) =>
        `- Section ${index + 1}: "${sec.title}" (ID: ${sec.sectionId})`,
    )
    .join("\n");

  const sectionContentText = currentSectionContent?.structure
    ? currentSectionContent.structure
        .map(
          (item) =>
            `- Sequence ${item.sequence} (<${item.tag}>): "${item.content}"`,
        )
        .join("\n")
    : "No structured content available";

  const userMessage = `
You are an expert code reviewer tasked with analyzing and refining code snippets for an online visual learning platform designed to teach practical skills. Your role is to check the correctness of code content, ensuring it is syntactically valid, logically sound, and functionally correct for learners.

This content is created for a Learning Management System (LMS), where it is structured as HTML snippets stored within a JSON data object. The LMS uses these data objects to display content to learners in a sequenced, interactive format. For example, a data object for a section might look like this:

{
  "complexity": "base",
  "structure": [
    { "sequence": 1, "tag": "h2", "content": "Introduction to Version Control" },
    { "sequence": 2, "tag": "code", "content": "def clone_repo(url):\\n    import os\\n    os.system(f'git clone {url}')\\n    print('Repository cloned successfully')" }
  ]
}

Here, the "structure" array defines the order and type of content (e.g., headings, code), and each item’s "content" is rendered as HTML within its specified "tag" (e.g., <h2>, <code>).

The subject you are reviewing content for is called "${unitName}". This subject includes multiple learning outcomes, and the specific learning outcome you are currently editing content for is titled "${outcomeName}". This learning outcome is structured into the following sections:

${sectionsText}

You are working within the section titled "${currentSectionTitle}", which contains the following full content:

${sectionContentText}

This section includes a specific code snippet that you must edit, identified by the tag "<${snippetTag}>". The current HTML content for this snippet is:

${snippetContent}

Your task is to check the correctness of this code snippet. Analyze its syntax, logic, and functionality, assuming it is written in Python and part of a lesson on "Introduction to Git version control." Identify any errors, potential bugs, or issues (e.g., security risks, missing error handling), and provide a corrected version if necessary. Ensure the code is clear and suitable for learners, maintaining its core purpose. Include a brief explanation of your findings and changes as comments within the code.

- Output only the final HTML snippet content, without the "<${snippetTag}>" and "</${snippetTag}>" tags. Do not introduce new tag types (e.g., do not change a <code> to a <p>).
- Return the corrected code as plain text content suitable for the "<${snippetTag}>" (e.g., <code>), with explanatory comments included in the code using Python’s # syntax.
- The LMS supports these HTML tags: <h1>...<h5>, <p>, <ul>, <li>, <ol>, <code>, <table>, <tr>, <td>, <statement>, <paragraphcard>, <separator>, <insert:...>, <strong>, <em>, <span>. Return only the content for the provided "<${snippetTag}>", without the tags.

Return only the updated snippet content.
`;

  console.log(
    "createEditCheckCodeCorrectnessPrompt: Final user message:",
    userMessage,
  );

  return {
    model: "gpt-4o-mini",
    messages: [{ role: "user", content: userMessage }],
    max_tokens: 2000,
    temperature: 0.5,
  };
};

export const createEditIncreaseComplexityPrompt = (context) => {
  const {
    unitName,
    outcomeName,
    allSections = [],
    currentSectionTitle,
    currentSectionContent,
    snippetTag,
    snippetContent,
  } = context;

  const sectionsText = allSections
    .map(
      (sec, index) =>
        `- Section ${index + 1}: "${sec.title}" (ID: ${sec.sectionId})`,
    )
    .join("\n");

  const sectionContentText = currentSectionContent?.structure
    ? currentSectionContent.structure
        .map(
          (item) =>
            `- Sequence ${item.sequence} (<${item.tag}>): "${item.content}"`,
        )
        .join("\n")
    : "No structured content available";

  const userMessage = `
You are an advanced programmer and educator tasked with refining code snippets for an online visual learning platform designed to teach practical skills. Your role is to increase the complexity of code content, adding advanced features or logic to enrich the learning experience.

This content is created for a Learning Management System (LMS), where it is structured as HTML snippets stored within a JSON data object. The LMS uses these data objects to display content to learners in a sequenced, interactive format. For example, a data object for a section might look like this:

{
  "complexity": "base",
  "structure": [
    { "sequence": 1, "tag": "h2", "content": "Introduction to Version Control" },
    { "sequence": 2, "tag": "code", "content": "def clone_repo(url):\\n    import os\\n    os.system(f'git clone {url}')\\n    print('Repository cloned successfully')" }
  ]
}

Here, the "structure" array defines the order and type of content (e.g., headings, code), and each item’s "content" is rendered as HTML within its specified "tag" (e.g., <h2>, <code>).

The subject you are reviewing content for is called "${unitName}". This subject includes multiple learning outcomes, and the specific learning outcome you are currently editing content for is titled "${outcomeName}". This learning outcome is structured into the following sections:

${sectionsText}

You are working within the section titled "${currentSectionTitle}", which contains the following full content:

${sectionContentText}

This section includes a specific code snippet that you must edit, identified by the tag "<${snippetTag}>". The current HTML content for this snippet is:

${snippetContent}

Your task is to increase the complexity of this code snippet, assuming it is written in Python and part of a lesson on "Introduction to Git version control." Enhance it by adding advanced features (e.g., error handling, logging, additional Git operations), ensuring it remains functional and relevant to the learning outcome. Include comments explaining the added complexity to aid learner understanding.

- Output only the final HTML snippet content, without the "<${snippetTag}>" and "</${snippetTag}>" tags. Do not introduce new tag types (e.g., do not change a <code> to a <p>).
- Return the enhanced code as plain text content suitable for the "<${snippetTag}>" (e.g., <code>), with explanatory comments included in the code using Python’s # syntax.
- The LMS supports these HTML tags: <h1>...<h5>, <p>, <ul>, <li>, <ol>, <code>, <table>, <tr>, <td>, <statement>, <paragraphcard>, <separator>, <insert:...>, <strong>, <em>, <span>. Return only the content for the provided "<${snippetTag}>", without the tags.

Return only the updated snippet content.
`;

  console.log(
    "createEditIncreaseComplexityPrompt: Final user message:",
    userMessage,
  );

  return {
    model: "gpt-4o-mini",
    messages: [{ role: "user", content: userMessage }],
    max_tokens: 2000,
    temperature: 0.7, // Slightly higher for creativity
  };
};

export const createEditDecreaseComplexityPrompt = (context) => {
  const {
    unitName,
    outcomeName,
    allSections = [],
    currentSectionTitle,
    currentSectionContent,
    snippetTag,
    snippetContent,
  } = context;

  const sectionsText = allSections
    .map(
      (sec, index) =>
        `- Section ${index + 1}: "${sec.title}" (ID: ${sec.sectionId})`,
    )
    .join("\n");

  const sectionContentText = currentSectionContent?.structure
    ? currentSectionContent.structure
        .map(
          (item) =>
            `- Sequence ${item.sequence} (<${item.tag}>): "${item.content}"`,
        )
        .join("\n")
    : "No structured content available";

  const userMessage = `
You are an expert programmer and educator tasked with refining code snippets for an online visual learning platform designed to teach practical skills. Your role is to decrease the complexity of code content, making it simpler and more accessible for beginner learners while retaining its core functionality.

This content is created for a Learning Management System (LMS), where it is structured as HTML snippets stored within a JSON data object. The LMS uses these data objects to display content to learners in a sequenced, interactive format. For example, a data object for a section might look like this:

{
  "complexity": "base",
  "structure": [
    { "sequence": 1, "tag": "h2", "content": "Introduction to Version Control" },
    { "sequence": 2, "tag": "code", "content": "def clone_repo(url):\\n    import os\\n    os.system(f'git clone {url}')\\n    print('Repository cloned successfully')" }
  ]
}

Here, the "structure" array defines the order and type of content (e.g., headings, code), and each item’s "content" is rendered as HTML within its specified "tag" (e.g., <h2>, <code>).

The subject you are reviewing content for is called "${unitName}". This subject includes multiple learning outcomes, and the specific learning outcome you are currently editing content for is titled "${outcomeName}". This learning outcome is structured into the following sections:

${sectionsText}

You are working within the section titled "${currentSectionTitle}", which contains the following full content:

${sectionContentText}

This section includes a specific code snippet that you must edit, identified by the tag "<${snippetTag}>". The current HTML content for this snippet is:

${snippetContent}

Your task is to decrease the complexity of this code snippet, assuming it is written in Python and part of a lesson on "Introduction to Git version control." Simplify it by removing advanced features (e.g., error handling, complex logic), using basic constructs, and ensuring it remains functional and clear for beginners. Include brief comments to explain the simplified code.

- Output only the final HTML snippet content, without the "<${snippetTag}>" and "</${snippetTag}>" tags. Do not introduce new tag types (e.g., do not change a <code> to a <p>).
- Return the simplified code as plain text content suitable for the "<${snippetTag}>" (e.g., <code>), with explanatory comments included in the code using Python’s # syntax.
- The LMS supports these HTML tags: <h1>...<h5>, <p>, <ul>, <li>, <ol>, <code>, <table>, <tr>, <td>, <statement>, <paragraphcard>, <separator>, <insert:...>, <strong>, <em>, <span>. Return only the content for the provided "<${snippetTag}>", without the tags.

Return only the updated snippet content.
`;

  console.log(
    "createEditDecreaseComplexityPrompt: Final user message:",
    userMessage,
  );

  return {
    model: "gpt-4o-mini",
    messages: [{ role: "user", content: userMessage }],
    max_tokens: 2000,
    temperature: 0.5,
  };
};

export const createEditAddCommentsPrompt = (context) => {
  const {
    unitName,
    outcomeName,
    allSections = [],
    currentSectionTitle,
    currentSectionContent,
    snippetTag,
    snippetContent,
  } = context;

  const sectionsText = allSections
    .map(
      (sec, index) =>
        `- Section ${index + 1}: "${sec.title}" (ID: ${sec.sectionId})`,
    )
    .join("\n");

  const sectionContentText = currentSectionContent?.structure
    ? currentSectionContent.structure
        .map(
          (item) =>
            `- Sequence ${item.sequence} (<${item.tag}>): "${item.content}"`,
        )
        .join("\n")
    : "No structured content available";

  const userMessage = `
You are an expert programmer and educator tasked with refining code snippets for an online visual learning platform designed to teach practical skills. Your role is to add explanatory comments to code content, making it easier for learners to understand its purpose and functionality.

This content is created for a Learning Management System (LMS), where it is structured as HTML snippets stored within a JSON data object. The LMS uses these data objects to display content to learners in a sequenced, interactive format. For example, a data object for a section might look like this:

{
  "complexity": "base",
  "structure": [
    { "sequence": 1, "tag": "h2", "content": "Introduction to Version Control" },
    { "sequence": 2, "tag": "code", "content": "def clone_repo(url):\\n    import os\\n    os.system(f'git clone {url}')\\n    print('Repository cloned successfully')" }
  ]
}

Here, the "structure" array defines the order and type of content (e.g., headings, code), and each item’s "content" is rendered as HTML within its specified "tag" (e.g., <h2>, <code>).

The subject you are reviewing content for is called "${unitName}". This subject includes multiple learning outcomes, and the specific learning outcome you are currently editing content for is titled "${outcomeName}". This learning outcome is structured into the following sections:

${sectionsText}

You are working within the section titled "${currentSectionTitle}", which contains the following full content:

${sectionContentText}

This section includes a specific code snippet that you must edit, identified by the tag "<${snippetTag}>". The current HTML content for this snippet is:

${snippetContent}

Your task is to add comments to this code snippet, assuming it is written in Python and part of a lesson on "Introduction to Git version control." Insert clear, concise comments using Python’s # syntax to explain what each significant line or block does, why it’s included, and how it contributes to the code’s purpose. Ensure the comments are beginner-friendly and enhance understanding without altering the code’s functionality.

- Output only the final HTML snippet content, without the "<${snippetTag}>" and "</${snippetTag}>" tags. Do not introduce new tag types (e.g., do not change a <code> to a <p>).
- Return the code with added comments as plain text content suitable for the "<${snippetTag}>" (e.g., <code>), preserving the original code structure.
- The LMS supports these HTML tags: <h1>...<h5>, <p>, <ul>, <li>, <ol>, <code>, <table>, <tr>, <td>, <statement>, <paragraphcard>, <separator>, <insert:...>, <strong>, <em>, <span>. Return only the content for the provided "<${snippetTag}>", without the tags.

Return only the updated snippet content.
`;

  console.log("createEditAddCommentsPrompt: Final user message:", userMessage);

  return {
    model: "gpt-4o-mini",
    messages: [{ role: "user", content: userMessage }],
    max_tokens: 2000,
    temperature: 0.5,
  };
};

export const createEditRefactorPrompt = (context) => {
  const {
    unitName,
    outcomeName,
    allSections = [],
    currentSectionTitle,
    currentSectionContent,
    snippetTag,
    snippetContent,
  } = context;

  const sectionsText = allSections
    .map(
      (sec, index) =>
        `- Section ${index + 1}: "${sec.title}" (ID: ${sec.sectionId})`,
    )
    .join("\n");

  const sectionContentText = currentSectionContent?.structure
    ? currentSectionContent.structure
        .map(
          (item) =>
            `- Sequence ${item.sequence} (<${item.tag}>): "${item.content}"`,
        )
        .join("\n")
    : "No structured content available";

  const userMessage = `
You are an expert programmer and educator tasked with refining code snippets for an online visual learning platform designed to teach practical skills. Your role is to refactor code content, improving its structure, readability, and maintainability while preserving its core functionality.

This content is created for a Learning Management System (LMS), where it is structured as HTML snippets stored within a JSON data object. The LMS uses these data objects to display content to learners in a sequenced, interactive format. For example, a data object for a section might look like this:

{
  "complexity": "base",
  "structure": [
    { "sequence": 1, "tag": "h2", "content": "Introduction to Version Control" },
    { "sequence": 2, "tag": "code", "content": "def clone_repo(url):\\n    import os\\n    os.system(f'git clone {url}')\\n    print('Repository cloned successfully')" }
  ]
}

Here, the "structure" array defines the order and type of content (e.g., headings, code), and each item’s "content" is rendered as HTML within its specified "tag" (e.g., <h2>, <code>).

The subject you are reviewing content for is called "${unitName}". This subject includes multiple learning outcomes, and the specific learning outcome you are currently editing content for is titled "${outcomeName}". This learning outcome is structured into the following sections:

${sectionsText}

You are working within the section titled "${currentSectionTitle}", which contains the following full content:

${sectionContentText}

This section includes a specific code snippet that you must edit, identified by the tag "<${snippetTag}>". The current HTML content for this snippet is:

${snippetContent}

Your task is to refactor this code snippet, assuming it is written in Python and part of a lesson on "Introduction to Git version control." Restructure it to improve readability, maintainability, and adherence to best practices (e.g., better variable names, modular design, PEP 8 compliance), while keeping its core functionality intact. Include comments to explain the refactored structure and key changes.

- Output only the final HTML snippet content, without the "<${snippetTag}>" and "</${snippetTag}>" tags. Do not introduce new tag types (e.g., do not change a <code> to a <p>).
- Return the refactored code as plain text content suitable for the "<${snippetTag}>" (e.g., <code>), with explanatory comments included in the code using Python’s # syntax.
- The LMS supports these HTML tags: <h1>...<h5>, <p>, <ul>, <li>, <ol>, <code>, <table>, <tr>, <td>, <statement>, <paragraphcard>, <separator>, <insert:...>, <strong>, <em>, <span>. Return only the content for the provided "<${snippetTag}>", without the tags.

Return only the updated snippet content.
`;

  console.log("createEditRefactorPrompt: Final user message:", userMessage);

  return {
    model: "gpt-4o-mini",
    messages: [{ role: "user", content: userMessage }],
    max_tokens: 2000,
    temperature: 0.5,
  };
};
/**
 * createAddTogetherPrompt - Used when the user wants AI to create brand-new content for a chosen tag.
 *
 * This version includes more context and detailed instructions:
 *  - Explains the learning content structure (sequential JSON).
 *  - Clarifies exactly where in the sequence we’re adding the new snippet.
 *  - Provides the immediate preceding item’s HTML (if it exists).
 *  - Forces the AI to return only raw HTML suitable for the chosen tag.
 *
 * @param {object} context
 *  - unitName: string (e.g., "Introduction to Programming")
 *  - outcomeName: string (e.g., "Outcome: Master Git")
 *  - allSections: array of section objects (title, sectionId, etc.)
 *  - currentSectionTitle: string
 *  - currentSectionContent: the existing content object that includes .structure
 *  - snippetTag: the chosen tag (e.g., "p", "ul", "table", "code", etc.)
 *  - userInstruction: user’s typed instructions (e.g., "Add a paragraph about X")
 *  - newElementSequence: the position (sequence number) where this snippet will appear
 *    so the AI knows the snippet flows from the prior content item.
 *  - styleGuidelines: optional, e.g. "Write in a slightly formal tone, maintain a consistent voice..."
 */
export const createAddTogetherPrompt = (context) => {
  const {
    unitName,
    outcomeName,
    allSections = [],
    currentSectionTitle,
    currentSectionContent,
    snippetTag,
    userInstruction,
    newElementSequence,
    styleGuidelines,
  } = context;

  // 1. Let’s map out the existing structure items for context
  let sectionContentText = "No structured content in this section.";
  let precedingItemText = null; // Will hold the preceding item’s snippet, if any

  if (currentSectionContent?.structure?.length) {
    // Entire content for logs
    sectionContentText = currentSectionContent.structure
      .map((item) => {
        return `- Sequence ${item.sequence} (<${item.tag}>): "${item.content}"`;
      })
      .join("\n");

    // 2. Try to find the preceding item’s content (newElementSequence - 1)
    if (typeof newElementSequence === "number") {
      const prevSeq = newElementSequence - 1;
      const found = currentSectionContent.structure.find(
        (it) => it.sequence === prevSeq,
      );
      if (found) {
        precedingItemText = `Sequence ${found.sequence} (<${found.tag}>): "${found.content}"`;
      }
    }
  }

  // 3. Build a bullet list of the entire sections for additional context
  const sectionsText =
    allSections
      .map(
        (sec, i) => `- Section ${i + 1}: "${sec.title}" (ID: ${sec.sectionId})`,
      )
      .join("\n") || "(No sections found)";

  // If there’s a custom style guideline, add it to the instructions
  const guidelinesText = styleGuidelines
    ? `\n**Style Guidelines**: ${styleGuidelines}\n`
    : "";

  // Explanation of snippetTag constraints
  const snippetInstruction = `
You must create **one** new snippet specifically for the tag "<${snippetTag}>". 
- If it’s a <p>, produce a single paragraph: <p>...</p>.
- If it’s a <ul> or <ol>, produce a single list with <li> items: <ul><li>...</li></ul> or similar.
- If it’s a <table>, produce valid table rows/cells, e.g. <table><tr><td>...</td></tr></table>.
- If it’s "code", produce only the code block inside <code>...</code>.
- Return **no** extra tags outside the main snippet. 
- The snippet must be **raw HTML** suitable for rendering inside this "<${snippetTag}>".
`.trim();

  // 4. If we have a preceding item, let’s note it in the “Where We Are Adding” block
  let precedingItemBlock = "";
  if (precedingItemText) {
    precedingItemBlock = `
- The preceding item is ${precedingItemText}.
  `;
  } else {
    precedingItemBlock = `
- The preceding item is not found (maybe this is the first element in the sequence).
  `;
  }

  // 5. Build the final userMessage
  const userMessage = `
You are an expert educator and writer tasked with adding **learning content** to a existing learning program on a Learning Management System (LMS).  
This LMS stores content as a **sequential JSON** array, where each item has:
  { "sequence": number, "tag": string, "content": string (HTML) }

This JSON represents a traditional HTML model and allows the LMS to dynamically render content using custom styled components for each individual html tag and it is rendered in that particular sequence.

**Context**:
1) Broader Subject Name: "${unitName}"
2) Your focus will be on the current Learning Outcome: "${outcomeName}"
3) The content for the learning outcome is broken into digestible sections, which are:
${sectionsText}

4) Current Section Title where you are requested to add Content to: "${currentSectionTitle}"
5) Existing Content within this section:
${sectionContentText}

**Where We Were Requested to Add a new Snippet of Content**:
- We want you to assist us in inserting a new snippet of learning content at sequence number ${
    typeof newElementSequence === "number" ? newElementSequence : "N/A"
  }. 
${precedingItemBlock}
- It must flow and fit in context, building upon previous paragraphs or matching the style of prior content.

${guidelinesText}

**Tag to Add**: "<${snippetTag}>"

**User Instruction**:
\`${userInstruction}\`

${snippetInstruction}

**Your Task**:
1) Read the user’s instruction carefully. 
2) **Write a brand-new snippet** to be placed at sequence ${
    typeof newElementSequence === "number" ? newElementSequence : "N/A"
  }, flowing logically from earlier content if relevant.
3) Maintain a consistent tone and style with the existing content, unless otherwise specified.
4) Return **only** that final snippet in raw HTML, strictly within <${snippetTag}>…</${snippetTag}>.

**Do not** add commentary or text outside the snippet. 
**Do not** add other top-level tags, side headings, or wrappers. Just the snippet.

Example of final output: 
\`<${snippetTag}>Some text or list or table…</${snippetTag}>\`
`.trim();

  // 6. Return the actual request body for the AI
  return {
    model: "gpt-4o-mini",
    messages: [
      {
        role: "user",
        content: userMessage,
      },
    ],
    max_tokens: 2000,
    temperature: 0.6,
  };
};

/**
 * createExecutablePrompt - Used when the user wants AI to create a brand-new executable code snippet
 * for the special "<executable>" tag. This prompt is tailored to produce valid JavaScript code that
 * can be executed in a code editor terminal and yield visible output.
 *
 * This version includes detailed context:
 *  - Explains the learning content structure (sequential JSON).
 *  - Clarifies exactly where in the sequence the new snippet will be inserted.
 *  - Provides the immediate preceding item’s details (if available).
 *  - Instructs the AI that the tag is reserved for executable JavaScript code.
 *  - Forces the AI to return only raw JavaScript code wrapped inside the "<executable>" tags.
 *
 * @param {object} context
 *  - unitName: string (e.g., "Introduction to Programming")
 *  - outcomeName: string (e.g., "Outcome: Master Git")
 *  - allSections: array of section objects (with title, sectionId, etc.)
 *  - currentSectionTitle: string
 *  - currentSectionContent: the existing content object that includes .structure
 *  - snippetTag: the chosen tag, in this case it will be "executable"
 *  - userInstruction: user’s typed instruction (e.g., "Create a code example that demonstrates X")
 *  - newElementSequence: the position (sequence number) where this snippet will appear,
 *    so the AI knows how the new code should flow from the prior content.
 *  - styleGuidelines: optional, e.g. "Write in a clear, didactic tone..."
 */
export const createExecutablePrompt = (context) => {
  const {
    unitName,
    outcomeName,
    allSections = [],
    currentSectionTitle,
    currentSectionContent,
    snippetTag,
    userInstruction,
    newElementSequence,
    styleGuidelines,
  } = context;

  // 1. Map out the existing structure items for context
  let sectionContentText = "No structured content in this section.";
  let precedingItemText = null; // Holds the preceding item's details, if any

  if (currentSectionContent?.structure?.length) {
    sectionContentText = currentSectionContent.structure
      .map(
        (item) =>
          `- Sequence ${item.sequence} (<${item.tag}>): "${item.content}"`,
      )
      .join("\n");

    // Try to find the preceding item (newElementSequence - 1)
    if (typeof newElementSequence === "number") {
      const prevSeq = newElementSequence - 1;
      const found = currentSectionContent.structure.find(
        (it) => it.sequence === prevSeq,
      );
      if (found) {
        precedingItemText = `Sequence ${found.sequence} (<${found.tag}>): "${found.content}"`;
      }
    }
  }

  // 2. Build a bullet list of all sections for additional context
  const sectionsText =
    allSections
      .map(
        (sec, i) => `- Section ${i + 1}: "${sec.title}" (ID: ${sec.sectionId})`,
      )
      .join("\n") || "(No sections found)";

  // 3. If there’s a custom style guideline, add it to the instructions
  const guidelinesText = styleGuidelines
    ? `\n**Style Guidelines**: ${styleGuidelines}\n`
    : "";

  // 4. Explanation of snippetTag constraints for executable code
  const snippetInstruction = `
You must create **one** new snippet specifically for the tag "<${snippetTag}>". 
This tag is reserved for executable JavaScript code that will run in the provided code editor terminal.
- The code must be valid JavaScript.
- It should be written to demonstrate a clear, interactive example related to the subject matter.
- It should produce output when executed (for example, by using console.log).
- Return **no** extra tags or commentary outside the main code block.
- The code must be wrapped inside <${snippetTag}> and </${snippetTag}>.
`.trim();

  // 5. Describe where the snippet should be inserted
  let precedingItemBlock = "";
  if (precedingItemText) {
    precedingItemBlock = `- The preceding item is ${precedingItemText}.`;
  } else {
    precedingItemBlock = `- The preceding item is not found (this may be the first element in the sequence).`;
  }

  // 6. Build the final user message for the AI
  const userMessage = `
You are an expert educator and writer tasked with adding **executable learning content** to an existing learning program on a Learning Management System (LMS).  
This LMS stores content as a **sequential JSON** array, where each item has:
  { "sequence": number, "tag": string, "content": string (HTML) }

This JSON represents a traditional HTML model and allows the LMS to dynamically render content using custom styled components for each HTML tag in sequence.

**Context**:
1) Broader Subject Name: "${unitName}"
2) Your focus will be on the current Learning Outcome: "${outcomeName}"
3) The content for the learning outcome is divided into digestible sections, which are:
${sectionsText}

4) Current Section Title where you are requested to add content: "${currentSectionTitle}"
5) Existing Content within this section:
${sectionContentText}

**Where We Are Adding a New Executable Code Snippet**:
- You are to insert a new snippet at sequence number ${
    typeof newElementSequence === "number" ? newElementSequence : "N/A"
  }.
${precedingItemBlock}
- The new snippet should logically follow the preceding content and match its style.

${guidelinesText}

**Tag to Add**: "<${snippetTag}>"
(This tag is reserved for executable JavaScript code that runs in a code editor terminal.)

**User Instruction**:
\`${userInstruction}\`

${snippetInstruction}

**Your Task**:
1) Read the user’s instruction carefully.
2) **Write a brand-new executable JavaScript code snippet** to be placed at sequence ${
    typeof newElementSequence === "number" ? newElementSequence : "N/A"
  }, that demonstrates an example related to the subject matter.
3) Ensure the code is valid, produces output when executed, and is written in clear JavaScript.
4) Use plenty of comments to explain the code’s purpose and functionality, making it beginner-friendly.
5) For more complex code blocks it is advised that you add a comment block to provide thorough context around the code
4) Return **only** the final snippet wrapped in <${snippetTag}>…</${snippetTag}>.
**Do not** add any commentary or additional tags.
Example of final output:
\`<${snippetTag}>console.log("Hello World");</${snippetTag}>\`
`.trim();

  // 7. Return the complete request body for the AI
  return {
    model: "gpt-4o-mini",
    messages: [
      {
        role: "user",
        content: userMessage,
      },
    ],
    max_tokens: 2000,
    temperature: 0.6,
  };
};
export const createAddComplexityBasePrompt = (context) => {
  const {
    unitName,
    outcomeName,
    allSections = [],
    currentSectionTitle,
    currentSectionContent, // contentToRender
    currentComplexity,
    newComplexity,
  } = context;

  // Build section list for context
  const sectionsText = allSections
    .map(
      (sec, index) =>
        `- Section ${index + 1}: "${sec.title}" (ID: ${sec.sectionId})`,
    )
    .join("\n");

  // Serialize current content structure
  const currentContentText = currentSectionContent?.structure
    ? currentSectionContent.structure
        .map(
          (item) =>
            `- Sequence ${item.sequence} (<${item.tag}>): "${item.content}"`,
        )
        .join("\n")
    : "No structured content available";

  const userMessage = `
You are an expert educator tasked with creating learning content for an online Learning Management System (LMS) that adapts to different learner paces. The LMS stores content as a sequential JSON array, where each item has:
  { "sequence": number, "tag": string, "content": string (HTML) }
This array is rendered in order using custom components for specific HTML tags.

**Context**:
- Subject: "${unitName}"
- Learning Outcome: "${outcomeName}"
- Sections in this outcome:
${sectionsText}
- Current Section: "${currentSectionTitle}"
- Current Content (at complexity "${currentComplexity}"):
${currentContentText}

**Complexity Scale**:
- The LMS uses a complexity scale from 1 (simplest) to 6 (most complex), with "Base" as the midpoint:
  - **Level 1-3**: For slower learners—longer, more elaborate content with extra context, simpler examples, and reintroduced baseline concepts (e.g., foundational terms or skills needed to understand the topic). Expansive and beginner-friendly, ensuring no learner is left behind.
  - **Base**: Balanced foundational content for average learners—clear, practical, with moderate detail and examples.
  - **Level 4-6**: For sharp, fast learners—concise, assumes prior knowledge, introduces advanced or complementary concepts, and uses compact, efficient examples.
- Goal: Tailor content to learner pace—lower levels provide more support; higher levels challenge advanced learners.

**Supported HTML Tags**:
- Use **only** these tags to structure the content:
  - **Headings**: <h1>, <h2>, <h3>, <h4>, <h5>
  - **Paragraphs**: <p>
  - **Lists**: <ul>, <li>, <ol>
  - **Code blocks**: <code>...</code> (for static code examples in any language)
  - **Executable Code**: <executable>...</executable> (for valid JavaScript code that runs in a code editor terminal, producing visible output like console.log; include beginner-friendly comments)
  - **Tables**: <table>, <tr>, <td>
  - **Inline emphasis**: <strong>, <em>, <span>
  - **Placeholders**: <insert: [brief explanation or code block]> (e.g., <insert: Diagram of OOP hierarchy>)
  - **Custom Tags** (for LMS styling):
    - <statement>: For standout text or key concepts needing highlight.
    - <separator>: For visual dividers or pacing between segments.
    - <paragraphcard>: For emphasized paragraphs in a card-like layout (use for key insights).
- **DO NOT** use wrapper tags like <header>, <section>, <article>, or <footer>.

**Task**:
- Adapt the current content (complexity: "${currentComplexity}") to a new complexity level: "${newComplexity}".
- Adjust depth and detail based on the scale:
  - If "${newComplexity}" is lower (1-3), expand with more context, simpler examples, and reintroduce baseline concepts.
  - If "${newComplexity}" is higher (4-6), make it concise, assume prior knowledge, and add advanced concepts or examples (e.g., use <executable> for interactive JS demos).
  - If "${newComplexity}" is "Base", balance clarity and practicality.
- Preserve the topic and intent of the original content, adjusting only complexity.
- Use the supported tags above to structure the content. For example, include <executable> tags for interactive JavaScript examples where appropriate.
- Output **only** the adapted HTML content, without wrapper tags.

**Current Content to Adapt**:
${currentContentText}

Return only the final HTML content for "${newComplexity}".
`;

  console.log(
    "createAddComplexityBasePrompt: Final user message:",
    userMessage,
  );

  return {
    model: "gpt-4o-mini",
    messages: [{ role: "user", content: userMessage }],
    max_tokens: 4000,
    temperature: 0.5,
  };
};

export const createAddComplexityStructurePrompt = (htmlContent) => {
  const userMessage = `
You are given HTML content for an LMS, which uses a sequential JSON array to store content:
  { "sequence": number, "tag": string, "content": string (HTML) }

**Supported HTML Tags**:
- <h1>, <h2>, <h3>, <h4>, <h5>
- <p>
- <ul>, <li>, <ol>
- <code> (static code)
- <executable> (executable JavaScript for a code editor terminal)
- <table>, <tr>, <td>
- <strong>, <em>, <span> (inline)
- <insert: [description]>
- Custom: <statement>, <separator>, <paragraphcard>

**Task**:
- Convert the provided HTML into a flat JSON array.
- Each entry should have:
  - "sequence": increasing integer (1, 2, 3, ...) in top-down order.
  - "tag": the HTML tag (e.g., "h1", "p", "executable").
  - "content": inner HTML/text, preserving sub-tags (e.g., <ul> includes <li>).
- Ignore wrapper tags (<header>, <section>, <article>, <footer>).
- For <insert: ...>, use "tag": "insert" and "content": "[description]".
- Return **only** a valid JSON array, no extra text.

**HTML Content**:
${htmlContent}

Example Output:
[
  {"sequence": 1, "tag": "h1", "content": "Title"},
  {"sequence": 2, "tag": "p", "content": "Text with <strong>bold</strong>"}
]
`;

  console.log(
    "createAddComplexityStructurePrompt: Final user message:",
    userMessage,
  );

  return {
    model: "gpt-4o-mini",
    messages: [{ role: "user", content: userMessage }],
    max_tokens: 3000,
    temperature: 0.2, // Lower for precise JSON
  };
};

export const createAddComplexityExamplesPrompt = (context) => {
  const {
    unitName,
    outcomeName,
    allSections = [],
    currentSectionTitle,
    htmlContent, // From Prompt 1
    newComplexity,
  } = context;

  // Build section list for context
  const sectionsText = allSections
    .map(
      (sec, index) =>
        `- Section ${index + 1}: "${sec.title}" (ID: ${sec.sectionId})`,
    )
    .join("\n");

  const userMessage = `
You are an expert educator tasked with enriching learning content for an online Learning Management System (LMS). The LMS renders content from a sequential JSON array, where each item has:
  { "sequence": number, "tag": string, "content": string (HTML) }

**Context**:
- Subject: "${unitName}"
- Learning Outcome: "${outcomeName}"
- Sections in this outcome:
${sectionsText}
- Current Section: "${currentSectionTitle}"
- New Complexity Level: "${newComplexity}"

**Complexity Concept**:
- We are developing content for the same topic at different levels of complexity, ranging from 1 (simplest) to 6 (most complex), with "Base" as the midpoint:
  - **Level 1-3 (Lower Complexity)**: Designed for slower learners—content is longer, more elaborate, with extensive context and simpler, step-by-step examples. Reintroduces baseline concepts (e.g., foundational skills or terms) to ensure understanding, prioritizing accessibility and support.
  - **Base**: Balanced content for average learners—practical, clear, with moderate detail and examples that bridge basic and intermediate understanding.
  - **Level 4-6 (Higher Complexity)**: Aimed at sharp, fast learners—content is concise, assumes prior knowledge, and introduces advanced or complementary concepts with compact, efficient examples (e.g., interactive demos).
- The complexity level "${newComplexity}" dictates the depth, detail, and type of examples you should add, ensuring relevance to the learner’s pace and prior knowledge.

**Supported HTML Tags**:
- Use only these tags:
  - **Headings**: <h1>, <h2>, <h3>, <h4>, <h5>
  - **Paragraphs**: <p>
  - **Lists**: <ul>, <li>, <ol>
  - **Code blocks**: <code>...</code> (static code in any language)
  - **Executable Code**: <executable>...</executable> (valid JavaScript, runs in a code editor terminal, produces output like console.log; include comments)
  - **Tables**: <table>, <tr>, <td>
  - **Inline emphasis**: <strong>, <em>, <span>
  - **Placeholders**: <insert: [brief explanation or code block]>
  - **Custom Tags**: <statement>, <separator>, <paragraphcard>
- **DO NOT** use <header>, <section>, <article>, or <footer>.

**Task**:
- Evaluate the provided HTML content, developed for "${newComplexity}".
- Add or refine practical examples (e.g., <code>, <executable>, real-world scenarios) **only where they add clear educational value** to the topic at this complexity:
  - For 1-3: Add simpler, detailed examples (e.g., <code> with extra context) to support beginners.
  - For Base: Add balanced, practical examples that reinforce understanding.
  - For 4-6: Add concise, advanced examples (e.g., <executable> for interactive JS demos) for experienced learners.
- If the content already has sufficient, relevant examples for "${newComplexity}", return it unchanged.
- Output **only** the enriched HTML content, without wrapper tags.

**HTML Content to Enrich**:
${htmlContent}

Return only the final enriched HTML content for "${newComplexity}".
`;

  console.log(
    "createAddComplexityExamplesPrompt: Final user message:",
    userMessage,
  );

  return {
    model: "gpt-4o-mini",
    messages: [{ role: "user", content: userMessage }],
    max_tokens: 4500,
    temperature: 0.5,
  };
};
/**
 * Generates a request payload for server-managed lesson image generation.
 * The callable owns the primary/fallback model policy and AI Wallet billing;
 * clients deliberately cannot select an image model.
 *
 * @param {string} prompt    – Full, descriptive text-to-image prompt (40-100 words).
 * @param {string} fileName  – Target file name (e.g. "hero_banner.jpg").
 * @param {string} style     – One of the predefined style keywords.
 * @param {object} generationContext - Stable content identity for safe retry.
 * @returns {object} Server-managed image request body.
 * @throws {Error} If inputs are invalid.
 */
export const createImageGenerationPrompt = (
  prompt,
  fileName,
  style,
  generationContext = {}
) => {
  // Basic validation
  if (!prompt || typeof prompt !== "string") {
    throw new Error("Invalid or missing prompt");
  }
  if (!fileName || typeof fileName !== "string") {
    throw new Error("Invalid or missing fileName");
  }
  if (!style || typeof style !== "string") {
    throw new Error("Invalid or missing style");
  }

  return {
    endpoint: "images.generate",
    prompt: prompt.trim(),
    n: 1,
    size: "1024x1024",
    quality: "medium",
    output_format: "png",
    generationContext: {
      ...generationContext,
      fileName,
    },
  };
};

/**
 * Generates a prompt for creating a tutor podcast script based on section content.
 * Returns plain text with speaker-labeled dialogue, designed for audio narration.
 *
 * @param {string} htmlContent - The HTML content of the section to base the script on.
 * @param {string} sectionId - The ID of the section (e.g., "Introduction_to_Networking").
 * @returns {Promise<object>} Request payload for the AI model.
 * @throws {Error} If input is invalid or payload generation fails.
 */
export const createTutorScriptPrompt = async (htmlContent, sectionId) => {
  try {
    // Validate input
    if (!htmlContent || typeof htmlContent !== "string") {
      throw new Error("Invalid or missing htmlContent");
    }
    if (!sectionId || typeof sectionId !== "string") {
      throw new Error("Invalid or missing sectionId");
    }

    const userMessage = `
You are an **award-winning podcast writer** and **master educator**.  
Your job is to turn the SECTION CONTENT into an **audio-first** teaching conversation between exactly **two recurring speakers -a female and a male**:

• **[Host]** –female- warm, energetic facilitator who keeps the show on-track, asks questions, and frames segments.  
• **[Expert]** –male- subject-matter guru who gives clear, insightful, story-rich explanations.

The name of the host is "Alexa" and the expert is "Dr Jordan Fearson".
Background:
- Dr Jordan Fearson is from Johannesburg, South Africa.
- You need to determine the specialized qualifications and experience of Dr Jordan Fearson based on the section content provided. the goal is to make him sound like a credible expert in the field related to the section content.


────────────────────────────────────────────────────────────────
SECTION CONTENT  (source material – cover it all, nothing more):
${htmlContent}

SECTION ID:
${sectionId}
────────────────────────────────────────────────────────────────

🎯 **DELIVERABLE – plain text only, no markdown, no JSON**  
Each line starts with the speaker label in square brackets, e.g.:

[Host]: …  
[Expert]: …

Never invent additional speakers.  Labels **must** be identical throughout.

────────────────────────────────────────────────────────────────
🔖 **STRUCTURE & TIMING**

1. **INTRODUCTION**  ≈ 10 - 25 sec  
   Goal: Open with a warm, charismatic greeting that immediately sets the vibe, builds anticipation, and creates a strong emotional or intellectual pull into the lesson.

🔧 STRUCTURE FOR AI TO FOLLOW
GREETING + WELCOME TO EPISODE
Start with an inviting, casual welcome to the show or new episode.

Tone: Friendly, upbeat, and human. Vary phrases across episodes to avoid repetition.

Examples:

“Hey there, and welcome back to another episode of Fun with Cyber…”

“Hello and thanks for tuning in to our learning podcast, where we unravel the secrets behind today’s tech.”

“It’s my pleasure to welcome you to another chapter in our learning and informative podcast — your go-to pod for mastering big ideas, one bite at a time.”

“Today, we’re diving into something you definitely want to understand…”

HOOK – SPARK CURIOSITY
Hit the listener with a teaser, strange fact, or attention-grabber right after the welcome. This makes the listener lean in before they know what they’re learning.

Examples:

“What if I told you your online password might say more about your childhood than you think?”

“There’s a reason most cyber attacks don’t feel like attacks at all — and today, we’re going to expose why.”

“Pop quiz: if a hacker steals your data, but you never notice — did it even happen?”

CONTEXT SETUP
Introduce the podcast's identity and the episode's topic in a way that ties it to real-world applications, everyday challenges, or the listener’s personal experiences, making the content feel immediate and valuable.

Examples:

"Today, we're illuminating the networks that power your daily life—from streaming your favorite show to connecting with loved ones across the globe."
"In this audio exploration of biology essentials, we'll uncover the living systems that influence everything from your morning jog to global ecosystems."
"Join us as we delve into historical insights that reshape how you view today's world—decoding the past to better navigate the present."
"Let's demystify business strategies that could transform your career or startup, drawing from the innovations shaping companies you use every day."
"We're exploring the physics behind everyday wonders, from the flight of a paper airplane to the stars guiding your GPS."
"Today, we bridge economic concepts to your wallet—uncovering why inflation hits your grocery bill and how understanding it empowers your financial choices."

INTRODUCE THE EXPERT
Keep it brief but vivid. Highlight why Dr. Jordan Fearson is credible—ideally in a colorful, memorable way, drawing from the section content to determine his specialized qualifications and experience. Incorporate a subtle reference to his Johannesburg background if it fits naturally with the topic, without stereotyping. Tailor the introduction to the section's theme, making the Expert's background feel relevant and inspiring. Avoid generic credentials; focus on unique, story-driven highlights.

Examples:

"Joining me is Dr. Jordan Fearson—a cybersecurity pioneer from Johannesburg who once traced a global breach to a single overlooked router in a bustling café."

"With us today is Dr. Jordan Fearson, a data science expert who's trained AI models to predict weather patterns, drawing from his roots in South Africa's diverse climates."

"Here to guide us is Dr. Jordan Fearson, a historian with decades of experience, who uncovered lost artifacts in Johannesburg archives that rewrote colonial narratives."

"Joining the conversation is Dr. Jordan Fearson, a biology specialist from Johannesburg, renowned for engineering microbes that combat local water crises—yes, he turned bacteria into heroes."

"With me is Dr. Jordan Fearson, an economics guru who's modeled market shifts, once forecasting a boom from his Johannesburg vantage point on emerging African economies."

"Today, we're with Dr. Jordan Fearson, a physics innovator who built simulations of cosmic events, inspired by stargazing over Johannesburg's night skies."”

VALUE PROMISE – WHY THIS MATTERS
Explain what listeners will gain from the episode, connecting it to real-world relevance, personal growth, or practical applications in their daily lives. Make it inspiring and specific to the section's content, showing how the knowledge empowers or enlightens them

Examples:

"By the end of our discussion, you'll grasp how networks underpin your connected world, empowering you to troubleshoot everyday tech glitches like a pro."
"We'll unpack the secrets of cellular biology, revealing why understanding DNA could help you make informed health choices in your own life."
"Through this exploration, you'll gain tools to analyze historical events, helping you spot patterns in today's news that shape global decisions."
"Expect to walk away with strategies for effective leadership, transforming how you navigate team dynamics at work or in your community."
"We'll demystify quantum physics basics, showing how these principles drive innovations like your smartphone—and spark wonder about the universe around you."
"By episode's close, you'll understand economic cycles, equipping you to make smarter financial moves amid rising costs in your daily budget."


SECTION ROADMAP
Preview what’s coming in the episode—but keep it light, engaging, and story-like. Avoid rigid academic phrasing; instead, use conversational language that builds excitement. Base the roadmap on the major concepts or subheadings in the section content, limiting to 3-5 key stops for brevity

Examples:

"Here's our roadmap for today: We'll kick off by unpacking the essentials of networking, then dive into how data travels the digital highways, and wrap up with tips to secure your own connections."
"Let's outline our journey: Starting with the building blocks of cells, we'll explore how they communicate, zoom in on a fascinating real-life example from nature, and end with ways this knowledge applies to health breakthroughs."
"Think of this episode as a time-travel tour: First, we'll set the scene in ancient times, then dissect a pivotal event, and finally connect those dots to modern society with actionable insights."
"Our plan is simple and exciting: We'll begin with core business principles, dig into a success story from the real world, and finish with practical strategies you can apply to your ventures right away."
"Get ready for a cosmic adventure: We'll start at the atomic level, venture into quantum weirdness, examine a mind-bending experiment, and conclude with how it powers everyday tech."
"Here's how we'll navigate economics today: Opening with fundamental concepts, moving to analyze market trends through a current case, and closing with tools to make informed decisions in your financial life."

HAND-OFF TO EXPERT
End the intro with a smooth, natural transition that invites Dr. Jordan Fearson into the conversation. Use an engaging question or cue tied to the section's core topic, building on the hook, roadmap, or value promise for seamless flow.

Examples:

"Alright, let's dive right in. Dr. Fearson, kick us off with the fundamentals: what makes up the backbone of modern networking?"
"Okay, Jordan, let's break it down—starting with the basics: how do cells really work together in the human body?"
"With that roadmap in mind, Dr. Fearson, take us back to the beginning: what sparked the key events in this historical era?"
"Jordan, help us unpack this—let's start with the essentials: how does effective leadership drive business success in today's world?"
"Dr. Fearson, over to you for the deep dive: what are the core principles behind quantum mechanics, and why do they matter?"
"Alright, let's get to the heart of it. Jordan, walk us through the basics: how do economic cycles influence our everyday decisions?"

2. **MAIN SEGMENTS**  3 - 6 min total (scale with content)  
   Create one main segment per major concept or subheading in the source content, scaling the total to 5–10 minutes (approximately 800–1,600 words, adjusting based on complexity). Follow this conversational flow strictly: Host cue (a question or prompt to set up the concept) → Expert explanation (clear, insightful overview) → Host follow-up or analogy (to clarify or relate) → Expert deeper dive or story (with examples or anecdotes). Insert at least one relatable analogy, mini-story, quick quiz ('Pop quiz: what happens if…?'), and real-world application per segment. Use callbacks to earlier points for reinforcement ('Remember the firewall metaphor? That’s relevant here…'). Ensure natural alternation, with no speaker exceeding 4-5 sentences per turn, and vary energy with humor or rhetorical questions. Map segments directly to the htmlContent's structure (e.g., use HTML headings as segment breaks). For dense topics, add extra quizzes/stories; for basics, keep concise. Maintain audio-friendliness by describing any visuals verbally.

Examples:

**Networking Basics Segment:**

[Host]: Dr. Fearson, let's start with the fundamentals—what is a computer network, anyway?

[Expert]: At its core, a computer network is a group of interconnected devices that share resources and communicate, like computers, servers, and routers linked via cables or wireless signals. This allows data to flow between them, enabling everything from email to streaming.

[Host]: That sounds straightforward, but can you give us an analogy? It's like a busy highway system, right, where data packets are cars zipping to destinations?

[Expert]: Exactly—imagine Johannesburg's bustling streets during rush hour: data packets are vehicles navigating lanes (protocols), avoiding traffic jams (congestion). Let me dive deeper with a story. Back in my early days in South Africa, I helped set up a network for a local school; we connected 50 computers, and suddenly kids could access global resources. Pop quiz: What happens if a 'roadblock' like a faulty router occurs? The whole system slows down. In real life, this applies to your home Wi-Fi—understanding networks helps you fix dropouts during video calls. Remember our earlier hook about invisible connections? That's the foundation here.

**Cellular Biology Segment:**

[Host]: Okay, Dr. Fearson, shifting to cells—how do they function as the building blocks of life?

[Expert]: Cells are the smallest units of life, containing organelles like the nucleus for DNA and mitochondria for energy production. They perform tasks like metabolism and reproduction to keep organisms alive.

[Host]: To make it relatable, it's like a tiny factory, with workers (proteins) assembling products on assembly lines?

[Expert]: Spot on—like a Johannesburg market stall, bustling with vendors (enzymes) trading goods. For a deeper dive, recall the story of Rosalind Franklin's X-ray work that revealed DNA's structure, revolutionizing biology. Quick quiz: What if a cell's 'power plant' (mitochondria) fails? Energy crashes, leading to fatigue. This ties to real-world health apps, like how understanding cells helps treat diseases like diabetes. And callback to our intro on living systems—this is where it all starts.

**Historical Events Segment:**

[Host]: Dr. Fearson, let's unpack the causes of World War I—what sparked it all?

[Expert]: The war stemmed from nationalism, alliances, imperialism, and militarism, culminating in the assassination of Archduke Franz Ferdinand in 1914, which triggered a chain reaction.

[Host]: That's intense; analogy-wise, it's like a powder keg waiting for a spark, with countries as intertwined dominos?

[Expert]: Precisely—like a tense Johannesburg township negotiation escalating from one incident. Diving deeper, think of the mini-story of the Black Hand group plotting the assassination, driven by Serbian nationalism. Pop quiz: What happens if one alliance pulls out? The whole balance shifts. In today's world, this applies to understanding global conflicts, like in diplomacy simulations. Remember our roadmap's time-travel tour? This is the pivotal event we mentioned.

3. **MICRO-RECAP** after every 2-3 segments  
   Insert a micro-recap after every 2-3 main segments (adjust based on content flow, e.g., after logical breaks in the htmlContent). This should be a single, concise line (1-2 sentences, ~20-40 words) delivered by the Host, summarizing key takeaways from the preceding segments and teasing the next with an engaging question or invitation. Ensure it's tied directly to the covered concepts for accuracy, and vary phrasing to keep it fresh and energetic. Use this to reinforce learning with callbacks to analogies/stories from prior segments. If the script has fewer than 4 segments, include at least one recap midway.


4. **CONCLUSION**  ≈ 45–60 sec  
   • Rapid fire recap of key takeaways (Host).  
   • One actionable tip or mnemonic (Expert).  
   • Tease the next lesson / CTA (“Join us next time for … ; meanwhile, try implementing …”).  
   • Friendly sign-off (“Stay curious and stay secure!”).

Target word count: 750 – 1,500 (≈5–10 min at 150 words per minute narration speed).
Spend extra time on complex concepts (e.g., add more analogies/stories); be concise on basics to maintain pacing. You are allowed to go over the allocated time if the content demands it, but avoid unnecessary filler.

────────────────────────────────────────────────────────────────
💡 **BEST-PRACTICE RULES**

• **Conversational rhythm:** Alternate turns naturally; never let one speaker talk for more than ~5 sentences without the other jumping in.  
• **Clarity first:** Define jargon in-line when first used (“APT – that’s an Advanced Persistent Threat, basically …”).  
• **Relatability:** At least one *personal anecdote* or *pop-culture analogy* per major segment.  
• **Audio friendliness:** Describe visuals verbally (“Picture a three-layer cake: …”). No “as you can see”.  
• **Reinforcement:** Host occasionally asks recall questions (“Quick check, why is confidentiality different from integrity?”).  
• **Energy management:** Vary pace with rhetorical questions, humour, and short pauses (“<laughs>”, “*beat*”).  
• **Consistency HARD RULES:**  
  – Only [Host] and [Expert] labels, exact spelling & casing.  
  – Do NOT add or rename speakers.  
  – No markdown, headings, or code fences.

Return **only** the finished script, nothing else.
`;

    const requestBody = {
      model: "gpt-5",
      messages: [
        {
          role: "user",
          content: userMessage,
        },
      ],
      max_completion_tokens: 15500,
    };

    return requestBody;
  } catch (error) {
    console.error("Error generating tutor script prompt:", error.message);
    throw new Error(`Failed to generate tutor script prompt: ${error.message}`);
  }
};
/**
 * Generates a prompt to process a tutor podcast script, assigning voices and speech instructions for each line.
 * Uses function-calling with gpt-4.1-2025-04-14 model to ensure structured JSON output.
 *
 * @param {Object} scriptData - Output from createTutorScriptPrompt { script: string, speakers: string[] }
 * @returns {Promise<object>} Request payload for the AI model with function-calling.
 * @throws {Error} If input is invalid or payload generation fails.
 */
export const createScriptWithVoicesPrompt = async (scriptData) => {
  try {
    // Validate input
    if (!scriptData || typeof scriptData !== "object") {
      throw new Error("Invalid or missing scriptData");
    }
    const { script, speakers } = scriptData;
    if (!script || typeof script !== "string") {
      throw new Error("Invalid or missing script");
    }
    if (!Array.isArray(speakers) || speakers.length < 2) {
      throw new Error(
        "Invalid or missing speakers array; at least two speakers required",
      );
    }

    const userMessage =
      "You are an expert script processor tasked with transforming a tutor podcast script into a line-by-line JSON format for audio generation in a Learning Management System (LMS). The script contains dialog between multiple speakers, and you must assign a unique voice to each speaker from a provided catalog and set speech style instructions for each line.\n\n" +
      "**INPUT SCRIPT DATA**:\n" +
      "- Script:\n" +
      "```\n" +
      "${script}\n" +
      "```\n" +
      "- Speakers: ${JSON.stringify(speakers)}\n\n" +
      "**VOICE CATALOG**:\n" +
      "| Voice       | Gender / Vibe (approx.)     | Description                                   | Typical Use-Case                |\n" +
      "|-------------|-----------------------------|-----------------------------------------------|---------------------------------|\n" +
      "| alloy       | Warm adult male             | Friendly radio-host baritone, neutral US accent | Tutorials, corporate explainers |\n" +
      "| echo        | Deep calm male              | Low-pitch, measured, slightly “movie-trailer” | Narration, meditation apps      |\n" +
      "| fable       | Story-teller female         | Mid-pitch, expressive, soft consonants        | Bedtime stories, audiobooks     |\n" +
      "| onyx        | Authoritative male          | Firm, resonant, newsroom-style                | News-reader, security alerts    |\n" +
      "| nova        | Bright energetic female     | Upbeat, slightly higher pitch                 | Podcasts, marketing spiels      |\n" +
      "| shimmer     | Youthful / androgynous      | Light, friendly, almost teen-radio            | Casual chats, in-app assistants |\n\n" +
      "**SPEECH STYLE INSTRUCTIONS**:\n" +
      "Each line must have its own instructions for speech style, using free-form strings like:\n" +
      "| Instruction String                  | Effect                              |\n" +
      "|------------------------------------|-------------------------------------|\n" +
      "| cheerful, upbeat, medium pace      | Smiling voice, quicker cadence      |\n" +
      "| calm and reassuring, slow          | Softer volume, slower words         |\n" +
      "| whisper softly                     | Noticeable breathiness, low volume  |\n" +
      "| excited, speak 20 % faster         | Higher pitch, faster tempo          |\n" +
      "| dramatic, movie-trailer style      | Deeper tone, pauses for effect      |\n" +
      "| sad, speak in a low voice          | Lower pitch, subdued energy         |\n" +
      "| shout the words like an announcer  | Louder amplitude, emphasis          |\n" +
      "| sarcastic tone                     | Drawn-out vowels, ironic lilt       |\n" +
      "| robotic monotone                   | Flatter prosody, minimal inflection |\n" +
      "| sing this like a lullaby           | Attempts melodic intonation         |\n\n" +
      "**YOUR TASK**:\n" +
      "1. Parse the script into individual lines, identifying the speaker (e.g., [Tutor], [Co-Host]) and text for each line using the format `[Speaker]: Text`.\n" +
      "2. Assign a unique voice to each speaker from the voice catalog, ensuring:\n" +
      "   - Consistency: Each speaker uses the same voice throughout (e.g., Tutor always uses `nova`).\n" +
      "   - Suitability: Choose voices for podcast-style tutorials (e.g., `nova` for Tutor, `alloy` for Co-Host for energetic and warm contrast).\n" +
      "3. Assign speech style instructions for each line based on content or context:\n" +
      "   - Default to `cheerful, upbeat, medium pace` for introductions, explanations, and banter.\n" +
      "   - Use `excited, speak 20% faster` for questions or engaging moments.\n" +
      "   - Use `calm and reassuring, slow` for technical or complex explanations.\n" +
      "   - Use `dramatic, movie-trailer style` for conclusions or emphasis.\n" +
      "4. Generate a JSON object with:\n" +
      "   - `voiceAllocation`: Array of objects mapping each speaker to a voice.\n" +
      "   - `script`: Array of line entries with `character`, `line`, `instructions`, and `sequence`.\n" +
      "5. Return **ONLY valid JSON** via the `output_script_with_voices` function call. No extra text, commentary, or markdown.\n\n" +
      "**Output Structure**:\n" +
      "{\n" +
      '  "voiceAllocation": [\n' +
      "    {\n" +
      '      "character": string,\n' +
      '      "voice": string\n' +
      "    }\n" +
      "  ],\n" +
      '  "script": [\n' +
      "    {\n" +
      '      "character": string,\n' +
      '      "line": string,\n' +
      '      "instructions": string,\n' +
      '      "sequence": integer\n' +
      "    }\n" +
      "  ]\n" +
      "}\n\n" +
      "**Example Input**:\n" +
      '- Script: "[Tutor]: Hey everyone, welcome to today’s lesson!\\n[Co-Host]: Yo, what’s up, listeners?"\n' +
      '- Speakers: ["Tutor", "Co-Host"]\n\n' +
      "**Example Output**:\n" +
      "{\n" +
      '  "voiceAllocation": [\n' +
      "    {\n" +
      '      "character": "Tutor",\n' +
      '      "voice": "nova"\n' +
      "    },\n" +
      "    {\n" +
      '      "character": "Co-Host",\n' +
      '      "voice": "alloy"\n' +
      "    }\n" +
      "  ],\n" +
      '  "script": [\n' +
      "    {\n" +
      '      "character": "Tutor",\n' +
      '      "line": "Hey everyone, welcome to today’s lesson!",\n' +
      '      "instructions": "cheerful, upbeat, medium pace",\n' +
      '      "sequence": 1\n' +
      "    },\n" +
      "    {\n" +
      '      "character": "Co-Host",\n' +
      '      "line": "Yo, what’s up, listeners?",\n' +
      '      "instructions": "excited, speak 20% faster",\n' +
      '      "sequence": 2\n' +
      "    }\n" +
      "  ]\n" +
      "}\n\n" +
      "**Instructions**:\n" +
      "- Parse lines using the format `[Speaker]: Text`.\n" +
      "- Assign voices from the catalog, ensuring consistency and suitability for tutorials.\n" +
      "- Set instructions based on line content (e.g., introductions use `cheerful, upbeat, medium pace`).\n" +
      "- Use strictly increasing `sequence` numbers (1, 2, 3, ...).\n" +
      "- Validate speakers against the provided `speakers` array.\n" +
      "- Return JSON via `output_script_with_voices` function call.\n";

    // Define the function schema for function-calling
    const functionSchema = {
      name: "output_script_with_voices",
      description:
        "Returns a JSON object with voice assignments and line-by-line script with speech instructions.",
      parameters: {
        type: "object",
        properties: {
          voiceAllocation: {
            type: "array",
            items: {
              type: "object",
              properties: {
                character: { type: "string" },
                voice: {
                  type: "string",
                  enum: ["alloy", "echo", "fable", "onyx", "nova", "shimmer"],
                },
              },
              required: ["character", "voice"],
            },
          },
          script: {
            type: "array",
            items: {
              type: "object",
              properties: {
                character: { type: "string" },
                line: { type: "string" },
                instructions: { type: "string" },
                sequence: { type: "integer" },
              },
              required: ["character", "line", "instructions", "sequence"],
            },
          },
        },
        required: ["voiceAllocation", "script"],
      },
    };

    const requestBody = {
      model: "gpt-4.1-2025-04-14",
      messages: [
        {
          role: "user",
          content: userMessage,
        },
      ],
      functions: [functionSchema],
      function_call: { name: "output_script_with_voices" },
      max_completion_tokens: 8500, // Supports parsing and processing long scripts
    };

    return requestBody;
  } catch (error) {
    console.error("Error generating script with voices prompt:", error.message);
    throw new Error(
      `Failed to generate script with voices prompt: ${error.message}`,
    );
  }
};
/**
 * Generates a prompt for creating audio for a single script line using OpenAI's TTS model.
 *
 * @param {Object} lineData - { character, text, instructions, sequence, voice }
 * @returns {Promise<object>} Request payload for the AI audio model.
 * @throws {Error} If input is invalid or payload generation fails.
 */
export const createTutorAudioPrompt = async (lineData) => {
  try {
    if (!lineData || typeof lineData !== "object") {
      throw new Error("Invalid or missing lineData");
    }
    const { character, text, instructions, sequence, voice } = lineData;
    if (
      !character ||
      typeof character !== "string" ||
      !text ||
      typeof text !== "string" ||
      !instructions ||
      typeof instructions !== "string" ||
      !Number.isInteger(sequence) ||
      !voice ||
      typeof voice !== "string"
    ) {
      throw new Error(
        "Invalid lineData: missing/invalid character, text, instructions, sequence, or voice",
      );
    }

    // The Audio Speech API doesn't have an `instructions` field.
    // Fold your style into the input so the voice delivers it appropriately.
    const styledInput = `[${character}] (${instructions}) ${text}`;

    // IMPORTANT:
    // 1) Keep endpoint so the CF knows which route to take
    // 2) Put `model` (and the other audio params) at TOP LEVEL,
    //    because your CF checks only `model` or `responses.model`.
    return {
      endpoint: "audio.speech",
      model: "gpt-4o-mini-tts",
      voice,
      input: styledInput,
      format: "mp3",
    };
  } catch (error) {
    console.error("Error generating audio prompt:", error.message);
    throw new Error(`Failed to generate audio prompt: ${error.message}`);
  }
};

/**
 * Generates a prompt for allocating unique voices to speakers in a podcast script.
 * Analyzes the script to identify speakers, their character, and assigns distinct voices.
 *
 * @param {string} script - The podcast script with speaker labels (e.g., "[Tutor]: ...").
 * @returns {Promise<object>} Request payload for the AI model with function-calling.
 * @throws {Error} If input is invalid or payload generation fails.
 */
export const createSpeakerVoiceAllocationPrompt = async (script) => {
  try {
    // Validate input
    if (!script || typeof script !== "string" || !script.trim()) {
      throw new Error("Invalid or missing script");
    }

    const userMessage = `
You are an expert script analyst tasked with allocating unique voices to speakers in a podcast script for a Learning Management System (LMS). The script contains conversational, educational dialogue with speaker labels (e.g., [Tutor]:, [Co-Host]:, [Guest 1]:). Your job is to identify unique speakers, analyze their character based on dialogue, and assign a distinct voice to each speaker from the provided catalog.

**SCRIPT TO ANALYZE**:
${script}

**VOICE CATALOG**:
| Voice   | Gender / Vibe (approx.) | Description                                   | Typical Use-Case                |
|---------|-------------------------|-----------------------------------------------|---------------------------------|
| alloy   | Warm adult male         | Friendly radio-host baritone, neutral US accent | Tutorials, corporate explainers |
| echo    | Deep calm male          | Low-pitch, measured, slightly “movie-trailer” | Narration, meditation apps      |
| fable   | Story-teller female     | Mid-pitch, expressive, soft consonants        | Bedtime stories, audiobooks     |
| onyx    | Authoritative male      | Firm, resonant, newsroom-style                | News-reader, security alerts    |
| nova    | Bright energetic female | Upbeat, slightly higher pitch                 | Podcasts, marketing spiels      |
| shimmer | Youthful / androgynous  | Light, friendly, almost teen-radio            | Casual chats, in-app assistants |

**YOUR TASK**:
1. **Identify Unique Speakers**:
   - Parse the script to extract all unique speaker labels (e.g., Tutor, Co-Host, Guest 1, Speaker 1).
   - Count the total number of unique speakers.

2. **Analyze Speaker Character**:
   - For each speaker, analyze their dialogue to determine:
     - **Role**: Primary educator (e.g., Tutor), engaging sidekick (e.g., Co-Host), or guest contributor (e.g., Guest 1).
     - **Language Style**: Tone (e.g., authoritative, friendly, curious), complexity (e.g., technical, conversational), and vibe (e.g., upbeat, calm).
     - **Context**: Contribution type (e.g., explaining concepts, asking questions, adding humor).

3. **Assign Unique Voices**:
   - Assign a voice from the catalog to each speaker based on their role, style, and context:
     - **Tutor**: Often knowledgeable and clear (e.g., alloy for tutorials, onyx for technical topics).
     - **Co-Host**: Engaging and conversational (e.g., nova for upbeat energy, shimmer for casual chats).
     - **Guests**: Match to their vibe (e.g., fable for expressive storytelling, echo for calm insights).
   - Ensure **each speaker has a unique voice** (e.g., no two speakers can use alloy).
   - If there are more speakers than available voices (6), return an error JSON.
   - If there are 2 speakers ensure that one speaker is female and the other is male. We promote the male and female voice combination as far as possible

4. **Output**:
   - **Success**: Return a JSON object with a voiceAllocation array, each entry containing:
     - character: The speaker’s name (e.g., Tutor).
     - voice: The assigned voice from the catalog (e.g., alloy).
   - **Error**: If the number of speakers exceeds 6, return { "error": "Too many speakers: [count] speakers exceed available voices (6)" }.

**Output Structure**:
- Success:
{
  "voiceAllocation": [
    { "character": "Speaker name (e.g., Tutor)", "voice": "Voice from catalog (e.g., alloy)" },
    ...
  ]
}
- Error:
{ "error": "Too many speakers: [count] speakers exceed available voices (6)" }

**Example Output**:
- Success:
{
  "voiceAllocation": [
    { "character": "Tutor", "voice": "alloy" },
    { "character": "Co-Host", "voice": "nova" },
    { "character": "Guest 1", "voice": "shimmer" }
  ]
}
- Error:
{ "error": "Too many speakers: 7 speakers exceed available voices (6)" }

**Instructions**:
- Identify **all unique speakers** by their labels in the script.
- Analyze the **character and dialogue** to determine the most suitable voice for each speaker.
- Assign **distinct voices** to each speaker, ensuring no voice (e.g., alloy, nova) is reused.
- If there are more than 6 speakers, return the error JSON.
- Return **only valid JSON** via the function call. No extra text, commentary, or markdown.
    `;

    // Define the function schema for function-calling
    const functionSchema = {
      name: "output_speaker_voice_allocation",
      description:
        "Returns a JSON object with unique voice allocations for podcast script speakers.",
      parameters: {
        type: "object",
        properties: {
          voiceAllocation: {
            type: "array",
            items: {
              type: "object",
              properties: {
                character: {
                  type: "string",
                  description: "The speaker’s name (e.g., Tutor, Co-Host)",
                },
                voice: {
                  type: "string",
                  enum: ["alloy", "echo", "fable", "onyx", "nova", "shimmer"],
                  description: "The assigned voice from the catalog",
                },
              },
              required: ["character", "voice"],
            },
          },
          error: {
            type: "string",
            description: "Error message if too many speakers",
          },
        },
        oneOf: [{ required: ["voiceAllocation"] }, { required: ["error"] }],
      },
    };

    const requestBody = {
      model: "o3-mini",
      messages: [
        {
          role: "user",
          content: userMessage,
        },
      ],
      functions: [functionSchema],
      function_call: { name: "output_speaker_voice_allocation" },
      max_completion_tokens: 8500, // Sufficient for JSON output
    };

    return requestBody;
  } catch (error) {
    console.error(
      "Error generating speaker voice allocation prompt:",
      error.message,
    );
    throw new Error(
      `Failed to generate speaker voice allocation prompt: ${error.message}`,
    );
  }
};

/**
 * Generates a prompt to process a tutor podcast script into a line-by-line JSON format with speech instructions.
 * Uses function-calling to ensure structured JSON output.
 *
 * @param {Object} inputData - Contains script text and voice allocation JSON.
 * @param {string} inputData.script - The podcast script with speaker labels (e.g., "[Tutor]: ...").
 * @param {Object} inputData.voiceAllocation - Voice allocation JSON from createSpeakerVoiceAllocationPrompt.
 * @returns {Promise<object>} Request payload for the AI model with function-calling.
 * @throws {Error} If input is invalid or payload generation fails.
 */
export const createScriptLineInstructionsPrompt = async (inputData) => {
  try {
    // Validate input
    if (!inputData || typeof inputData !== "object") {
      throw new Error("Invalid or missing inputData");
    }
    const { script, voiceAllocation } = inputData;
    if (!script || typeof script !== "string" || !script.trim()) {
      throw new Error("Invalid or missing script");
    }
    if (
      !voiceAllocation ||
      !Array.isArray(voiceAllocation.voiceAllocation) ||
      voiceAllocation.voiceAllocation.length < 2
    ) {
      throw new Error(
        "Invalid or missing voiceAllocation; at least two speakers required",
      );
    }

    const userMessage = `
You are an expert script processor tasked with transforming a tutor podcast script into a line-by-line JSON format for audio generation in a Learning Management System (LMS). The script contains conversational, educational dialogue with speaker labels (e.g., [Tutor]:, [Co-Host]:). You are provided with a voice allocation JSON mapping each speaker to a unique voice. Your job is to parse the script into individual lines and assign contextually appropriate speech instructions for each line.

**INPUT DATA**:
- Script:
${script}

- Voice Allocation:
${JSON.stringify(voiceAllocation, null, 2)}

**VOICE CATALOG** (for context):
| Voice   | Gender / Vibe (approx.) | Description                                   | Typical Use-Case                |
|---------|-------------------------|-----------------------------------------------|---------------------------------|
| alloy   | Warm adult male         | Friendly radio-host baritone, neutral US accent | Tutorials, corporate explainers |
| echo    | Deep calm male          | Low-pitch, measured, slightly "movie-trailer" | Narration, meditation apps      |
| fable   | Story-teller female     | Mid-pitch, expressive, soft consonants        | Bedtime stories, audiobooks     |
| onyx    | Authoritative male      | Firm, resonant, newsroom-style                | News-reader, security alerts    |
| nova    | Bright energetic female | Upbeat, slightly higher pitch                 | Podcasts, marketing spiels      |
| shimmer | Youthful / androgynous  | Light, friendly, almost teen-radio            | Casual chats, in-app assistants |

**SPEECH STYLE INSTRUCTIONS**:
Assign instructions for each line based on its content, context, and speaker’s role, using free-form strings like:
| Instruction String                  | Effect                              |
|------------------------------------|-------------------------------------|
| cheerful, professional, medium pace | Smiling voice, clear delivery       |
| youthful, energetic, casual         | Upbeat, informal, lively cadence    |
| calm, reassuring, slow             | Softer volume, deliberate pace      |
| excited, fast-paced                | Higher pitch, quicker tempo         |
| dramatic, emphatic                 | Strong emphasis, pauses for effect  |
| curious, questioning               | Rising intonation, inquisitive tone |
| humorous, playful                  | Light-hearted, with a chuckle       |
| serious, technical                 | Precise, focused, steady pace       |

**YOUR TASK**:
1. Parse the script into individual lines, identifying the speaker and text for each line using the format [Speaker]: Text.
2. Validate that each speaker matches an entry in the provided voiceAllocation JSON.
3. Assign speech instructions for each line based on:
   - **Content**: E.g., introductions use "cheerful, professional, medium pace", questions use "curious, questioning", technical explanations use "serious, technical".
   - **Context**: Consider the speaker’s role (e.g., Tutor as educator, Co-Host as engaging sidekick) and the line’s purpose (e.g., banter, explanation, conclusion).
   - **Voice Allocation**: Align instructions with the assigned voice’s vibe (e.g., nova with "youthful, energetic, casual", onyx with "serious, technical").
4. Generate a JSON array where each entry contains:
   - "sequence": Incremental line number (1, 2, 3, ...).
   - "character": The speaker’s name (e.g., Tutor).
   - "text": The line’s text (excluding the [Speaker]: label).
   - "instructions": Speech style instruction (e.g., "cheerful, professional, medium pace").
5. Return **ONLY valid JSON** via the "output_script_lines" function call. No extra text, commentary, or markdown.

**Output Structure**:
[
  {
    "sequence": integer,
    "character": string,
    "text": string,
    "instructions": string
  },
  ...
]

**Example Input**:
- Script:
[Tutor]: Hey everyone, welcome to today’s lesson on Object-Oriented Programming! I’m Alex, your tutor, joined by my co-host, Jamie.
[Co-Host]: Yo, what’s good, listeners? Jamie here, ready to unpack this OOP thing. Alex, I gotta be honest—when I first heard “Object-Oriented Programming,” I thought it was, like, programming with a fancy attitude. *laughs* So, break it down for me—what’s OOP all about?

- Voice Allocation:
{
  "voiceAllocation": [
    { "character": "Tutor", "voice": "alloy" },
    { "character": "Co-Host", "voice": "nova" }
  ]
}

**Example Output**:
[
  {
    "sequence": 1,
    "character": "Tutor",
    "text": "Hey everyone, welcome to today’s lesson on Object-Oriented Programming! I’m Alex, your tutor, joined by my co-host, Jamie.",
    "instructions": "cheerful, professional, medium pace"
  },
  {
    "sequence": 2,
    "character": "Co-Host",
    "text": "Yo, what’s good, listeners? Jamie here, ready to unpack this OOP thing. Alex, I gotta be honest—when I first heard “Object-Oriented Programming,” I thought it was, like, programming with a fancy attitude. *laughs* So, break it down for me—what’s OOP all about?",
    "instructions": "youthful, energetic, casual"
  }
]

**Instructions**:
- Parse lines using the format [Speaker]: Text.
- Validate speakers against the voiceAllocation JSON.
- Assign instructions based on content, context, and voice vibe (e.g., nova aligns with "youthful, energetic, casual").
- Use strictly increasing sequence numbers (1, 2, 3, ...).
- Ensure instructions are contextually appropriate, reflecting the line’s tone and purpose.
- Return JSON via "output_script_lines" function call.
    `;

    // Define the function schema for function-calling
    const functionSchema = {
      name: "output_script_lines",
      description:
        "Returns a JSON array with line-by-line script details and speech instructions.",
      parameters: {
        type: "array",
        items: {
          type: "object",
          properties: {
            sequence: {
              type: "integer",
              description: "Line number in the script (1-based)",
            },
            character: {
              type: "string",
              description: "The speaker’s name (e.g., Tutor, Co-Host)",
            },
            text: {
              type: "string",
              description: "The line’s text (excluding speaker label)",
            },
            instructions: {
              type: "string",
              description:
                "Speech style instructions (e.g., cheerful, professional, medium pace)",
            },
          },
          required: ["sequence", "character", "text", "instructions"],
        },
      },
    };

    const requestBody = {
      model: "o3-mini",
      messages: [
        {
          role: "user",
          content: userMessage,
        },
      ],
      functions: [functionSchema],
      function_call: { name: "output_script_lines" },
      max_completion_tokens: 15000,
    };

    return requestBody;
  } catch (error) {
    console.error(
      "Error generating script line instructions prompt:",
      error.message,
    );
    throw new Error(
      `Failed to generate script line instructions prompt: ${error.message}`,
    );
  }
};

// AICalls.js
// AICalls.js
export function createSpeechStylePrompt2(input) {
  // ── validation ─────────────────────────────────────────────
  if (
    !input ||
    !Array.isArray(input.lines) ||
    input.lines.length === 0 ||
    !input.voiceAllocation ||
    !Array.isArray(input.voiceAllocation.voiceAllocation)
  ) {
    throw new Error("createSpeechStylePrompt2: invalid input");
  }

  const { lines, voiceAllocation } = input;

  // Keep payload compact but explicit (no instructions yet)
  const scriptForModel = lines.map(({ sequence, character, text }) => ({
    sequence,
    character,
    text,
  }));

  const userMessage = String.raw`
You are a senior dialogue director for a high-production educational podcast.

TASK (INSTRUCTIONS-ONLY)
For EACH input line, produce a short delivery "instructions" string to guide TTS.
• Keep it 3–10 words, comma-separated: tone + energy + pace.
• Examples: "cheerful, professional, medium pace", "curious, questioning, mid-fast".

HARD RULES
1) DO NOT change, reorder, add, or remove lines.
2) Return ONLY JSON with this exact shape:
{
  "instructions": [
    { "sequence": <int>, "instructions": "<short string>" }
  ]
}
3) Every returned sequence MUST match an input line's sequence exactly.
4) One instruction PER input line. Counts MUST match.

SHOW-VIBE CHEAT-SHEET (for inspiration)
- Cold-open / Hook: energetic, inviting
- Host intro: warm, confident, professional
- Co-host question: curious, questioning
- Deep explain: serious, technical, steady
- Story / anecdote: storytelling, animated
- Humorous aside: playful
- Key takeaway: emphatic, encouraging, medium-slow
- CTA outro: upbeat, motivational

Descriptor lexicon (mix 2–4 + optional tempo):
cheerful · professional · youthful · energetic · casual · serious · technical ·
dramatic · emphatic · calm · measured · questioning · excited · fast-paced ·
reassuring · playful · storytelling · motivational · slow · medium pace · mid-fast · fast

──────────────── INPUT A · SCRIPT LINES (READ-ONLY) ────────────────
${JSON.stringify(scriptForModel, null, 2)}

──────────────── INPUT B · VOICE ALLOCATION (READ-ONLY) ────────────
${JSON.stringify(voiceAllocation, null, 2)}

OUTPUT STRICTLY:
{
  "instructions": [
    { "sequence": 1, "instructions": "cheerful, professional, medium pace" }
  ]
}
`.trim();

  // Responses payload (correct fields for JSON output)
  return {
    endpoint: "responses",
    responses: {
      model: "gpt-5-mini-2025-08-07",
      // Single-turn input is fine; a plain string is also accepted by the Responses API
      input: userMessage,
      reasoning: { effort: "low" },
      text: {
        verbosity: "low",
        // IMPORTANT: object form, not string; no legacy response_format
        format: { type: "json_object" },
      },
      // Plenty for ~90 short instruction strings
      max_output_tokens: 9500,
    },
  };
}

/*****************************************************************
 *  createSpeechStylePrompt
 *  --------------------------------------------------------------
 *  Adds a short “instructions” string to every line of an already
 *  sequenced podcast-script array so the TTS engine knows how to
 *  perform it.
 *
 *  ▸ INPUT
 *    {
 *      lines: [           // [{ sequence, character, text }]
 *        { sequence: 1, character: "Host",    text: "…" },
 *        { sequence: 2, character: "Co-Host", text: "…" }
 *      ],
 *      voiceAllocation: { // output of your voice-allocation step
 *        "voiceAllocation": [
 *          { "character": "Host",    "voice": "alloy" },
 *          { "character": "Co-Host", "voice": "nova" }
 *        ]
 *      }
 *    }
 *
 *  ▸ RETURNS  (function-call, JSON only)
 *    {
 *      "lines": [
 *        { sequence: 1, character: "Host",    text: "…", instructions: "cheerful, professional, medium pace" },
 *        { sequence: 2, character: "Co-Host", text: "…", instructions: "youthful, energetic, casual" }
 *      ]
 *    }
 *****************************************************************/
// AICalls.js (or wherever this lives)
export function createSpeechStylePrompt(input) {
  // ── validation ─────────────────────────────────────────────
  if (
    !input ||
    !Array.isArray(input.lines) ||
    input.lines.length === 0 ||
    !input.voiceAllocation ||
    !Array.isArray(input.voiceAllocation.voiceAllocation)
  ) {
    throw new Error("createSpeechStylePrompt: invalid input");
  }

  const { lines, voiceAllocation } = input;

  // ── USER MESSAGE (asks model to return strict JSON { lines: [...] }) ──
  const userMessage = String.raw`
You are a **senior dialogue director** for a high-production educational podcast.
You will receive a **final, lock-sequenced script** (read-only) and a **voice allocation** map.

### TASK
Add an **"instructions"** field to **every object** in the script to guide TTS delivery.
Keep instructions **short** (3–10 words) and **comma-separated** describing tone + energy + pace.
Examples: "cheerful, professional, medium pace", "curious, questioning, mid-fast".

### HARD RULES
1) Do **not** change, reorder, add, or remove objects or fields other than "instructions".
2) Return a single JSON object: **{ "lines": [ ... ] }**
3) Each item must include: **sequence (int), character (string), text (string), instructions (string)**.
4) Speaker → Voice mapping is read-only; just use it to inspire instructions.

### SHOW-VIBE CHEAT-SHEET (for inspiration)
- Cold-open / Hook: energetic, inviting
- Host intro: warm, confident, professional
- Co-host question: curious, questioning
- Deep explain: serious, technical, steady
- Story / anecdote: storytelling, animated
- Humorous aside: playful
- Key takeaway: emphatic, encouraging, medium-slow
- CTA outro: upbeat, motivational

Descriptor lexicon (mix 2–4 + optional tempo):
cheerful · professional · youthful · energetic · casual · serious · technical ·
dramatic · emphatic · calm · measured · questioning · excited · fast-paced ·
reassuring · playful · storytelling · motivational · slow · medium pace · mid-fast · fast

──────────────── INPUT A · SCRIPT LINES (READ-ONLY) ────────────────
${JSON.stringify(lines, null, 2)}

──────────────── INPUT B · VOICE ALLOCATION (READ-ONLY) ────────────
${JSON.stringify(voiceAllocation, null, 2)}

### OUTPUT FORMAT (STRICT)
Return **only** valid JSON with this shape:
{
  "lines": [
    {
      "sequence": 1,
      "character": "Host",
      "text": "…unchanged…",
      "instructions": "cheerful, professional, medium pace"
    }
  ]
}
`.trim();

  // ── RESPONSES API payload ──────────────────────────────────
  return {
    endpoint: "responses",
    responses: {
      model: "gpt-5-mini-2025-08-07",
      // canonical Responses input shape
      input: [
        {
          role: "user",
          content: [{ type: "input_text", text: userMessage }],
        },
      ],
      reasoning: { effort: "low" },
      // JSON mode belongs here (object, not string)
      text: {
        verbosity: "low",
        format: { type: "json_object" },
      },
      max_output_tokens: 4000,
    },
  };
}

/**
 * Simplify base structured HTML content for one outline item.
 * Target: LOWER complexity (foundation/beginner level, ~complexity 1–2).
 *
 * Input: baseHtmlContent = enriched HTML from /content_complexity_base
 * Output: HTML-only string, same tag whitelist, simpler language & pacing.
 */
export const createLowerComplexityFromBaseContentV1GPT5 = (
  programText,
  programName,
  unit,
  outcome,
  outcomeList,
  fullOutline,
  outlineItem,
  outcomeId,
  baseHtmlContent,
) => {
  // — sanitize (same pattern as other creators)
  const safeProgText = (programText || "").trim() || "No programme data";
  const safeProgName = (programName || "").trim() || "Unnamed Programme";

  const safeUnitName = (unit?.name || "").trim() || "Unnamed Unit";
  const unitSeq = Number.isFinite(unit?.sequence)
    ? unit.sequence
    : (console.warn("Missing unit.sequence, defaulting to 0"), 0);

  const safeOutcome = (outcome?.name || "").trim() || "Unnamed Outcome";
  const globalSeq = Number.isFinite(outcome?.globalSequence)
    ? outcome.globalSequence
    : "?";

  const safeList = (outcomeList || "• No outcomes").trim();

  const sectionTitle = outlineItem?.title?.trim() || "Untitled Section";
  const outlinePretty = JSON.stringify(fullOutline, null, 2);
  const safeOutcomeId = (outcomeId || "").trim() || "UNKNOWN_OUTCOME_ID";

  const safeBaseHtml =
    (baseHtmlContent || "").trim() ||
    "<p>No base content was provided for this outline item.</p>";

  const userMessage = `
You will **simplify** the existing structured HTML lesson for a single outline item.

Goal:
- Keep the **same concepts, structure, and allowed HTML tags**,
- But rewrite it so that it is **much easier to understand** for learners who:
  • Are beginners in this topic, and
  • Need more scaffolding, clearer language, and slower pacing.

Think of this as transforming complexity **3 → 1–2** on a 1–5 scale.

────────────────────────────────────────
PROGRAMME CONTEXT (for continuity only)
────────────────────────────────────────
Programme: "${safeProgName}"
Unit ${unitSeq}: "${safeUnitName}"
Outcome (globalSequence ${globalSeq}): "${safeOutcome}"
Outcomes in this unit (teaching order):
${safeList}

COMPLETE OUTCOME OUTLINE (contextual reference)
${outlinePretty}

────────────────────────────────────────
OUTLINE ITEM (the one you are simplifying)
────────────────────────────────────────
"${sectionTitle}"

────────────────────────────────────────
BASE HTML CONTENT (from /content_complexity_base)
────────────────────────────────────────
${safeBaseHtml}

────────────────────────────────────────
TRANSFORMATION GOAL – LOWER COMPLEXITY (FOUNDATION LEVEL)
────────────────────────────────────────
You must **rewrite** the lesson above into a clearer, more beginner-friendly version while:

1) PRESERVING SCOPE & MEANING
   • Do NOT introduce brand-new advanced topics.
   • Do NOT remove any **core concepts**; instead explain them more gently and clearly.
   • Keep the same **teaching focus** as the original: this is still content for
     the outline item "${sectionTitle}".

2) LOWERING COGNITIVE LOAD
   • Use shorter sentences and simpler vocabulary where possible.
   • Break long paragraphs into smaller, more digestible chunks.
   • Unpack jargon: briefly define technical terms in plain language the first time they appear.
   • Use more explicit signposts like “First…”, “Next…”, “In simple terms…”.

3) MORE SCAFFOLDING & SUPPORT
   • Add brief, concrete examples that make abstract ideas easier.
   • Add short “quick recap” sentences at natural break points (but do not create new sections).
   • When the original text jumps too quickly, insert one extra clarifying sentence
     that bridges the gap.

4) STRUCTURE & TAGS – KEEP THE SKELETON
   • Keep the **section structure** broadly the same:
     – Preserve the approximate sequence and hierarchy of headings (<h1–h5>).
     – Preserve the presence and intent of lists, tables, and highlighted blocks.
   • You MAY simplify list items and table text, but keep their role and intent.

   ALLOWED HTML (exact same whitelist as your LMS):
   • Headings: <h1>, <h2>, <h3>, <h4>, <h5>
   • Paragraphs: <p>
   • Lists: <ul>, <ol>, <li>
   • Code blocks (non-JS): <code>…</code>
   • Tables: <table>, <thead>, <tbody>, <tr>, <th>, <td>
   • Inline: <strong>, <em>, <span>
   • Custom LMS tags:
     <statement>, <separator>, <paragraphcard>, <executable>…</executable>

   ▸ Do NOT introduce any tags outside this whitelist.
   ▸ If the base content uses <statement>, <paragraphcard>, <separator>, or <executable>,
     you may keep them and simplify the text inside.

5) IMAGE HANDLING – PRESERVE EXACTLY
   • The base content may contain rich image tags like:
     <image>{"outcomeId":"${safeOutcomeId}", ...}</image>
   • You must **copy all <image> blocks exactly as they appear**:
     – Same JSON, same fields, same fileName, same outcomeId.
     – Do NOT change the image JSON, do NOT add new <image> tags,
       and do NOT remove existing <image> tags.
   • Only the surrounding explanatory text should change in complexity.

6) CODE & TECHNICAL CONTENT
   • If there are <code> or <executable> blocks, **do not change the code logic**.
   • You may simplify the explanatory text around the code, but keep the code itself intact
     and valid.

7) STYLE & TONE
   • Tone: calm, encouraging, and patient.
   • Assume an adult learner with basic literacy but little prior exposure to the topic.
   • Avoid highly compressed academic phrases; prefer clear, direct explanations.

────────────────────────────────────────
FINAL OUTPUT
────────────────────────────────────────
• Return **only** the simplified HTML lesson for this single outline item.
• Use **only** the allowed tags listed above.
• Do NOT include extra commentary, markdown, or explanations.
`.trim();

  return {
    endpoint: "responses",
    responses: {
      model: "gpt-5",
      input: [
        {
          role: "user",
          content: [{ type: "input_text", text: userMessage }],
        },
      ],
      reasoning: { effort: "high" },
      text: { verbosity: "high" },
      max_output_tokens: 16500,
    },
  };
};

/**
 * Intensify base structured HTML content for one outline item.
 * Target: HIGHER complexity (advanced/nuanced level, ~complexity 4–5).
 *
 * Input: baseHtmlContent = enriched HTML from /content_complexity_base
 * Output: HTML-only string, same tag whitelist, more depth & sophistication.
 */
export const createHigherComplexityFromBaseContentV1GPT5 = (
  programText,
  programName,
  unit,
  outcome,
  outcomeList,
  fullOutline,
  outlineItem,
  outcomeId,
  baseHtmlContent,
) => {
  // — sanitize (same pattern)
  const safeProgText = (programText || "").trim() || "No programme data";
  const safeProgName = (programName || "").trim() || "Unnamed Programme";

  const safeUnitName = (unit?.name || "").trim() || "Unnamed Unit";
  const unitSeq = Number.isFinite(unit?.sequence)
    ? unit.sequence
    : (console.warn("Missing unit.sequence, defaulting to 0"), 0);

  const safeOutcome = (outcome?.name || "").trim() || "Unnamed Outcome";
  const globalSeq = Number.isFinite(outcome?.globalSequence)
    ? outcome.globalSequence
    : "?";

  const safeList = (outcomeList || "• No outcomes").trim();

  const sectionTitle = outlineItem?.title?.trim() || "Untitled Section";
  const outlinePretty = JSON.stringify(fullOutline, null, 2);
  const safeOutcomeId = (outcomeId || "").trim() || "UNKNOWN_OUTCOME_ID";

  const safeBaseHtml =
    (baseHtmlContent || "").trim() ||
    "<p>No base content was provided for this outline item.</p>";

  const userMessage = `
You will **increase the intellectual and technical sophistication** of the existing structured HTML lesson for a single outline item.

Goal:
- Keep the **same core concepts and structure**,
- But rewrite it to be **richer, more nuanced, and more advanced** for learners who:
  • Already grasp the basics, and
  • Are ready for deeper theoretical and practical insight.

Think of this as transforming complexity **3 → 4–5** on a 1–5 scale.

────────────────────────────────────────
PROGRAMME CONTEXT (for continuity only)
────────────────────────────────────────
Programme: "${safeProgName}"
Unit ${unitSeq}: "${safeUnitName}"
Outcome (globalSequence ${globalSeq}): "${safeOutcome}"
Outcomes in this unit (teaching order):
${safeList}

COMPLETE OUTCOME OUTLINE (contextual reference)
${outlinePretty}

────────────────────────────────────────
OUTLINE ITEM (the one you are intensifying)
────────────────────────────────────────
"${sectionTitle}"

────────────────────────────────────────
BASE HTML CONTENT (from /content_complexity_base)
────────────────────────────────────────
${safeBaseHtml}

────────────────────────────────────────
TRANSFORMATION GOAL – HIGHER COMPLEXITY (ADVANCED LEVEL)
────────────────────────────────────────
You must **rewrite** the lesson above into a more advanced, insight-rich version while:

1) PRESERVING CORE SCOPE & FOCUS
   • Do NOT change the fundamental topic of this outline item.
   • You may introduce moderate additional nuance, conditions, and caveats **within**
     the same conceptual area.
   • Do NOT wander into completely unrelated future modules; stay aligned with the
     same learning outcome.

2) DEEPENING THE CONTENT
   • Add:
     – Deeper conceptual explanations and “why this works” reasoning.
     – More precise terminology (with brief clarification if needed).
     – Relationships to adjacent concepts or common edge cases.
   • Where appropriate, highlight trade-offs, constraints, and real-world pitfalls.
   • For practical topics, include slightly more complex examples or scenarios that
     challenge the learner’s understanding.

3) ACADEMIC & PROFESSIONAL TONE
   • Use more formal, technically accurate language, but keep it readable.
   • You can be slightly more compact in explanations (less repetition), but do not
     sacrifice clarity.
   • Assume the learner has already seen the beginner version and is now ready to
     “go one level deeper.”

4) STRUCTURE & TAGS – MAINTAIN SKELETON, ENRICH CONTENT
   • Keep the **overall heading hierarchy and section ordering** broadly similar.
   • You may:
     – Refine headings to be more precise.
     – Expand bullet lists into more detailed items.
     – Enhance tables with clarifying distinctions (still within the same topic).
   • Do NOT introduce new HTML tags beyond the whitelist below.

   ALLOWED HTML (same LMS whitelist):
   • Headings: <h1>, <h2>, <h3>, <h4>, <h5>
   • Paragraphs: <p>
   • Lists: <ul>, <ol>, <li>
   • Code blocks (non-JS): <code>…</code>
   • Tables: <table>, <thead>, <tbody>, <tr>, <th>, <td>
   • Inline: <strong>, <em>, <span>
   • Custom LMS tags:
     <statement>, <separator>, <paragraphcard>, <executable>…</executable>

   ▸ You may add additional <statement>, <paragraphcard>, or <separator> tags
     where they make advanced content clearer (e.g., summarising a key theorem or rule).

5) IMAGE HANDLING – PRESERVE EXACTLY
   • The base content may contain rich image tags like:
     <image>{"outcomeId":"${safeOutcomeId}", ...}</image>
   • You must **copy all <image> blocks exactly as they appear**:
     – Same JSON, same fields, same fileName, same outcomeId.
     – Do NOT change the image JSON, do NOT add new <image> tags,
       and do NOT remove existing <image> tags.
   • You may adjust the *surrounding text* to explain the image in a more advanced way,
     but do not touch the JSON itself.

6) CODE & TECHNICAL CONTENT
   • Preserve all <code> and <executable> blocks as valid, working examples.
   • You may:
     – Add more detailed commentary around the code.
     – Highlight performance, complexity, security, or robustness considerations.

7) STYLE & TONE
   • Tone: expert mentor speaking to an intelligent, motivated learner.
   • You can use domain-specific vocabulary and expect the learner to keep up,
     as long as you still define non-obvious terms briefly.
   • The overall effect should be: “same topic, but with deeper, more professional insight.”

────────────────────────────────────────
FINAL OUTPUT
────────────────────────────────────────
• Return **only** the advanced HTML lesson for this single outline item.
• Use **only** the allowed tags listed above.
• Do NOT include extra commentary, markdown, or explanations.
`.trim();

  return {
    endpoint: "responses",
    responses: {
      model: "gpt-5",
      input: [
        {
          role: "user",
          content: [{ type: "input_text", text: userMessage }],
        },
      ],
      reasoning: { effort: "high" },
      text: { verbosity: "high" },
      max_output_tokens: 16500,
    },
  };
};
